import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import React, { useState, useRef, useEffect, useCallback, useMemo, useTransition, useLayoutEffect } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { XMarkIcon, InformationCircleIcon, AdjustmentsHorizontalIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { RemoveButton, TimePlotButton, PlotButton, MapButton,
  PlusButton, PieBarButton, UploadButton, SearchButton, DuplicateButton,
  BroadcastGeoButton, TreeButton, StatsButton, CogGlyph, MergeButton,
  ScatterButton, LogButton, DownloadButton, NullButton, ViolinBoxButton,
  ClearAllButton, DevelopmentButton, InstructionsButton, ViewModeButton, BoxPlotButton,
  GeomEnrichButton, StatMenuButton, FilterToggleButton, TimeStatsButton } from './components/Buttons.jsx';
import wellknown from 'wellknown';
import { Title, Logoglyph, Footer } from './components/UI.jsx';
import { ProjectCard } from './components/InfoPanel.jsx';
import useElementSize from './hooks/useElementSize.js';
import { PanelContainer, PanelLoader, InteractionWrapper } from './components/PanelUI.jsx';
import TableViewer from './components/TableViewer.jsx';
import BoxPlotViewer from './components/Boxplotviewer.jsx';
import ViolinPlotViewer from './components/Violinplotviewer.jsx';
import TimePlotViewer from './components/TimePlotViewer.jsx'; 
import TimeStatsViewer from './components/TimeStatsViewer.jsx';
import GlobeViewer from './components/GlobeViewer.jsx';
import MapViewer from './components/MapViewer.jsx';
import SamplePanel from './components/SamplePanel.jsx';
import RobinsonMapViewer from './components/RobinsonMapViewer.jsx';
import NumericDistributionViewer from './components/NumericDistributionViewer.jsx';
import LifeMapViewer from './components/LifeMapViewer.jsx';
import ScatterPlotViewer from './components/ScatterPlotViewer.jsx';
import StatsViewer from './components/StatsViewer.jsx';
import { MAP_STYLES } from './components/Maps.js';
import { DISTINCT_COLORS} from './constants/Colors.js'
import { defaultPath, animathonPath, fullPath, fullPathwithBiomes, shuffledFullPath,
  goldenAngle, equatorialCircumference, yearInMs, SITE_PASSWORD, GRID_COLS, TAB_CONFIG,
   STAT_TABLE_PATH, GEOM_TABLE_PATH, GEOM_VAR_DESCRIPTIONS, GEOM_METADATA } from './constants/Constants.js';
import { tableFromArrays, tableFromIPC } from 'apache-arrow';

const MAX_HISTORY = 8;

// Native WKB (Well-Known Binary) to GeoJSON parser
// Bypasses the need for large libraries and Node Buffer polyfills
function parseWKB(buffer) {
  let offset = 0;
  // Safely map the Uint8Array byteOffset for memory-efficient reading
  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);

  function readGeometry() {
    const isLittleEndian = view.getUint8(offset) === 1;
    offset += 1;
    
    const wkbType = view.getUint32(offset, isLittleEndian);
    offset += 4;
    
    let hasZ = false;
    let hasM = false;
    let hasSRID = false;
    let geomType = wkbType;

    // PostGIS EWKB flags
    if (wkbType & 0x80000000) hasZ = true;
    if (wkbType & 0x40000000) hasM = true;
    if (wkbType & 0x20000000) hasSRID = true;
    
    geomType = geomType & 0x0FFFFFFF;

    // Standard ISO WKB Types
    if (geomType >= 1000 && geomType < 2000) { hasZ = true; geomType -= 1000; } 
    else if (geomType >= 2000 && geomType < 3000) { hasM = true; geomType -= 2000; } 
    else if (geomType >= 3000 && geomType < 4000) { hasZ = true; hasM = true; geomType -= 3000; }

    if (hasSRID) offset += 4;

    function readCoord() {
      const x = view.getFloat64(offset, isLittleEndian); offset += 8;
      const y = view.getFloat64(offset, isLittleEndian); offset += 8;
      if (hasZ) offset += 8; // skip 3D altitude if present
      if (hasM) offset += 8; // skip measure if present
      return [x, y];
    }

    if (geomType === 1) return { type: 'Point', coordinates: readCoord() };
    if (geomType === 2) {
      const numPoints = view.getUint32(offset, isLittleEndian); offset += 4;
      const coords = [];
      for(let i = 0; i < numPoints; i++) coords.push(readCoord());
      return { type: 'LineString', coordinates: coords };
    }
    if (geomType === 3) {
      const numRings = view.getUint32(offset, isLittleEndian); offset += 4;
      const rings = [];
      for(let i = 0; i < numRings; i++) {
        const numPoints = view.getUint32(offset, isLittleEndian); offset += 4;
        const ring = [];
        for(let j = 0; j < numPoints; j++) ring.push(readCoord());
        rings.push(ring);
      }
      return { type: 'Polygon', coordinates: rings };
    }
    if (geomType === 4) {
      const numPoints = view.getUint32(offset, isLittleEndian); offset += 4;
      const coords = [];
      for(let i = 0; i < numPoints; i++) coords.push(readGeometry().coordinates);
      return { type: 'MultiPoint', coordinates: coords };
    }
    if (geomType === 5) {
      const numLines = view.getUint32(offset, isLittleEndian); offset += 4;
      const coords = [];
      for(let i = 0; i < numLines; i++) coords.push(readGeometry().coordinates);
      return { type: 'MultiLineString', coordinates: coords };
    }
    if (geomType === 6) {
      const numPolys = view.getUint32(offset, isLittleEndian); offset += 4;
      const coords = [];
      for(let i = 0; i < numPolys; i++) coords.push(readGeometry().coordinates);
      return { type: 'MultiPolygon', coordinates: coords };
    }
    if (geomType === 7) {
      const numGeoms = view.getUint32(offset, isLittleEndian); offset += 4;
      const geoms = [];
      for(let i = 0; i < numGeoms; i++) geoms.push(readGeometry());
      return { type: 'GeometryCollection', geometries: geoms };
    }
    
    throw new Error(`Unsupported WKB type: ${geomType}`);
  }

  return readGeometry();
}

// Global error listener to suppress specific errors from lifemap.js
if (typeof window !== 'undefined') {
  const isLifemapLengthError = (err) => {
    if (!err) return false;
    const msg = typeof err === 'string' ? err : (err.message || '');
    const stack = typeof err === 'string' ? err : (err.stack || '');
    const combined = `${msg}\n${stack}`;
    return combined.includes("reading 'length'") && combined.includes('lifemap.js');
  };

  // Catch standard uncaught DOM/JS errors
  window.addEventListener('error', (e) => {
    if (isLifemapLengthError(e.error) || (e.message?.includes("reading 'length'") && e.filename?.includes('lifemap.js'))) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true); // 'true' ensures we catch it before Webpack/React error boundaries

  // Catch unhandled promise rejections (often thrown by async web mapping libraries)
  window.addEventListener('unhandledrejection', (e) => {
    if (isLifemapLengthError(e.reason)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  });

  // Intercept React/Webpack Error Overlays (which hook into console.error)
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // If any part of the arguments matches our specific lifemap error, swallow it
    if (args.some(arg => isLifemapLengthError(arg))) {
      return; 
    }
    // Otherwise, log normally
    originalConsoleError(...args);
  };
}

const EMPTY_ARRAY = Object.freeze([]); // Stable empty array reference for memoization
const gridWidth = WidthProvider(GridLayout);

const SearchCache = new Map();
const ColorCache = new Map(); // Cache for stable color assignments
const GeoCache = new Map();

const clearFileFromCaches = (filename, headers) => {
  let clearedCount = 0;
  
  // Clear ColorCache (LRU)
  for (const key of ColorCache.keys()) {
    if (key.startsWith(`${filename}|`)) {
      ColorCache.delete(key);
      clearedCount++;
    }
  }

  // Clear SearchCache
  for (const key of SearchCache.keys()) {
    if (key.startsWith(`${filename}|`)) {
      SearchCache.delete(key);
    }
  }

  // Clear GeoCache
  if (headers && headers.length > 0) {
    const geoKey = headers.join('|');
    if (GeoCache.has(geoKey)) {
      GeoCache.delete(geoKey);
      console.log(`[GeoCache] Purged key for: ${filename}`);
    }
  }
  
  if (clearedCount > 0) {
    console.log(`[Cache] Purged ${clearedCount} entries for: ${filename}`);
  }
};

// Search calculation to allow "excluding" a specific column or the date filter
const getIndicesExcluding = (table, headers, data, excludeCol = null, excludeDate = false) => {
  const { searchFilter, searchColumns, spatialFilter, numericFilters, dateFilter, numericIncludeNulls, categoricalFilters } = data;
  
  // Create a version of numericFilters without the excluded column
  const activeNumeric = { ...numericFilters };
  if (excludeCol) delete activeNumeric[excludeCol];

  const activeNulls = { ...numericIncludeNulls };
  if (excludeCol) delete activeNulls[excludeCol];

  const activeCat = { ...categoricalFilters };
  if (excludeCol) delete activeCat[excludeCol];
  
  // If excludeDate is true, pass null for date filter
  const activeDate = excludeDate ? null : dateFilter;
  
  return calculateSearchIndices(
    table, headers, searchFilter, searchColumns, spatialFilter, activeNumeric, activeDate, data.includeMissingDates ?? true, activeNulls, activeCat
  );
};

function getGeoColumns(headers, table = null) {
  // Create a stable key from headers to cache regex lookups
  const cacheKey = headers.join('|');
  let potentialCols;
  if (GeoCache.has(cacheKey)) {
    potentialCols = GeoCache.get(cacheKey);
  } else {
    potentialCols = {
      latCol: headers.find(h => /^(latitude|lat)$/i.test(h)),
      lonCol: headers.find(h => /^(longitude|lon|lng)$/i.test(h)),
      geoCol: headers.find(h => /^(geometry|geom|geojson|wkt|_geojson_str)$/i.test(h))
    };
    GeoCache.set(cacheKey, potentialCols);
  }

  if (!table) return potentialCols;

  let { latCol, lonCol, geoCol } = potentialCols;

  // Verify that the columns actually contain valid data (and lat/lon are paired)
  let hasPairedCoords = false;
  if (latCol && lonCol) {
    const latData = table.getChild(latCol);
    const lonData = table.getChild(lonCol);
    if (latData && lonData) {
      for (let i = 0; i < table.numRows; i++) {
        if (latData.isValid(i) && lonData.isValid(i)) {
          const lat = latData.get(i);
          const lon = lonData.get(i);
          if (lat != null && lon != null) {
            const latNum = typeof lat === 'number' ? lat : parseFloat(lat);
            const lonNum = typeof lon === 'number' ? lon : parseFloat(lon);
            if (!isNaN(latNum) && !isNaN(lonNum)) {
              hasPairedCoords = true;
              break; // Found at least one valid paired coordinate, we're good
            }
          }
        }
      }
    }
  }

  let hasGeoData = false;
  if (geoCol) {
    const geoData = table.getChild(geoCol);
    if (geoData) {
      for (let i = 0; i < table.numRows; i++) {
        if (geoData.isValid(i)) {
          const val = geoData.get(i);
          if (val != null && val !== '') {
            hasGeoData = true;
            break;
          }
        }
      }
    }
  }

  return {
    latCol: hasPairedCoords ? latCol : undefined,
    lonCol: hasPairedCoords ? lonCol : undefined,
    geoCol: hasGeoData ? geoCol : undefined
  };
}


// Helpers to find any valid lat/lon pair in nested arrays
const isVal = (n) => typeof n === 'number' && !isNaN(n) && isFinite(n);
const isStrictlyValidLatLng = (lat, lon) => isVal(lat) && isVal(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;

const getAnyValidCoordRecursive = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  // If found a [lon, lat] pair
  if (typeof arr[0] === 'number' && typeof arr[1] === 'number') {
    return isStrictlyValidLatLng(arr[1], arr[0]) ? [arr[1], arr[0]] : null;
  }
  for (let item of arr) {
    if (Array.isArray(item)) {
      const found = getAnyValidCoordRecursive(item);
      if (found) return found;
    }
  }
  return null;
};

const truncateColName = (col, maxLen = 20) => col && col.length > maxLen ? col.substring(0, maxLen) + '...' : String(col);

const getColorForIndex = (i) => {
  if (i < DISTINCT_COLORS.length) return DISTINCT_COLORS[i];
  // Fallback: Golden Angle distribution to maximize hue difference
  const h = (i * goldenAngle) % 360; 
  
  // convert hsl to hex with fixed saturation and lightness
  const s= 75/100;
  const l = 50/100;

  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))))
      .toString(16)
      .padStart(2, "0");

  return `#${f(0)}${f(8)}${f(4)}`;
  
};

const MAX_CACHE_SIZE = 10;

const computeStableColorMap = (table, filename, colorByColumn) => {
  if (!table || !colorByColumn) return null;
  const cacheKey = `${filename}|${colorByColumn}`;

  // LRU Logic: Move to end of Map (most recent) on hit
  if (ColorCache.has(cacheKey)) {
    const entry = ColorCache.get(cacheKey);
    ColorCache.delete(cacheKey);
    ColorCache.set(cacheKey, entry);
    return entry;
  }

  const freq = new Map();
  const col = table.getChild(colorByColumn);
  if (!col) return null;
  
  for (let i = 0; i < table.numRows; i++) {
    const val = col.get(i);
    freq.set(val, (freq.get(val) || 0) + 1);
  }

  const sortedCats = Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .map(e => e[0]);

  const colorMap = new Map();
  sortedCats.forEach((val, i) => {
    if (val == null || val === 'null' || val === '' || val === 'unknown') {
      colorMap.set(val, [59, 130, 246, 15]);
      return;
    }
    const colorStr = getColorForIndex(i);
    const rgb = colorStr.startsWith('#') 
      ? [parseInt(colorStr.slice(1, 3), 16), parseInt(colorStr.slice(3, 5), 16), parseInt(colorStr.slice(5, 7), 16)]
      : [59, 130, 246];
    colorMap.set(val, [...rgb, 200]);
  });

  const result = { colorMap, sortedCats };

  // Evict oldest if limit reached
  if (ColorCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = ColorCache.keys().next().value;
    ColorCache.delete(oldestKey);
  }

  ColorCache.set(cacheKey, result);
  return result;
};

const getColumnStats = (table, colName, indices = null) => {
  if (!table || !colName) return { min: 0, max: 1, isNumeric: false, count: 0 };
  const col = table.getChild(colName);
  if (!col) return { min: 0, max: 1, isNumeric: false, count: 0 };

  let min = Infinity, max = -Infinity, validCount = 0;
  
  // Decide whether to scan a subset (indices) or the whole table
  const hasIndices = indices !== null;
  const count = hasIndices ? indices.length : table.numRows;

  for (let i = 0; i < count; i++) {
    const rowIdx = hasIndices ? indices[i] : i;
    const v = col.get(rowIdx);
    
    // Convert to number if it isn't one (handles strings or numeric-like objects)
    const val = typeof v === 'number' ? v : parseFloat(v);

    if (val !== null && !isNaN(val) && isFinite(val)) {
      validCount++;
      if (val < min) min = val;
      if (val > max) max = val;
    }
  }

  const isNumeric = validCount > 0;
  let finalMin = isNumeric ? min : 0;
  let finalMax = isNumeric ? max : 1;

  // Ensure single-point distributions have a non-zero range (avoids division by zero in charts)
  if (isNumeric && finalMin === finalMax) {
    finalMin -= 1;
    finalMax += 1;
  }

  return {
    isNumeric,
    min: finalMin,
    max: finalMax,
    count: validCount
  };
};

const calculateSearchIndices = (table, headers, searchFilter, searchColumns, spatialFilter, numericFilters, dateFilter, includeMissingDates = true, numericIncludeNulls = null, categoricalFilters = null) => {
  if (!table) return null;
  
  const numCheckers = [];
 const numericCols = new Set([
    ...Object.keys(numericFilters || {}),
    ...Object.keys(numericIncludeNulls || {})
  ]);

  if (numericCols.size > 0) {
   numericCols.forEach(colName => {
      const child = table.getChild(colName);
      if (child) {
        const range = numericFilters?.[colName];
        // If the toggle hasn't been explicitly touched, defaults to DROP nulls if a range is set, and KEEP nulls if no range is set.
        const defaultInclude = range ? false : true;
        const includeNulls = numericIncludeNulls?.[colName] ?? defaultInclude;
        
        if (range || !includeNulls) {
          numCheckers.push({ child, range, includeNulls });
        }
      }
    });
  }

  const catCheckers = [];
  if (categoricalFilters) {
    for (const colName in categoricalFilters) {
      const child = table.getChild(colName);
      if (child) catCheckers.push({ child, expected: categoricalFilters[colName] });
    }
  }

  // Check if any filter is active
 if (!searchFilter && !spatialFilter && numCheckers.length === 0 && !dateFilter && includeMissingDates && catCheckers.length === 0) {
    return null; 
  }

  const indices = [];
  const numRows = table.numRows;
  
  // Prepare column children for faster access in the loop
  const colsToCheck = (searchColumns && searchColumns.length > 0) 
    ? searchColumns 
    : headers.filter(h => !/geometry/i.test(h));
  const columnChildren = colsToCheck.map(h => table.getChild(h));

  // Prepare geo columns if spatial filter is active
  let latChild, lonChild;
  if (spatialFilter) {
    const { latCol, lonCol } = getGeoColumns(headers, table);
    if (latCol && lonCol) {
      latChild = table.getChild(latCol);
      lonChild = table.getChild(lonCol);
    }
  }


  // Prepare Date Column for Time Check
  const dateColName = headers.find(h => /parsed_collection_date_start/i.test(h));
  const dateChild = dateColName ? table.getChild(dateColName) : null;

  for (let i = 0; i < numRows; i++) {
    // --- Date filter check ---
    if (dateChild) {
      const val = dateChild.get(i);
      const ts = new Date(val).getTime();
      const isInvalid = isNaN(ts) || ts === 0; // treat null/NaN/1970 as missing

      if (isInvalid) {
        // If toggle is off, prune the sample immediately regardless of sliders
        if (!includeMissingDates) continue;
      } else if (dateFilter) {
        // If sliders are active, check if valid date is within range
        if (ts < dateFilter[0] || ts > dateFilter[1]) continue;
      }
    }

    // --- Numeric range check ---
    if (numCheckers.length > 0) {
      let failNumeric = false;
      for (const { child, range, includeNulls } of numCheckers) {
        const val = child.get(i);
        const isNull = val === null || val === undefined || val === '';
        if (isNull) {
          if (!includeNulls) {
            failNumeric = true;
            break;
          }
        } else if (range && (val < range[0] || val > range[1])) {
           failNumeric = true;
           break;
        }
      }
      if (failNumeric) continue;
    }

    // --- Categorical check ---
    if (catCheckers.length > 0) {
      let failCat = false;
      for (const { child, expected } of catCheckers) {
        const val = child.get(i);
        const isNullOrEmpty = val == null || val === '';
        const strVal = isNullOrEmpty ? 'null' : String(val);
        if (strVal !== expected) {
           failCat = true;
           break;
        }
      }
      if (failCat) continue;
    }

    // --- Spatial check ---
    if (spatialFilter && latChild && lonChild) {
      const lat = latChild.get(i);
      const lon = lonChild.get(i);
      if (lon < spatialFilter[0] || lat < spatialFilter[1] || lon > spatialFilter[2] || lat > spatialFilter[3]) continue;
    }

    // --- Search string check ---
    if (searchFilter) {
      let match = false;
      const lowerSearch = searchFilter.toLowerCase();
      for (let col = 0; col < columnChildren.length; col++) {
        const val = columnChildren[col].get(i);
        if (val !== null && String(val).toLowerCase().includes(lowerSearch)) {
          match = true;
          break;
        }
      }
      if (!match) continue;
    }

    indices.push(i);
  }
  return indices;
};


//  --------- Panel Components --------- 

const TablePanel = React.memo(function TablePanel({ 
  id, data, onRemove, onOpenMap, onOpenPlot, onSearchChange, onSearchColumnsChange,
  onOpenLifeMap, onOpenStats, onOpenTimeStats, onOpenNumericPlot, onOpenScatterPlot, onOpenBoxPlot, onMergeClick, isMergeSource, basic = false , onRowClick
}) {
  const { filename, table, searchFilter, headers = EMPTY_ARRAY, searchColumns, spatialFilter } = data;
  const [chartContainerRef, { height: containerHeight }] = useElementSize({ debounceMs: 90 });
  const [showSearch, setShowSearch] = useState(!!searchFilter);
  const [searchQuery, setSearchQuery] = useState(searchFilter || '');
  const searchInputRef = useRef(null);
  const allowMerge = false; // Disable merge for now

  const handleDownload = useCallback(() => {
    if (!table) return;
    const indices = data.searchIndices || Array.from({ length: table.numRows }, (_, i) => i);
    
    const chunks = [];
    chunks.push(headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(',') + '\n');

    let currentChunk = '';
    for (let i = 0; i < indices.length; i++) {
      const rowIdx = indices[i];
      if (rowIdx === -1) continue; // skip sentinel if empty
      
      const rowData = headers.map(h => {
        const col = table.getChild(h);
        if (!col) return '';
        let val = col.get(rowIdx);
        
        // Match table viewer formatting for dates
        if (val !== null && val !== undefined && /date/i.test(h)) {
            const date = new Date(val);
            if (!isNaN(date.getTime())) {
                val = date.toISOString().split('T')[0];
            }
        }

        // Match table viewer formatting for geometries
        if (val !== null && val !== undefined && /geometry/i.test(h)) {
            const geoJsonChild = table.getChild('_geojson_str');
            if (geoJsonChild) {
                try {
                    const geo = JSON.parse(geoJsonChild.get(rowIdx));
                    val = geo.type === 'Point' 
                      ? `Point(${geo.coordinates[0].toFixed(4)}, ${geo.coordinates[1].toFixed(4)})` 
                      : geo.type;
                } catch (e) { /* ignore */ }
            }
        }

        if (val === null || val === undefined) return '';
        
        let str = String(val);
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
           return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      currentChunk += rowData.join(',') + '\n';

      // Batch push to prevent memory/string-size issues on huge files
      if (i > 0 && i % 10000 === 0) {
          chunks.push(currentChunk);
          currentChunk = '';
      }
    }
    if (currentChunk) chunks.push(currentChunk);

    const blob = new Blob(chunks, { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename.split(/[/:]/).pop().split('.')[0]}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [table, data.searchIndices, headers, filename]);

   // Sync local state if the search filter changes externally (e.g., from Basic view)
  useEffect(() => {
    if (searchFilter) {
      setSearchQuery(searchFilter);
      setShowSearch(true);
    } else if (searchFilter === '') {
      // If search is cleared in basic, clear it here too
      setSearchQuery('');
    }
  }, [searchFilter]);

  const [isPending, startTransition] = useTransition();

  function runSearch() {
    startTransition(() => {
      onSearchChange(filename, searchQuery);
    });
  }

  const closeSearch = () => {
  setShowSearch(false);
  setSearchQuery('');
  onSearchChange(filename, '');
};

  const dateCol = useMemo(() => headers.find(h => /parsed_collection_date_start/i.test(h) && h.toLowerCase() !== 'has_collection_date' && h.toLowerCase() !== 'has_valid_collection_date' && h.toLowerCase() !== 'is_validated'), [headers]);
  //console.log('Date column detected:', dateCol);
  const hasTaxon = useMemo(() => headers.includes('tax_id') || headers.includes('taxon_id') || headers.includes('TAX_ID') || headers.includes('HOST_TAXID'), [headers]);

  const hasGeo = useMemo(() => {
    const { latCol, lonCol, geoCol } = getGeoColumns(headers, table);
    return (latCol && lonCol) || geoCol;
  }, [headers, table]);

  const handleColumnToggle = useCallback((col) => {
    const current = searchColumns || [];
    const newCols = current.includes(col) 
      ? current.filter(c => c !== col) 
      : [...current, col];
    startTransition(() => {
    onSearchColumnsChange(filename, newCols);
  });
  }, [searchColumns, filename, onSearchColumnsChange]);

  return (
      <PanelContainer id={id} basic={basic}>
      {!basic && (
        <div className={`panel-header ${isMergeSource ? 'bg-blue-50 border-b-2 border-blue-500' : ''} ${!basic ? 'cursor-move' : ''}`}>
          <span className="truncate ml-2">
            {isMergeSource ? "Select second table to merge" : filename}
          </span>
          <div className="flex items-center gap-0.5">
          { allowMerge && (
            <MergeButton 
              onClick={() => onMergeClick(id)} 
              isMergeSource={isMergeSource} 
            />
          )}
          {isPending && <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"/>}
          {!showSearch && (<SearchButton onClick={() => { setShowSearch(s => !s) }} />)}
          {showSearch && (
        
          <input ref={searchInputRef} 
          autoFocus 
          className="border border-gray-500 rounded-md px-2 py-0.5 w-32" 
          placeholder="Search..." 
          value={searchQuery} 
          onChange={e => setSearchQuery(e.target.value)} 
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); if (e.key === 'Escape') closeSearch(); }} />           
           )}
          {dateCol && (
            <TimePlotButton onClick={() => onOpenPlot(data)} />
          )}
          {hasGeo && (
            <MapButton onClick={() => onOpenMap(data)} />
          )}
          <StatsButton onClick={() => onOpenStats(data)} />
          {hasTaxon && (
            <TreeButton onClick={() => onOpenLifeMap(data)} />
          )}
          {dateCol && (
            <TimeStatsButton onClick={() => onOpenTimeStats(data)} />
          )}
          <ScatterButton onClick={() => onOpenScatterPlot(data)} />
          <BoxPlotButton onClick={() => onOpenBoxPlot(data)} />
          <PlotButton   onClick={() => onOpenNumericPlot(data)} />
          <DownloadButton onClick={handleDownload} title="Download Table Data (CSV)" />
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
        </div>
       )}
      <div ref={chartContainerRef} className="flex flex-col h-full px-2 pb-2 overflow-hidden pt-2">
        <TableViewer
          data={data}
          height={containerHeight}
          basicMode={basic}
          onToggleColumn={handleColumnToggle}
          onRowClick={onRowClick}
        />
      </div>
    </PanelContainer>
  );
});

const MapPanel = React.memo(function MapPanel({ 
  id, data, colorConfig, onColorByChange, onRemove,
  onSpatialFilterChange, onBaseLayerChange, basic = false, smallScreen=false, onRowClick, selectedSampleIndex, onConfigChange
}) {
  const { 
    table, headers, filename, dateFilter, searchFilter, 
    searchColumns, searchIndices, spatialFilter, baseLayer = 'satellite' 
  } = data;

  const [projection, setProjection] = useState(data.projection || basic ? "globe" : "mercator");
  const [showViewOptions, setShowViewOptions] = useState(false);
  const viewOptionsRef = useRef(null);
  const viewButtonWrapperRef = useRef(null);
  const [envMode, setEnvMode] = useState('none');
  const [showEnvOptions, setShowEnvOptions] = useState(false); // New state for dropdown
  const envOptionsRef = useRef(null);

  const colorBy = data.colorBy || headers[0] || '';
  const stableColorConfig = colorConfig;
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const zoomLevel = smallScreen? 0.4 : (projection=='globe' ? 1.8 : (projection=='robinson'? -1: 0)) ;
  const [viewState, setViewState] = useState({ longitude: 0, latitude: 0,
     zoom: zoomLevel,
     pitch: 0, bearing: 0 });
  const { latCol, lonCol } = useMemo(() => getGeoColumns(headers, table), [headers, table]);
  const searchStr = searchFilter;

  const [isSpatialActive, setIsSpatialActive] = useState(!!spatialFilter);
  const [showSettings, setShowSettings] = useState(false);
  const [radiusConfig, setRadiusConfig] = useState({ min: 1.5, max: 6 });
  const [lineWidth, setlineWidth] = useState(0.0);


  // Handle click outside for projection dropdown
  useEffect(() => {
    if (!showViewOptions) return;
    const closeOnClickOutside = (e) => {
      if (viewOptionsRef.current && !viewOptionsRef.current.contains(e.target) &&
          (!viewButtonWrapperRef.current || !viewButtonWrapperRef.current.contains(e.target))) {
        setShowViewOptions(false);
      }
    };
    document.addEventListener('mousedown', closeOnClickOutside);
    return () => document.removeEventListener('mousedown', closeOnClickOutside);
  }, [showViewOptions]);

  const handleBoundsChange = useCallback((bounds) => {
    if (isSpatialActive) onSpatialFilterChange(filename, bounds);
  }, [isSpatialActive, filename, onSpatialFilterChange]);

  const toggleSpatial = () => {
    const next = !isSpatialActive;
    setIsSpatialActive(next);
    onSpatialFilterChange(filename, next ? null : null);
  };

  const handleProjectionChange = (mode) => {
    setProjection(mode);
    setShowViewOptions(false);
    // Disable spatial filtering for non-mercator views if active
    if (mode !== 'mercator' && isSpatialActive) {
      toggleSpatial();
    }
  };

  // Numeric Stats
  const stats = useMemo(() => {
  // Use searchIndices to get stats for the currently visible points
    return getColumnStats(table, colorBy, searchIndices);
  }, [table, colorBy, searchIndices]);

  const isGradient = data.isGradient ?? stats.isNumeric;

  // Processed Points
  const processedData = useMemo(() => {
    if (!table) return [];
    const groups = new Map();
    const latData = table.getChild(latCol);
    const lonData = table.getChild(lonCol);
    const geoStrData = table.getChild('_geojson_str');
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    for (const i of source) {
      if (geoStrData) {
        const geoStr = geoStrData.get(i);
        // If there is geometry and it's not a point, skip the scatterplot aggregation
        if (geoStr && !geoStr.includes('"type":"Point"')) continue; 
      }

      let lat = latData ? parseFloat(latData.get(i)) : NaN;
      let lon = lonData ? parseFloat(lonData.get(i)) : NaN;

      // Fallback if lat/lon columns are missing but geojson point exists
      if ((isNaN(lat) || isNaN(lon)) && geoStrData) {
        try {
          const geo = JSON.parse(geoStrData.get(i));
          if (geo.type === 'Point') [lon, lat] = geo.coordinates;
        } catch(e) {}
      }
      
      if (isNaN(lat) || isNaN(lon)) continue;

      const key = `${lat.toFixed(6)}|${lon.toFixed(6)}`;
      let entry = groups.get(key);
      const colorCol = table.getChild(colorBy);
      const val = colorCol ? colorCol.get(i) : null;
      
      if (!entry) {
        entry = { 
          position: [lon, lat], 
          count: 0, 
          uniqueValues: new Set(), 
          allValues: [],    // Used by MapViewer for coloring
          allSamples: []    // Used by MapViewer for click/index lookup
        };
        groups.set(key, entry);
      }
      entry.count += 1;
      entry.allValues.push(val); // for Map Colors/Legend
      entry.allSamples.push({ value: val, index: i }); // for Click selection
      if (val != null) entry.uniqueValues.add(val);
    }
    return Array.from(groups.values());
  }, [table, searchIndices, latCol, lonCol, colorBy, headers]);


  // GeoJSON Features
  const geojsonFeatures = useMemo(() => {
    if (!table) return [];
    const geoStrData = table.getChild('_geojson_str');
    if (!geoStrData) return [];
    const features = [];
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    for (const i of source) {
      try {
        const geoStr = geoStrData.get(i);
        if (!geoStr) continue;
        const geom = JSON.parse(geoStr);
        if (geom.type !== 'Point') {
          features.push({ type: 'Feature', geometry: geom, properties: table.get(i) });
        }
      } catch(e) {}
    }
    return features;
  }, [table, searchIndices]);

  // Visible Categories for Legend
  const visibleCategories = useMemo(() => {
    if (!table || !colorBy) return [];
    
    const counts = new Map();
    const col = table.getChild(colorBy);
    if (!col) return []; // Safely exit if column disappeared
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    // Count frequencies in the current filtered set
    for (const i of source) {
      const val = col.get(i);
      counts.set(val, (counts.get(val) || 0) + 1);
    }

    // Sort by local frequency (most frequent first)
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, count }));
  }, [table, colorBy, searchIndices]);

   const scaleInfo = useMemo(() => {
    const metersPerPixel = (Math.cos(viewState.latitude * Math.PI / 180) * equatorialCircumference) / (256 * Math.pow(2, viewState.zoom));
    const targetWidthPx = 40;
    const meters = metersPerPixel * targetWidthPx;
    
    return {
      label: meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`,
      width: targetWidthPx
    };
  }, [viewState.latitude, viewState.zoom])

 const handleSampleClick = useCallback((index) => {
    if (onRowClick && table) {
      onRowClick(table.get(index), index);
    }
  }, [onRowClick, table]);

  return (
    <PanelContainer id={id} basic={basic}>
      {!basic && (
        <div className={`panel-header ${!basic ? 'cursor-move' : ''}`}>
          <span className="truncate ml-2">Map: {filename}</span>
          <div className="flex items-center gap-0.5">
            {/* Basemap selector only visible in Mercator (Tile version) */}
            {projection === 'mercator' && (
              <select
                className="dropdown-selector max-w-[160px] truncate"
                value={baseLayer}
                onChange={e => onBaseLayerChange(id, e.target.value)}
              >
                {Object.entries(MAP_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.name}</option>
                ))}
              </select>
            )}

            <select className="dropdown-selector max-w-[160px] truncate"
              value={colorBy} 
              onChange={e => onColorByChange(id, e.target.value)}
            >
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>

            {projection === 'mercator' && (
              <BroadcastGeoButton isActive={isSpatialActive} onClick={toggleSpatial} />
            )}

            {/* Projection Toggle with Dropdown logic */}
            <div ref={viewButtonWrapperRef} className="relative">
              <MapButton 
                isActive={showViewOptions || projection !== 'mercator'} 
                onClick={() => setShowViewOptions(!showViewOptions)} 
                title="Change Projection"
              />
              
              {showViewOptions && (
                <div ref={viewOptionsRef} className="absolute top-9 right-[-24px] z-[1100] bg-white border border-gray-300 rounded-xl shadow-xl px-1 py-1 flex flex-col items-stretch space-y-1">
                  {['mercator', 'robinson', 'globe'].map((mode) => (
                    <button 
                      key={mode}
                      onClick={() => handleProjectionChange(mode)} 
                      className={`text-sm text-left px-3 py-1 rounded-lg hover:bg-gray-200 capitalize transition-colors ${projection === mode ? 'bg-blue-100' : ''}`}
                    >
                      {mode === 'mercator' ? 'Mercator' : mode === 'robinson' ? 'Robinson' : 'Globe'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <RemoveButton onClick={() => onRemove(id)} />
          </div>
        </div>
      )}

      <div className="flex-grow w-full h-full relative overflow-hidden bg-slate-900">
        {projection === 'robinson' ? (
          <RobinsonMapViewer
            id={id} viewState={viewState} onViewStateChange={setViewState}
            processedData={processedData} geojsonFeatures={geojsonFeatures}
            colorBy={colorBy} stats={stats} stableColorConfig={stableColorConfig}
            isGradient={isGradient} onBoundsChange={handleBoundsChange}
            onSampleClick={handleSampleClick}
            radiusMinPixels={radiusConfig.min} radiusMaxPixels={radiusConfig.max} lineWidth={lineWidth}
            selectedSampleIndex={selectedSampleIndex}
          />
        ) : projection === 'globe' ? (
          <GlobeViewer
            id={id} viewState={viewState} onViewStateChange={setViewState}
            processedData={processedData} geojsonFeatures={geojsonFeatures}
            colorBy={colorBy} stats={stats} stableColorConfig={stableColorConfig}
            isGradient={isGradient} baseLayer={baseLayer}
            onBoundsChange={handleBoundsChange}
            onSampleClick={handleSampleClick}
            envMode={envMode} 
            radiusMinPixels={radiusConfig.min} radiusMaxPixels={radiusConfig.max} lineWidth={lineWidth}
            selectedSampleIndex={selectedSampleIndex}
          />
        ) : (
          <MapViewer
            id={id} viewState={viewState} onViewStateChange={setViewState}
            processedData={processedData} geojsonFeatures={geojsonFeatures}
            colorBy={colorBy} stats={stats} stableColorConfig={stableColorConfig}
            onSampleClick={handleSampleClick}
            shouldBroadcast={isSpatialActive}
            isGradient={isGradient} baseLayer={baseLayer}
            onBoundsChange={handleBoundsChange}
            radiusMinPixels={radiusConfig.min} radiusMaxPixels={radiusConfig.max} lineWidth={lineWidth}
            selectedSampleIndex={selectedSampleIndex}
          />
        )}
          {/* Settings Toggle and Panel */}
          <div className="absolute top-2 left-2 z-[1001]">
            <button 
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 min-w-[22px] min-h-[22px] rounded-full bg-white/90 border border-gray-800 shadow-sm hover:bg-gray-300 text-xs font-bold visibility ${showSettings ? 'hidden' : 'visible'} `}
            >
                {showSettings ? '✕' : <CogGlyph />}
            </button>

            {showSettings && (
              <>
              {/* Transparent Backdrop to catch clicks outside */}
              <div 
                className="fixed inset-0 z-20 cursor-default" 
                onClick={() => setShowSettings(false)} 
              />

              <div className="absolute top-0 left-0 bg-white/95 border border-gray-800 p-3 rounded-xl shadow-xl w-48 flex flex-col gap-3 text-[10px] z-30">
                <div>
                  <label className="flex justify-between mb-1 text-gray-500 font-bold">Min Radius <span>{radiusConfig.min}px</span></label>
                  <input type="range" min="0.5" max="10" step="0.5" value={radiusConfig.min} 
                    onChange={e => setRadiusConfig(p => ({...p, min: +e.target.value}))} className="w-full h-1" />
                </div>
                <div>
                  <label className="flex justify-between mb-1 text-gray-500 font-bold">Max Radius <span>{radiusConfig.max}px</span></label>
                  <input type="range" min="0.5" max="20" step="0.5" value={radiusConfig.max} 
                    onChange={e => setRadiusConfig(p => ({...p, max: +e.target.value}))} className="w-full h-1" />
                </div>
                <div>
                  <label className="flex justify-between mb-1 text-gray-500 font-bold">Line Width <span>{lineWidth}px</span></label>
                  <input type="range" min="0.0" max="2" step="0.1" value={lineWidth} 
                    onChange={e => setlineWidth(+e.target.value)} className="w-full h-1" />
                </div>
              </div>
             </>
                )}
          </div>

        {/* Legend Overlay */}
        <div className="absolute bottom-2 left-2 z-[1000] bg-white/90 backdrop-blur-sm border border-gray-800 rounded-lg shadow-lg overflow-hidden max-w-[180px]">
          {basic ? (
            <div className="flex flex-col">
              {/* Variable Selector for Basic Mode */}
              <div className="flex items-center bg-gray-100/50 border-b border-gray-300">
                <span className="pl-2 text-[9px] font-bold text-gray-500 tracking-tight">Color:</span>
                <select 
                  className="flex-grow bg-transparent px-2 py-1 text-[10px] font-bold cursor-pointer outline-none min-w-0 truncate"
                  value={colorBy} 
                  onChange={e => onColorByChange(id, e.target.value)}
                >
                  {headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <button 
                  onClick={() => setIsLegendOpen(!isLegendOpen)} 
                  className="px-3 py-1 text-[10px] hover:bg-gray-300 border-l border-gray-200"
                >
                  {isLegendOpen ? '▼' : '▲'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setIsLegendOpen(!isLegendOpen)} className="w-full px-2 py-1 text-[10px] font-bold hover:bg-gray-300 flex justify-between items-center">
              {colorBy} <span className='ml-4'>{isLegendOpen ? '▼' : '▲'}</span>
            </button>
          )}

          {isLegendOpen && !isGradient && (
            <div className="p-2 max-h-40 overflow-y-auto">
              {visibleCategories.slice(0, 20).map(({ value, count }) => (
                <div key={String(value)} className="flex items-center justify-between gap-2 mb-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-3 h-3 rounded-full border border-gray-800 flex-shrink-0" style={{
                      backgroundColor: (() => {
                        const rgba = stableColorConfig.colorMap.get(value) || [0,0,0,255];
                        return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] / 255})`;
                      })()
                    }} />
                    <span className="text-[10px] truncate">{String(value ?? 'null')}</span>
                  </div>
                  <span className="text-[9px] text-gray-400 font-mono">{count}</span>
                </div>
              ))}
              {visibleCategories.length > 20 && (
                <div className="text-[8px] text-gray-500 italic mt-1 border-t pt-1">
                  Showing top 20 of {visibleCategories.length}
                </div>
              )}
            </div>
          )}



          {isLegendOpen && isGradient && (
            <div className="flex flex-col gap-1 p-2 min-w-[150px]">
              <div className="h-2 w-full bg-gradient-to-r from-[#ffff00] to-[#ff0000] rounded" />
              <div className="flex justify-between text-[8px] text-gray-600 font-bold">
                <span>{stats.min.toLocaleString()}</span>
                <span>{stats.max.toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Right Controls (Basic Mode View Options & Gradient Toggle) */}
        <div className="absolute bottom-2 right-1 z-[1000] flex items-center gap-2 mr-1">
          {basic && (
            <div className="flex items-center gap-1 rounded-lg p-0.5 shadow-sm">
              {projection === 'mercator' && (
                <select
                  className="dropdown-selector max-w-[120px] truncate bg-transparent"
                  value={baseLayer}
                  onChange={e => onBaseLayerChange(id, e.target.value)}
                >
                  {Object.entries(MAP_STYLES).map(([k, v]) => (
                    <option key={k} value={k}>{v.name}</option>
                  ))}
                </select>
              )}
              {projection === 'mercator' && (
                <BroadcastGeoButton isActive={isSpatialActive} onClick={toggleSpatial} />
              )}
              <div ref={viewButtonWrapperRef} className="relative flex items-center">
                <MapButton 
                  isActive={showViewOptions || projection !== 'mercator'} 
                  onClick={() => setShowViewOptions(!showViewOptions)} 
                  title="Change Projection"
                />
                {showViewOptions && (
                  <div ref={viewOptionsRef} className="absolute bottom-full right-0 mb-2 z-[1100] bg-white border border-gray-300 rounded-xl shadow-xl px-1 py-1 flex flex-col items-stretch space-y-1">
                    {['mercator', 'robinson', 'globe'].map((mode) => (
                      <button 
                        key={mode}
                        onClick={() => handleProjectionChange(mode)} 
                        className={`text-sm text-left px-3 py-1 rounded-lg hover:bg-gray-200 capitalize transition-colors ${projection === mode ? 'bg-blue-100' : ''}`}
                      >
                        {mode === 'mercator' ? 'Mercator' : mode === 'robinson' ? 'Robinson' : 'Globe'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gradient Toggle */}
          {(stats.isNumeric) &&  (
            <button
              onClick={() => onConfigChange && onConfigChange(id, { isGradient: !isGradient })}
              title="Toggle Gradient/Categorical"
              className={`w-5 h-5 border border-gray-800 rounded-full transition-colors shadow-sm ${isGradient ? 'bg-gradient-to-r from-yellow-400 to-red-500' : 'bg-gray-200'}`}
            />
          )}
        </div>

        {/* Environmental Selector Dropdown */}
        {basic && projection === 'globe' && (
          <div className="absolute top-2 right-2 flex flex-col items-end gap-2">
            <div className="relative">
              <button
                onClick={() => setShowEnvOptions(!showEnvOptions)}
                title="Environmental Overlay"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-800 shadow-lg transition-all text-[10px] font-bold ${
                  envMode !== 'none' ? 'bg-gradient-to-r from-yellow-400 to-green-500' : 'bg-gradient-to-r from-gray-400/80 to-gray-200/80 text-gray-800 hover:bg-gray-200'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${envMode === 'none' ? 'bg-gray-400 border border-gray-800' : 'bg-green-500 border border-green-800'}`} />
                {envMode === 'none' ? 'Env: Off' : envMode.charAt(0).toUpperCase() + envMode.slice(1)}
              </button>

              {showEnvOptions && (
                <>
                  <div className="fixed inset-0 z-[1050]" onClick={() => setShowEnvOptions(false)} />
                  <div className="absolute top-11 right-0 z-[1100] bg-white border border-gray-800 rounded-xl shadow-xl px-1 py-1 flex flex-col items-stretch space-y-1">
                    {['none', 'biomes', 'ecoregions'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => {
                          setEnvMode(mode);
                          setShowEnvOptions(false);
                        }}
                        className={`text-sm text-left px-3 py-1 font-bold rounded-lg hover:bg-gray-200 capitalize transition-colors ${
                          envMode === mode ? 'bg-blue-100' : ''
                        }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!basic && (
      <div className={`bg-gray-100 px-2 ${basic ? 'py-1' : 'py-0'} text-[10px] text-gray-600 border-t border-gray-300 flex justify-between items-end`}>
      <div className="truncate mr-4">
        {processedData.length>0 && `Number of unique points${isSpatialActive ? ' (currently in view)' : ''}: ${processedData.length}`}
        {geojsonFeatures.length>0 && processedData.length>0 && ' | '}
        {geojsonFeatures.length>0 && `Number of unique geometries: ${geojsonFeatures.length}`}
        {dateFilter && ` | Date Filter: ${new Date(dateFilter[0]).toLocaleDateString()} - ${new Date(dateFilter[1]).toLocaleDateString()}`}
        {searchStr && ` | Search: "${searchStr}"`}
        {searchColumns && searchColumns.length > 0 && ` (in: ${searchColumns.join(', ')})`}
      </div>
      
      {/* Map Scale (expert mode) */}
      {projection=='mercator' && (
        <div className="flex gap-2 items-center flex-shrink-0 mr-1">
          <div
            className="h-1 border-x border-b border-gray-500 mb-0.5"
            style={{ width: scaleInfo.width }}
          />
          <div className="font-mono text-[10px] leading-none mb-1">{scaleInfo.label}</div>
        </div>
      )}
      </div>
    )}
    {basic && projection === 'mercator' && (
      <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
        {/* Map Scale (basic mode) */}
        <div
          className="h-1.5 border-x border-b border-white mb-0.5 mr-2"
          style={{ 
            width: scaleInfo.width, 
            filter: 'drop-shadow(0px 1px 0px black) drop-shadow(0px -1px 0px black) drop-shadow(1px 0px 0px black) drop-shadow(-1px 0px 0px black)' 
          }}
        />
        <div className="font-mono text-[10px] text-black leading-none mb-1 border border-white ring-1 ring-black bg-white rounded-md p-1 px-2 shadow-sm">
          {scaleInfo.label}
        </div>
      </div>
    )}
  </PanelContainer>
);});

const NumericPlotPanel = React.memo(function NumericPlotPanel({ id, data, onRemove, onDuplicate, onFilterChange, onColorByChange, onNullToggle, basic=false, onConfigChange }) {
  const { table, headers, filename, numericFilters, numericIncludeNulls } = data;
  const isLogScale = data.isLogScale || false;
  const setIsLogScale = (updater) => {
      const next = typeof updater === 'function' ? updater(isLogScale) : updater;
      onConfigChange && onConfigChange(id, { isLogScale: next });
  };

  const numericHeaders = useMemo(() => {
    return headers.filter(h => {
      const col = table.getChild(h);
      //return col && typeof col.get(0) === 'number' || col.get(0) === null || col.get(0) === undefined;
      // More robust check: sample multiple values to confirm numeric-ness, allowing for some nulls
      if (!col) return false;
      let numericCount = 0;
      let totalCount = 0;
      for (let i = 0; i < Math.min(50, table.numRows); i++) {
        const val = col.get(i);
        if (val === null || val === undefined) continue;
        totalCount++;
        if (typeof val === 'number') numericCount++;
      }
      return totalCount > 0 && numericCount / totalCount > 0.8; // at least 80% numeric
    });
  }, [table, headers]);

  // Ensure the inherited colorBy is actually numeric before using it
  const selectedCol = (data.colorBy && numericHeaders.includes(data.colorBy)) 
    ? data.colorBy 
    : numericHeaders[0];

    // Detect if the column is a date type based on its name
  const isDateCol = useMemo(() => /date/i.test(selectedCol) && selectedCol.toLowerCase() !== 'has_collection_date' && selectedCol.toLowerCase() !== 'has_valid_collection_date' && selectedCol.toLowerCase() !== 'is_validated', [selectedCol]);

  const formatForInput = useCallback((v) => {
    if (v === null || v === undefined) return '';
    if (isDateCol) {
      const date = new Date(v);
      return !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';
    }
    return Number(v.toFixed(3)).toString();
  }, [isDateCol]);

  const { minX, maxX } = useMemo(() => {
  // Pass null for indices to get the global range of the dataset
  const stats = getColumnStats(table, selectedCol, null);
  return { minX: stats.min, maxX: stats.max };
}, [table, selectedCol]);

  // Memoize the range to prevent the useEffect from firing on every render
  const currentRange = useMemo(() => {
    const raw = (numericFilters && numericFilters[selectedCol]) || [minX, maxX];
    // Clamp to minX/maxX to prevent the slider from breaking with extreme parsed limits
    return [Math.max(minX, raw[0]), Math.min(maxX, raw[1])];
  }, [numericFilters, selectedCol, minX, maxX]);

  const [localRange, setLocalRange] = useState([0, 1]);
  const [tempInputs, setTempInputs] = useState(['', '']);
  
  // Track dragging state locally to block prop-synching during interaction
  const [isDragging, setIsDragging] = useState(false);

  // Sync local state when global filter changes (e.g. column switch or external reset)
  useEffect(() => {
    if (isDragging) return; // do not update if the user is mid-drag

    setLocalRange([
      (currentRange[0] - minX) / (maxX - minX || 1),
      (currentRange[1] - minX) / (maxX - minX || 1)
    ]);
    setTempInputs([
      formatForInput(currentRange[0]),
      formatForInput(currentRange[1])
    ]);
  }, [currentRange, minX, maxX, isDragging, formatForInput]);

  const handleVisualChange = useCallback((newVisualRange) => {
    setIsDragging(true); // Signal that we are interacting
    // Update text inputs immediately so they reflect slider position
    setLocalRange(newVisualRange);
    setTempInputs([
      formatForInput(minX + newVisualRange[0] * (maxX - minX)),
      formatForInput(minX + newVisualRange[1] * (maxX - minX))
    ]);
  }, [minX, maxX, formatForInput]);

  const handleFilterCommit = useCallback(() => {
    setIsDragging(false); // Interaction complete
    const sortedPercent = [...localRange].sort((a, b) => a - b);
    const actualRange = [
      minX + sortedPercent[0] * (maxX - minX),
      minX + sortedPercent[1] * (maxX - minX)
    ];
    onFilterChange(filename, selectedCol, actualRange);
  }, [localRange, minX, maxX, filename, selectedCol, onFilterChange]);

  // Handle typing: only updates the local string state
  const handleInputChange = (index, val) => {
    const newTemp = [...tempInputs];
    newTemp[index] = val;
    setTempInputs(newTemp);
  };

  const handleDateChange = (index, dateStr) => {
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return;

    const nextRange = [...currentRange];
    nextRange[index] = ts;
    const sortedRange = nextRange.sort((a, b) => a - b);

    onFilterChange(filename, selectedCol, sortedRange);
  };


  const handleInputBlur = (index) => {
    const restored = [...tempInputs];
    // Revert to the value currently in the global filter
    restored[index] = formatForInput(currentRange[index]);
    setTempInputs(restored);
  };

  // Manual input logic to clamp and sort
  const handleInputKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      const val = parseFloat(tempInputs[index]);
      if (isNaN(val)) return e.target.blur();

      // Clamp value between minX and maxX
      const clampedVal = Math.max(minX, Math.min(maxX, val));
      
      const nextRange = [...currentRange];
      nextRange[index] = clampedVal;
      
      // Sort to ensure the range is always [smaller, larger]
      const sortedRange = nextRange.sort((a, b) => a - b);
      
      onFilterChange(filename, selectedCol, sortedRange);
      e.target.blur();
    }
  };

  const distributionIndices = useMemo(() => {
    return getIndicesExcluding(table, headers, data, selectedCol, false); 
  }, [
    table, headers, selectedCol, 
    data.searchFilter, data.searchColumns, data.spatialFilter, 
    data.numericFilters, data.dateFilter, data.includeMissingDates, 
    data.numericIncludeNulls, data.categoricalFilters
  ]);

  // Setup NullButton State
  const isFiltered = numericFilters && numericFilters[selectedCol];
  const defaultInclude = isFiltered ? false : true;
  const includeNulls = numericIncludeNulls?.[selectedCol] ?? defaultInclude;
  const excludeNulls = !includeNulls;

  const handleNullToggle = () => {
    onNullToggle(filename, selectedCol, !includeNulls);
  };

  return (
    <PanelContainer id={id}>
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
        <span className="text-xs font-bold truncate ml-2">{!basic? "Dist: " + filename : ""}</span>
        <div className="flex items-center gap-0.5">
          
          {/* Precise Inputs */}
          <div className="flex items-center bg-white/60 px-1 py-0.45 rounded-lg border border-gray-300 shadow-sm mr-1">
            <input 
              type={isDateCol ? "date" : "text"}
              className="text-[10px] bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-center 
             [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:p-0"
              style={!isDateCol ? { width: `${Math.max(1, tempInputs[0].length)}ch` } : {}}
              value={tempInputs[0]}
              onChange={(e) => handleInputChange(0, e.target.value)}
              onKeyDown={(e) => handleInputKeyDown(e, 0)}
              onBlur={() => !isDateCol && setTempInputs([formatForInput(currentRange[0]), tempInputs[1]])}
            />
            <span className="text-gray-400 text-[10px]">→</span>
            <input 
              type={isDateCol ? "date" : "text"}
              className="text-[10px] bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-center 
             [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:p-0"
              style={isDateCol ? { width: '82px' } : { width: `${Math.max(1, tempInputs[1].length)}ch` }}
              value={tempInputs[1]}
              onChange={(e) => handleInputChange(1, e.target.value)}
              onKeyDown={(e) => handleInputKeyDown(e, 1)}
              onBlur={() => !isDateCol && setTempInputs([formatForInput(currentRange[0]), tempInputs[1]])}
            />
          </div>
          <select 
            className="dropdown-selector w-full truncate" 
            value={selectedCol || ''} 
            onChange={e => {
                // When changing columns, reset dragging and filters
                setIsDragging(false);
                onColorByChange(id, e.target.value);
            }}
          >
            {numericHeaders.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <NullButton 
            isActive={excludeNulls} 
            onClick={handleNullToggle} 
            title={excludeNulls ? "Include null values" : "Exclude null values"} 
          />
          <LogButton isActive={isLogScale} onClick={() => setIsLogScale(s => !s)} />
          {onDuplicate && (
          <DuplicateButton onClick={() => onDuplicate(id)} />
          )}
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>

      <div className="flex-grow flex flex-col p-2 px-4 overflow-hidden">
        {selectedCol && table?.getChild(selectedCol) ? (
          <NumericDistributionViewer 
            table={table}
            columnName={selectedCol}
            searchIndices={distributionIndices}
            minVal={minX}
            maxVal={maxX}
            range={localRange}
            isLogScale={isLogScale}
            onChange={handleVisualChange}
            onMouseUp={handleFilterCommit}
          />
        ) : (
          <div className="flex-grow flex items-center justify-center text-gray-500 text-xs italic">
            No numeric data available.
          </div>
        )}
      </div>
    </PanelContainer>
  );
});

const StatsPanel = React.memo(function StatsPanel({ id, data, colorConfig, onColorByChange, onRemove, onDuplicate, onCategoryClick, onNullToggle, basic = false, onConfigChange }) {
  const { table, headers, filename, categoricalFilters, numericIncludeNulls } = data;
  const selectedCol = data.colorBy || headers[0] || '';
  
   const viewType = data.viewType || 'pie';
   const setViewType = (updater) => {
       const next = typeof updater === 'function' ? updater(viewType) : updater;
       onConfigChange && onConfigChange(id, { viewType: next });
   };
  const [showSettings, setShowSettings] = useState(false);
  
  // Isolate the searchIndices for this panel specifically to ignore its own column categorical filter
  const statsIndices = useMemo(() => {
    return getIndicesExcluding(table, headers, data, selectedCol, false);
  }, [
    table, headers, selectedCol, 
    data.searchFilter, data.searchColumns, data.spatialFilter, 
    data.numericFilters, data.dateFilter, data.includeMissingDates, 
    data.numericIncludeNulls, data.categoricalFilters
  ]);

  const activeCategory = categoricalFilters?.[selectedCol] || null;

  const handleCategoryClick = useCallback((category) => {
    if (category === 'Other') return;
    onCategoryClick(filename, selectedCol, category === activeCategory ? null : category);
  }, [activeCategory, filename, selectedCol, onCategoryClick]);

  const includeNulls = numericIncludeNulls?.[selectedCol] ?? true;
  const excludeNulls = !includeNulls;

  const handleNullToggle = () => {
    if (onNullToggle) {
      onNullToggle(filename, selectedCol, !includeNulls);
    }
  };

  
  // State for gauge/pie configuration
  const[pieConfig, setPieConfig] = useState({
    angle: 360,        // Total sweep of the chart (e.g. 180 for semi-circle)
    rotation: -90,     // Start rotation (in degrees)
    innerRadius: 0,  // 0 = Pie, >0 = Donut
    outerRadius: 1   // Size of the chart
  });

  return (
    <PanelContainer id={id}>
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
       <span className="truncate ml-2">{!basic? "Stats: " + filename : ""}</span>
        <div className="flex items-center gap-0.5">
          <select 
            className="dropdown-selector max-w-[160px] truncate"
            value={selectedCol} 
            onChange={e => onColorByChange(id, e.target.value)}
          >
            {headers.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <NullButton isActive={excludeNulls} onClick={handleNullToggle} title={excludeNulls ? "Include null values" : "Exclude null values"} />
          <PieBarButton 
            isActive={viewType=='bar'}
            onClick={() => setViewType(prev => prev === 'pie' ? 'bar' : 'pie')} 
          />
          {onDuplicate && (
          <DuplicateButton onClick={() => onDuplicate(id)} />
          )}
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>
      
      <div className="flex-grow flex flex-col p-4 overflow-hidden relative">
        {/* Settings Toggle Button */}
        {viewType === 'pie' && (
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className={`absolute bottom-4 left-4 px-2 py-2 rounded-full bg-gray-50 border border-gray-300 hover:bg-gray-100 text-gray-600 z-20 shadow-md border-b-2 active:border-b visibility ${showSettings ? 'hidden' : 'visible'} transition-all active:translate-y-0.3 active:shadow-none`}
          >
            <CogGlyph />
          </button>
        )}

        {/* Floating Control Panel */}
        {showSettings && viewType === 'pie' && (
          <>
            {/* Transparent Backdrop to catch clicks outside */}
            <div 
              className="fixed inset-0 z-20 cursor-default" 
              onClick={() => setShowSettings(false)} 
            />
            <div className="absolute bottom-4 left-4 bg-white/95 border border-gray-200 p-3 rounded-xl z-30 w-48 flex flex-col gap-3 text-[10px] shadow-lg">
              <div>
                <label className="flex justify-between mb-1 text-gray-500">Angle <span>{pieConfig.angle}°</span></label>
                <input type="range" min="10" max="360" step="5" value={pieConfig.angle} 
                  onChange={e => setPieConfig(p => ({...p, angle: +e.target.value}))} className="w-full h-1" />
              </div>
              <div>
                <label className="flex justify-between mb-1 text-gray-500">Rotation <span>{pieConfig.rotation}°</span></label>
                <input type="range" min="-180" max="180" step="5" value={pieConfig.rotation} 
                  onChange={e => setPieConfig(p => ({...p, rotation: +e.target.value}))} className="w-full h-1" />
              </div>
              <div>
                <label className="flex justify-between mb-1 text-gray-500">Inner Radius <span>{pieConfig.innerRadius}</span></label>
                <input type="range" min="0" max="0.9" step="0.05" value={pieConfig.innerRadius} 
                  onChange={e => setPieConfig(p => ({...p, innerRadius: +e.target.value}))} className="w-full h-1" />
              </div>
              <div>
                <label className="flex justify-between mb-1 text-gray-500">Outer Radius <span>{pieConfig.outerRadius}</span></label>
                <input type="range" min="0.2" max="1" step="0.05" value={pieConfig.outerRadius} 
                  onChange={e => setPieConfig(p => ({...p, outerRadius: +e.target.value}))} className="w-full h-1" />
              </div>
            </div>
          </>
        )}

        {selectedCol && table?.getChild(selectedCol) ? (
          <StatsViewer 
            table={table}
            selectedCol={selectedCol}
            searchIndices={statsIndices}
            viewType={viewType}
            pieConfig={pieConfig}
            colorConfig={colorConfig}
            excludeNulls={excludeNulls}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryClick}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-xs italic">
            No categorical data available.
          </div>
        )}
      </div>
    </PanelContainer>
  );
});

const TimeStatsPanel = React.memo(function TimeStatsPanel({ id, data, colorConfig, onColorByChange, onRemove, onConfigChange, basic = false }) {
  const { table, headers, filename } = data;
  const dateCol = headers.find(h => /parsed_collection_date_start/i.test(h));
  const selectedCol = data.colorBy || headers[0] || '';
  
  const isLogScale = data.isLogScale || false;
  const setIsLogScale = (updater) => {
      const next = typeof updater === 'function' ? updater(isLogScale) : updater;
      onConfigChange && onConfigChange(id, { isLogScale: next });
  };

  // 1. Isolate the include missing dates toggle to THIS panel only via local config updates
  const includeMissingDatesLocal = data.includeMissingDatesLocal ?? true;
  const handleNullToggle = () => {
    onConfigChange && onConfigChange(id, { includeMissingDatesLocal: !includeMissingDatesLocal });
  };

  // 2. Fetch full global limits for the date boundaries
  const { globalMin, globalMax } = useMemo(() => {
    if (!table || !dateCol) return { globalMin: 0, globalMax: 1 };
    const dateData = table.getChild(dateCol);
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < table.numRows; i++) {
      const val = dateData.get(i);
      const ts = new Date(val).getTime();
      if (!isNaN(ts) && ts !== 0) {
        if (ts < min) min = ts;
        if (ts > max) max = ts;
      }
    }
    if (min === Infinity) min = 0;
    if (max === -Infinity) max = min + 31536000000;
    if (min === max) max = min + 31536000000;
    return { globalMin: min, globalMax: max };
  }, [table, dateCol]);

  // 3. Fallback to external global zooming filters if applied, else full bounds
  const minX = data.dateFilter ? data.dateFilter[0] : globalMin;
  const maxX = data.dateFilter ? data.dateFilter[1] : globalMax;

  // 4. Force search index generation WITHOUT global date or missing date toggles
  // This allows the panel to operate independently, while respecting categorical/search/space filters
  const plotIndices = useMemo(() => {
    return getIndicesExcluding(table, headers, data, null, true);
  }, [
    table, headers, 
    data.searchFilter, data.searchColumns, data.spatialFilter, 
    data.numericFilters, data.dateFilter, data.includeMissingDates, 
    data.numericIncludeNulls, data.categoricalFilters
  ]);

  return (
    <PanelContainer id={id}>
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
        <span className="text-xs font-bold truncate ml-2">{!basic? "TimeStats: " + filename : ""}</span>
        <div className="flex items-center gap-0.5">
          <select className="dropdown-selector max-w-[140px] truncate" value={selectedCol} onChange={e => onColorByChange(id, e.target.value)}>
            {headers.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <NullButton isActive={!includeMissingDatesLocal} onClick={handleNullToggle} title={!includeMissingDatesLocal ? "Include samples with missing dates" : "Exclude samples with missing dates"} />
          <LogButton isActive={isLogScale} onClick={() => setIsLogScale(s => !s)} />
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>
      <div className="flex-grow p-2 px-4 overflow-hidden">
        {dateCol && table?.getChild(dateCol) && table?.getChild(selectedCol) ? (
          <TimeStatsViewer table={table} dateCol={dateCol} catCol={selectedCol} searchIndices={plotIndices} minX={minX} maxX={maxX} isLogScale={isLogScale} colorConfig={colorConfig} includeMissingDates={includeMissingDatesLocal} />
        ) : (
          <div className="flex-grow flex items-center justify-center text-gray-500 text-xs italic h-full">No temporal or categorical data available.</div>
        )}
      </div>
    </PanelContainer>
  );
});

const LifeMapPanel = React.memo(function LifeMapPanel({ id, data, onRemove, basic = false, selectedSample }) {
  const { table, headers, filename, searchIndices } = data;

  // Apply current search and date filters
  const filteredData = useMemo(() => {
    if (!table) return [];
    const rows = [];
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);

    for (const i of source) {
      rows.push(table.get(i));
    }
    return rows;
  }, [table, searchIndices, headers]);

  return (
    <PanelContainer id={id} basic={basic}>
      {!basic && (
        <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
          <span className="truncate ml-2">LifeMap: {filename}</span>
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      )}
      <div className="flex-grow overflow-hidden relative">
        <LifeMapViewer data={filteredData} theme={basic? "dark" : "light"} selectedSample={selectedSample} />
      </div>
    </PanelContainer>
  );
});


const TimePlotPanel = React.memo(function TimePlotPanel({ id, data, onRemove, onFilterChange, onConfigChange, basic = false }) {
  const { table, headers, filename } = data;
  const dateCol = headers.find(h => /parsed_collection_date_start/i.test(h));
  const isLogScale = data.isLogScale || false;
  const setIsLogScale = (updater) => {
      const next = typeof updater === 'function' ? updater(isLogScale) : updater;
      onConfigChange && onConfigChange(id, { isLogScale: next });
  };
  const includeMissingDates = data.includeMissingDates ?? true;

  // Calculate indices for the plot representation 
  // This respects Search and Spatial filters but ignores the Date filter
  const plotIndices = useMemo(() => {
    return getIndicesExcluding(table, headers, data, null, true);
  }, [
    table, headers, 
    data.searchFilter, data.searchColumns, data.spatialFilter, 
    data.numericFilters, data.dateFilter, data.includeMissingDates, 
    data.numericIncludeNulls, data.categoricalFilters
  ]);

  // Pre-calculate full data range once
  const { minX, maxX } = useMemo(() => {
    if (!table || !dateCol) return { minX: 0, maxX: 1 };
    const dateData = table.getChild(dateCol);
    let minX = Infinity;
    let maxX = -Infinity;
    for (let i = 0; i < table.numRows; i++) {
      const val = dateData.get(i);
      const ts = new Date(val).getTime();
      if (!isNaN(ts)) {
        if (ts < minX) minX = ts;
        if (ts > maxX) maxX = ts;
      }
    }
    if (minX === Infinity) minX = 0;
    if (maxX === -Infinity) maxX = minX + yearInMs; // Add a year if no valid dates
    if (minX === maxX) maxX = minX + yearInMs; // Add a year if all dates are the same
    return { minX, maxX };
  }, [table, dateCol]);

  const [range, setRange] = useState([0, 1]);
  const [isDragging, setIsDragging] = useState(false); // dragging state to prevent visual snap-backs while the user interacts with the slider
  const throttleRef = useRef(null);

  // Clean up throttle on unmount
  useEffect(() => {
    return () => {
      if (throttleRef.current) cancelAnimationFrame(throttleRef.current);
    };
  }, []);

  // effect to sync the local range when external data bounds or dateFilter change
  useEffect(() => {
    if (isDragging) return;
    if (data.dateFilter) {
      setRange([
        Math.max(0, (data.dateFilter[0] - minX) / (maxX - minX || 1)),
        Math.min(1, (data.dateFilter[1] - minX) / (maxX - minX || 1))
      ]);
    } else {
      setRange([0, 1]);
    }
  }, [data.dateFilter, minX, maxX, isDragging]);

  const handleRangeChange = useCallback((newRange, commit = false) => {
    // Update local state immediately for smooth UI
    setRange(newRange);
    
    if (!commit) {
      setIsDragging(true);
    } else {
      setIsDragging(false);
      // Only trigger expensive global filter when user lets go (commit)
      const sorted = [...newRange].sort((a, b) => a - b);
      onFilterChange(filename, [
        minX + sorted[0] * (maxX - minX), 
        minX + sorted[1] * (maxX - minX)
      ], includeMissingDates);
    }
  }, [minX, maxX, filename, onFilterChange, includeMissingDates]);


  const getISODate = (percent) => {
    const ts = minX + percent * (maxX - minX);
    return new Date(ts).toISOString().split('T')[0];
  };

  const handleManualDateChange = (index, dateStr) => {
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return;
    const newPercent = Math.max(0, Math.min(1, (ts - minX) / (maxX - minX)));
    const newRange = [...range];
    newRange[index] = newPercent;
    // For manual input, commit immediately
    handleRangeChange(newRange, true);
  };


  // Helper to trigger filter change with current slider state
  const handleToggleMissingDates = () => {
    const sorted = [...range].sort((a, b) => a - b);
    const currentActualRange = [
      minX + sorted[0] * (maxX - minX), 
      minX + sorted[1] * (maxX - minX)
    ];
    
    // If the sliders are at the very edges (0 and 1), we can pass null 
    // to allow the search optimization to trigger, otherwise pass the range.
    const isFullRange = range[0] <= 0 && range[1] >= 1;
    
    onFilterChange(
      filename, 
      isFullRange ? null : currentActualRange, 
      !includeMissingDates
    );
  };

  return (
    <PanelContainer id={id}>
      {/* Header */}
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
        <span className="text-xs font-bold truncate ml-2">{!basic? `Timeline: ${filename}` : ''}</span>
        <div className="flex items-center gap-0.5">          
          {/* Precise Date Inputs */}
          <div className="flex items-center bg-white/60 px-1 py-0.45 rounded-lg border border-gray-300 shadow-sm">
            <input 
              type="date" className="text-[10px] bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-center 
             [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:p-0"
              value={getISODate(range[0])}
              onChange={(e) => handleManualDateChange(0, e.target.value)}
            />
            <span className="text-gray-400 text-[10px]">→</span>
            <input 
              type="date" className="text-[10px] bg-transparent border-none p-0 focus:ring-0 cursor-pointer text-center 
             [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:p-0"
              value={getISODate(range[1])}
              onChange={(e) => handleManualDateChange(1, e.target.value)}
            />
          </div>
          {/* Include/Exclude Missing Dates Toggle */}
          <NullButton isActive={!includeMissingDates} onClick={handleToggleMissingDates} title={`${includeMissingDates ? 'Exclude' : 'Include'} samples with missing dates`} />
          <LogButton isActive={isLogScale} onClick={() => setIsLogScale(s => !s)} />
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>
        {dateCol && table?.getChild(dateCol) ? (
        <TimePlotViewer 
          table={table}
          dateCol={dateCol}
          searchIndices={plotIndices}
          minX={minX}
          maxX={maxX}
          range={range}
          isLogScale={isLogScale}
          includeMissingDates={includeMissingDates}
          onChange={handleRangeChange}
        />
      ) : (
        <div className="flex-grow flex items-center justify-center text-gray-500 text-xs italic">
          No temporal data available.
        </div>
      )}
    </PanelContainer>
  );
});

const ScatterPlotPanel = React.memo(function ScatterPlotPanel({ id, data, colorConfig, onColorByChange, onConfigChange, onRemove, basic = false }) {
  const { table, headers, filename, searchIndices } = data;
  const isLogScale = data.isLogScale || false;
  const setIsLogScale = (updater) => {
      const next = typeof updater === 'function' ? updater(isLogScale) : updater;
      onConfigChange && onConfigChange(id, { isLogScale: next });
  };

  const numericHeaders = useMemo(() => {
    return headers.filter(h => {
      const col = table.getChild(h);
      //return col && typeof col.get(0) === 'number';
      // More robust check: sample multiple values to confirm numeric-ness, allowing for some nulls
      if (!col) return false;
      let numericCount = 0;
      let totalCount = 0;
      for (let i = 0; i < Math.min(50, table.numRows); i++) {
        const val = col.get(i);
        if (val === null || val === undefined) continue;
        totalCount++;
        if (typeof val === 'number') numericCount++;
      }
      return totalCount > 0 && numericCount / totalCount > 0.8; // at least 80% numeric
      
    });
  }, [table, headers]);

  const xAxis = data.xAxis || numericHeaders[0];
  const setXAxis = (val) => onConfigChange && onConfigChange(id, { xAxis: val });

  const yAxis = data.yAxis || numericHeaders[1] || numericHeaders[0];
  const setYAxis = (val) => onConfigChange && onConfigChange(id, { yAxis: val });


  const selectedColorBy = data.colorBy || headers[0];

  // Calculate Numeric Stats for the colorBy column
  const stats = useMemo(() => {
    // Filtered stats for the color dimension
    return getColumnStats(table, selectedColorBy, searchIndices);
  }, [table, selectedColorBy, searchIndices]);

  // Sync gradient state
  const isGradient = data.isGradient ?? stats.isNumeric;

  return (
    <PanelContainer id={id}>
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
        <span className="text-xs font-bold truncate ml-2">{basic? '' : 'Scatter: '+filename}</span>
        <div className="flex items-center gap-0.5">
          <select className="dropdown-selector-scatter truncate" value={xAxis || ''} title={xAxis} onChange={e => setXAxis(e.target.value)}>
            {numericHeaders.map(h => <option key={h} value={h}>X: {h}</option>)}
          </select>
          <select className="dropdown-selector-scatter truncate" value={yAxis || ''} title={yAxis} onChange={e => setYAxis(e.target.value)}>
            {numericHeaders.map(h => <option key={h} value={h}>Y: {h}</option>)}
          </select>
          <select className="dropdown-selector-scatter truncate" value={selectedColorBy || ''} title={selectedColorBy} onChange={e => onColorByChange(id, e.target.value)}>
            {headers.map(h => <option key={h} value={h}>Color: {h}</option>)}
          </select>
          
          {/* Gradient Toggle Button */}
          {stats.isNumeric && (
            <button
              onClick={() => onConfigChange && onConfigChange(id, { isGradient: !isGradient })}
              title="Toggle Gradient/Categorical"
              className={`w-5 h-5 border border-gray-800 rounded-full mx-1 transition-colors ${isGradient ? 'bg-gradient-to-r from-yellow-400 to-red-500' : 'bg-gray-200'}`}
            />
          )}
          <LogButton isActive={isLogScale} onClick={() => setIsLogScale(s => !s)} />
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>

      <div className="flex-grow overflow-hidden px-2">
        {xAxis && yAxis && table?.getChild(xAxis) && table?.getChild(yAxis) ? (
          <ScatterPlotViewer 
            table={table}
            xAxis={xAxis}
            yAxis={yAxis}
            colorBy={selectedColorBy}
            searchIndices={searchIndices}
            colorConfig={colorConfig}
            isGradient={isGradient}
            stats={stats}
            isLogScale={isLogScale}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-xs italic">
            No numeric data available.
          </div>
        )}
      </div>
    </PanelContainer>
  );
});


const BoxPlotPanel = React.memo(function BoxPlotPanel({ id, data, colorConfig, onColorByChange, onRemove, onConfigChange, basic = false }) {
  const { table, headers, filename, searchIndices } = data;
  const viewType = data.viewType || 'boxplot';
  const isLogScale = data.isLogScale || false;
  const setIsLogScale = (updater) => {
      const next = typeof updater === 'function' ? updater(isLogScale) : updater;
      onConfigChange && onConfigChange(id, { isLogScale: next });
  };
  const setViewType = (updater) => {
      const next = typeof updater === 'function' ? updater(viewType) : updater;
      onConfigChange && onConfigChange(id, { viewType: next });
  };

  const numericHeaders = useMemo(() => {
    return headers.filter(h => {
      const col = table?.getChild(h);
      if (!col) return false;
      let numCount = 0;
      for (let i = 0; i < Math.min(50, table.numRows); i++) {
        const v = col.get(i);
        if (v != null && typeof v === 'number') numCount++;
      }
      return numCount > 0;
    });
  }, [table, headers]);

  const nonNumericHeaders = useMemo(() => {
    return headers.filter(h => {
      const col = table?.getChild(h);
      if (!col) return false;
      let nonNumCount = 0;
      for (let i = 0; i < Math.min(50, table.numRows); i++) {
        const v = col.get(i);
        if (v != null && typeof v !== 'number') nonNumCount++;
      }
      return nonNumCount > 0;
    });
  }, [table, headers]);

  const yAxis = data.yAxis || numericHeaders[0] || headers[0];
  const setYAxis = (val) => onConfigChange && onConfigChange(id, { yAxis: val });
  const xAxis = data.colorBy || headers[0]; 

  return (
    <PanelContainer id={id}>
      <div className={`panel-header ${basic ? '' : 'cursor-move'}`}>
        <span className="text-xs font-bold truncate ml-2">{basic? '' : 'BoxPlot: '+filename}</span>
        <div className="flex items-center gap-0.5">
          <select className="dropdown-selector truncate max-w-[120px]" value={xAxis || ''} title={xAxis} onChange={e => onColorByChange(id, e.target.value)}>
            {nonNumericHeaders.map(h => <option key={h} value={h}>X: {h}</option>)}
          </select>
          <select className="dropdown-selector truncate max-w-[120px]" value={yAxis || ''} title={yAxis} onChange={e => setYAxis(e.target.value)}>
            {numericHeaders.map(h => <option key={h} value={h}>Y: {h}</option>)}
          </select>
          <ViolinBoxButton 
            isActive={viewType=='boxplot'}
            onClick={() => setViewType(prev => prev === 'violinplot' ? 'boxplot' : 'violinplot')} 
          />
          {!/date/i.test(yAxis) && (
            <LogButton isActive={isLogScale} onClick={() => setIsLogScale(s => !s)} />
          )}
          <RemoveButton onClick={() => onRemove(id)} />
        </div>
      </div>
      <div className="flex-grow overflow-hidden px-2 pt-2 pb-1">
        {xAxis && yAxis && table?.getChild(xAxis) && table?.getChild(yAxis) ? (
          viewType === 'boxplot' ? (
            <BoxPlotViewer table={table} categoryCol={xAxis} numericCol={yAxis} searchIndices={searchIndices} colorConfig={colorConfig} isLogScale={isLogScale} />
          ) : (
            <ViolinPlotViewer table={table} categoryCol={xAxis} numericCol={yAxis} searchIndices={searchIndices} colorConfig={colorConfig} isLogScale={isLogScale} />
          )
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-xs italic">
            No data available for plot.
          </div>
        )}
      </div>
    </PanelContainer>
  );
});



const ActiveFiltersPanel = React.memo(function ActiveFiltersPanel({ 
  data, searchPath, onLoadAll, appliedSearch, sqlQuery, displaySqlQuery, completeCount, isComputing, constraints,
  onClearAppliedSearch, onClearLocalSearch, onClearSpatial, onClearDate, onClearMissingDates, onClearNumeric, onClearNumericNulls, onClearCategorical, onClearConstraint,  onPasteQuery
}) {
  const {
    searchFilter, spatialFilter,
    numericFilters, numericIncludeNulls,
    dateFilter, includeMissingDates,
    categoricalFilters,
  } = data || {};

  
  const activeSummary = useMemo(() => {
    const parts = [];
    const fn = data?.filename;
    
    if (appliedSearch && appliedSearch.term && appliedSearch.column !== 'stat co-occurrence') {
        let displayCol = appliedSearch.column || 'Search';
        if (displayCol !== 'Search') {
            displayCol = displayCol.replace(/^(HOST_|VIRUS_|ENVIRONMENT_)/i, '').replace(/_/g, ' ').toLowerCase();
            displayCol = displayCol.charAt(0).toUpperCase() + displayCol.slice(1);
        }
        // Toggle operator based on matchType
        const op = appliedSearch.matchType === 'contains' ? 'contains' : '=';
        
        parts.push({
          label: `${displayCol} ${op} "${appliedSearch.term}"`, // For ActiveFiltersPanel use: `Dataset Filter: ${displayCol} ${op} "${appliedSearch.term}"`
          onRemove: () => onClearAppliedSearch && onClearAppliedSearch()
        });
    }

    //if (searchFilter) parts.push({ label: `Search: "${searchFilter}"`, onRemove: () => onClearLocalSearch && onClearLocalSearch(fn) });
    if (spatialFilter) parts.push({ label: `Spatial: Bounding Box Filter`, onRemove: () => onClearSpatial && onClearSpatial(fn) });
    if (dateFilter) parts.push({ label: `Date: ${new Date(dateFilter[0]).toISOString().split('T')[0]} to ${new Date(dateFilter[1]).toISOString().split('T')[0]}`, onRemove: () => onClearDate && onClearDate(fn) });
    if (includeMissingDates === false) parts.push({ label: `Dates: Excluding Nulls`, onRemove: () => onClearMissingDates && onClearMissingDates(fn) });
    if (numericFilters) {
      Object.entries(numericFilters).forEach(([c, r]) => {
        let displayMin = r[0] <= -999999999 ? '-∞' : Number(r[0].toFixed(3));
        let displayMax = r[1] >= 999999999 ? '∞' : Number(r[1].toFixed(3));
        parts.push({ label: `${c}: ${displayMin} to ${displayMax}`, onRemove: () => onClearNumeric && onClearNumeric(fn, c) });
      });
    }
    if (numericIncludeNulls) {
      Object.entries(numericIncludeNulls).forEach(([c, inc]) => {
        if (!inc && !numericFilters?.[c]) parts.push({ label: `${c}: Excluding Nulls`, onRemove: () => onClearNumericNulls && onClearNumericNulls(fn, c, true) });
        if (inc && numericFilters?.[c]) parts.push({ label: `${c}: Including Nulls`, onRemove: () => onClearNumericNulls && onClearNumericNulls(fn, c, false) });
      });
    }
    if (categoricalFilters) {
      Object.entries(categoricalFilters).forEach(([c, v]) => {
        parts.push({ label: `${c} = ${v}`, onRemove: () => onClearCategorical && onClearCategorical(fn, c) });
      });
    }
    if (constraints && constraints.length > 0) {
      constraints.forEach((c, idx) => {
        parts.push({
          label: `SQL: ${c.length > 40 ? c.substring(0, 40) + '...' : c}`,
          title: c,
          onRemove: () => onClearConstraint && onClearConstraint(idx)
        });
      });
    }

    return parts;
  }, [searchFilter, spatialFilter, numericFilters, numericIncludeNulls, constraints, dateFilter, includeMissingDates, categoricalFilters, appliedSearch, data?.filename, onClearAppliedSearch, onClearLocalSearch, onClearSpatial, onClearDate, onClearMissingDates, onClearNumeric, onClearNumericNulls, onClearCategorical]);


  const hasAnyFilter = sqlQuery.length > 0;
  const [copied, setCopied] = useState(false);
  const [showQueryInput, setShowQueryInput] = useState(false);
  const [queryText, setQueryText] = useState('');
  const inputRef = useRef(null);

  // Automatically focus the input when the bar opens
useEffect(() => {
  if (showQueryInput && inputRef.current) {
    inputRef.current.focus();
  }
}, [showQueryInput]);


  return (
    <PanelContainer id="active-filters">
      <div className="panel-header bg-white border-b border-gray-200">
        <span className="truncate ml-2 text-gray-800 font-bold text-xs">Active Filters</span>
      </div>
      <div className="flex-grow flex flex-col p-4 gap-3 bg-gray-50 relative">
        {!hasAnyFilter ? (
          <div className="text-gray-400 italic text-xs text-center mt-4">No filters applied. Filter the variables of interest to load all matching samples.</div>
        ) : (
          <>
            <div className="flex flex-col gap-1 mb-1 shrink-0">
              {activeSummary.map((item, i) => (
                <div key={i} className="text-[10px] bg-blue-100 text-blue-800 px-2 py-1 rounded-lg border border-blue-200 font-medium flex items-center justify-between gap-2 max-w-full">
                  <span className="truncate" title={item.title || item.label}>
                    {item.label}
                  </span>
                  <button
                    onClick={item.onRemove}
                    className="text-blue-500 hover:text-red-500 hover:bg-red-200 rounded-full p-0.5 shrink-0 transition-colors"
                    title="Remove filter"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            <div className="text-[10px] text-gray-500 font-bold mt-1 shrink-0 uppercase tracking-wide">SQL Equivalent:</div>
            <div className="relative bg-slate-900 rounded-xl border border-gray-300 overflow-hidden shadow-inner flex flex-col group min-h-[20px]">
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(sqlQuery);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="p-1 rounded-md bg-slate-700/80 text-slate-300 hover:text-white hover:bg-slate-600 transition-colors flex items-center gap-1"
                  title="Copy SQL to clipboard"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                    </>
                  ) : (
                    <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <pre className="text-[10px] text-blue-300 font-mono p-3 flex-grow whitespace-pre-wrap break-all select-text">
                {displaySqlQuery || sqlQuery}
              </pre>
            </div>

          </>
        )}
        <div className="w-full mt-2 flex items-center">
          {/* Morphing Container (Button <-> Input) */}
          {/* Using a grid to stack them perfectly on top of one another */}
          <div className="grid flex-grow transition-all duration-300 ease-in-out items-center">
            
            {/* Initial Button */}
            <button
              onClick={() => setShowQueryInput(true)}
              tabIndex={showQueryInput ? -1 : 0}
              className={`col-start-1 row-start-1 w-full px-3 py-1.5 bg-white border border-blue-300 text-gray-700 hover:bg-blue-50 text-xs font-bold rounded-lg shadow-sm whitespace-nowrap transition-all duration-300 ease-in-out ${
                showQueryInput
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100 pointer-events-auto"
              }`}
            >
              Paste query and load
            </button>

            {/* Input Field */}
            <input
              ref={inputRef}
              type="text"
              placeholder="Paste SQL query..."
              value={queryText}
              tabIndex={showQueryInput ? 0 : -1}
              onChange={(e) => setQueryText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && queryText.trim() && onPasteQuery) {
                  onPasteQuery(queryText.trim());
                  setShowQueryInput(false);
                  setQueryText("");
                } else if (e.key === "Escape") {
                  setShowQueryInput(false);
                  setQueryText("");
                }
              }}
              className={`col-start-1 row-start-1 w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs rounded-lg shadow-sm transition-all duration-300 ease-in-out ${
                showQueryInput
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none"
              }`}
            />
          </div>

          {/* Action Buttons sliding in from the right */}
          <div
            className={`flex items-center overflow-hidden transition-all duration-300 ease-in-out ${
              showQueryInput ? "max-w-[100px] opacity-100 ml-1.5" : "max-w-0 opacity-0 ml-0"
            }`}
          >
            <div className="flex items-center gap-1.5 w-max">
              <button
                onClick={() => {
                  if (queryText.trim() && onPasteQuery) {
                    onPasteQuery(queryText.trim());
                  }
                  setShowQueryInput(false);
                  setQueryText("");
                }}
                tabIndex={showQueryInput ? 0 : -1}
                className="p-1.5 shrink-0 border border-transparent hover:border-blue-200 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                title="Load query"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
                  />
                </svg>
              </button>
              <button
                onClick={() => {
                  setShowQueryInput(false);
                  setQueryText("");
                }}
                tabIndex={showQueryInput ? 0 : -1}
                className="p-1.5 shrink-0 border border-transparent hover:border-red-200 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                title="Cancel"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </PanelContainer>
  );
});

const HeaderFiltersPanel = React.memo(function HeaderFiltersPanel({
  data, onLoadAll, appliedSearch, sqlQuery, completeCount, isComputing, nsamples, isFetchingData, isStatFetching, constraints,
  onClearAppliedSearch, onClearLocalSearch, onClearSpatial, onClearDate, onClearMissingDates, onClearNumeric, onClearNumericNulls, onClearCategorical, onClearConstraint
}) {
  const {
    searchFilter, spatialFilter,
    numericFilters, numericIncludeNulls,
    dateFilter, includeMissingDates,
    categoricalFilters,
  } = data || {};

  const activeSummary = useMemo(() => {
    const parts = [];
    const fn = data?.filename;
    
    if (appliedSearch && appliedSearch.term && appliedSearch.column !== 'stat co-occurrence') {
        let displayCol = appliedSearch.column || 'Search';
        if (displayCol !== 'Search') {
            displayCol = displayCol.replace(/^(HOST_|VIRUS_|ENVIRONMENT_)/i, '').replace(/_/g, ' ').toLowerCase();
            displayCol = displayCol.charAt(0).toUpperCase() + displayCol.slice(1);
        }
        // Toggle operator based on matchType
        const op = appliedSearch.matchType === 'contains' ? 'contains' : '=';
        
        parts.push({
          label: `${displayCol} ${op} "${appliedSearch.term}"`, // For ActiveFiltersPanel use: `Dataset Filter: ${displayCol} ${op} "${appliedSearch.term}"`
          onRemove: () => onClearAppliedSearch && onClearAppliedSearch()
        });
    }

    //if (searchFilter) parts.push({ label: `Search: "${searchFilter}"`, onRemove: () => onClearLocalSearch && onClearLocalSearch(fn) });
    if (spatialFilter) parts.push({ label: `Spatial: Bounding Box Filter`, onRemove: () => onClearSpatial && onClearSpatial(fn) });
    if (dateFilter) {
      const fmtDate = (ts) => {
        const [y, m, d] = new Date(ts).toISOString().split('T')[0].split('-');
        return `${d}/${m}/${y}`;
      };
      parts.push({ 
        label: `Date: ${fmtDate(dateFilter[0])}-${fmtDate(dateFilter[1])}`, 
        onRemove: () => onClearDate && onClearDate(fn) 
      });
    }
    if (includeMissingDates === false) parts.push({ label: `Dates: Excluding Nulls`, onRemove: () => onClearMissingDates && onClearMissingDates(fn) });
    if (numericFilters) {
      Object.entries(numericFilters).forEach(([c, r]) => {
        const isDate = /date/i.test(c);
        let displayMin = r[0];
        let displayMax = r[1];
        
        if (isDate) {
          const format = (ts) => {
            if (ts <= -8640000000000000) return '-∞';
            if (ts >= 8640000000000000) return '∞';
            const d = new Date(ts);
            if (isNaN(d.getTime())) return ts;
            const [y, m, day] = d.toISOString().split('T')[0].split('-');
            return `${day}/${m}/${y}`;
          };
          displayMin = format(r[0]);
          displayMax = format(r[1]);
        } else {
          displayMin = r[0] <= -999999999 ? '-∞' : Number(r[0].toFixed(3));
          displayMax = r[1] >= 999999999 ? '∞' : Number(r[1].toFixed(3));
        }
        parts.push({ label: `${c}: ${displayMin} - ${displayMax}`, onRemove: () => onClearNumeric && onClearNumeric(fn, c) });
      });
    }
    if (numericIncludeNulls) {
      Object.entries(numericIncludeNulls).forEach(([c, inc]) => {
        if (!inc && !numericFilters?.[c]) parts.push({ label: `${c}: Excluding Nulls`, onRemove: () => onClearNumericNulls && onClearNumericNulls(fn, c, true) });
        if (inc && numericFilters?.[c]) parts.push({ label: `${c}: Including Nulls`, onRemove: () => onClearNumericNulls && onClearNumericNulls(fn, c, false) });
      });
    }
    if (categoricalFilters) {
      Object.entries(categoricalFilters).forEach(([c, v]) => {
        parts.push({ label: `${c} = ${v}`, onRemove: () => onClearCategorical && onClearCategorical(fn, c) });
      });
    }
    if (constraints && constraints.length > 0) {
      constraints.forEach((c, idx) => {
        parts.push({
          label: `SQL: ${c.length > 40 ? c.substring(0, 40) + '...' : c}`,
          title: c,
          onRemove: () => onClearConstraint && onClearConstraint(idx)
        });
      });
    }
    return parts;
  }, [searchFilter, spatialFilter, numericFilters, numericIncludeNulls, constraints, dateFilter, includeMissingDates, categoricalFilters, appliedSearch, data?.filename, onClearAppliedSearch, onClearLocalSearch, onClearSpatial, onClearDate, onClearMissingDates, onClearNumeric, onClearNumericNulls, onClearCategorical, onClearConstraint]);

  const hasAnyFilter = sqlQuery.length > 0;

  const [displayStats, setDisplayStats] = useState({
    nsamples,
    completeCount,
    isComputing,
    sqlQuery
  });

  useEffect(() => {
    // Wait 700ms before updating the UI to prevent rapid flickering
    const timer = setTimeout(() => {
      setDisplayStats(prev => {
        if (
          prev.nsamples !== nsamples || 
          prev.completeCount !== completeCount || 
          prev.isComputing !== isComputing || 
          prev.sqlQuery !== sqlQuery
        ) {
          return { nsamples, completeCount, isComputing, sqlQuery };
        }
        return prev;
      });
    }, 700); 

    return () => clearTimeout(timer);
  }, [nsamples, completeCount, isComputing, sqlQuery]);

  return (
      <div className='flex flex-row px-1 min-h-[6rem] h-24 w-full'>
        
        {/* Removed duplicate wrapper and added 'relative z-10' to prevent hover-stealing */}
        <div className='pt-2 mr-2 shrink-0 relative z-10 flex flex-col'>
          
          <div className='text-[13px] text-blue-900 font-bold text-center'>
          {displayStats.isComputing || typeof displayStats.completeCount !== 'number'
            ? (
              <span className="inline-flex items-center justify-center gap-1">
                {displayStats.nsamples.toLocaleString()} / 
                <span className="animate-spin h-3 w-3 border-2 border-blue-900 border-t-transparent rounded-full"/>
              </span>
            )
            : `${displayStats.nsamples.toLocaleString()}/${displayStats.completeCount.toLocaleString()}`
          }
            <div className="-mt-1 text-sm">
              {displayStats.sqlQuery.length > 0 ? 'matching samples' : 'samples'}
            </div>
            {isFetchingData && <div className="mt-1 text-md">loading...</div>}
            {isStatFetching && <div className="mt-1 text-md">fetching stat data...</div>}
          </div>
          
          {!isFetchingData && !isStatFetching && (
            <div className="flex items-center justify-center pt-0 shrink-0 mt-auto">
              <button
                onClick={() => onLoadAll(displayStats.sqlQuery, displayStats.completeCount)}
                disabled={displayStats.isComputing || typeof displayStats.completeCount !== 'number' || displayStats.completeCount === 0 || displayStats.nsamples >= displayStats.completeCount}
                className="cursor-pointer my-2 px-3 py-1.5 bg-white border border-gray-800 text-gray-800 hover:bg-blue-200 disabled:bg-gray-200 disabled:text-gray-500 text-xs font-bold rounded-lg shadow-sm transition-colors"
                title={
                  typeof displayStats.completeCount !== 'number' 
                    ? "Cannot load from backend: Local column filter applied" 
                    : displayStats.nsamples >= displayStats.completeCount 
                      ? "All matching samples are already loaded" 
                      : "Load all matching samples"
                }
              >
                Load All
              </button>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0 flex flex-col p-2 gap-3 relative z-0">
          {!hasAnyFilter ? (
            <div className="text-gray-400 italic text-xs text-center mt-4"></div>
          ) : (
            <>
              <div className="flex flex-wrap content-start w-full gap-[0.3rem] mb-1 overflow-y-auto max-h-[80px] shrink-0 custom-scrollbar pr-1">
                {activeSummary.map((item, i) => (
                  <div key={i} className="text-[11px] bg-blue-50 px-1.5 py-0.5 rounded-lg border border-gray-800 font-medium flex items-center gap-1 w-fit max-w-full min-w-0">
                    <span className="truncate" title={item.title || item.label}>
                      {item.label}
                    </span>
                    <button
                      onClick={item.onRemove}
                      className="text-blue-800 hover:text-red-500 hover:bg-red-200 rounded-full p-0 shrink-0 transition-colors"
                      title="Remove filter"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
  );
});

// --------- Simplified View Component for Basic Mode --------- 

const SimplifiedView = React.memo(({ 
  data, onSearchColChange, colorConfig, onColorByChange, onSpatialFilterChange,
  showInstructions, activeAttributions, onRowClick, selectedSample, setSelectedSample, selectedSampleIndex,
  searchPath, onRemoteSearch, isFetchingData, onOpenGeomModal, nRows, showFilters, setShowFilters, duplicatePanel,
   panels, panelData, searchIndicesMap, colorConfigsMap, removePanel, updateNumericFilter, updatePanelFilter, updateNumericNulls, updateCategoricalFilter, updateSearchFilter, onBaseLayerChange,  constraints, setConstraints, onPasteQuery,
   onOpenPlot, onOpenStats, onOpenNumericPlot, onOpenScatterPlot, onOpenBoxPlot, onConfigChange, onOpenTimeStats
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [containsCount, setContainsCount] = useState(null);
  const [isFetchingContainsCount, setIsFetchingContainsCount] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [appliedSearch, setAppliedSearch] = useState(null);
  const [completeCount, setCompleteCount] = useState(null);
  const [isComputingCount, setIsComputingCount] = useState(false);

  const [showLoadWarning, setShowLoadWarning] = useState(false);
  const [pendingLoadArgs, setPendingLoadArgs] = useState(null);

  const searchInputRef = useRef(null);
  const suggestionRefs = useRef([]);

  // Compute SQL Query for tracking the global complete matching count
  const { sqlQuery, displaySqlQuery } = useMemo(() => {
    if (!data || !data.headers) return { sqlQuery: '', displaySqlQuery: '' };
    const clauses = [];
    const displayClauses = [];
    const {
      searchFilter, searchColumns, spatialFilter,
      numericFilters, numericIncludeNulls,
      dateFilter, includeMissingDates, categoricalFilters, headers, enrichedColumns
    } = data;

    const formatCol = (c) => {
      if (enrichedColumns?.includes(c) || true ) return `"${c}"`;
      return `main."${c}"`;
    };

    // Applied Backend Search
    if (appliedSearch && appliedSearch.term && appliedSearch.column !== 'stat co-occurrence') {
        const term = appliedSearch.term.replace(/'/g, "''");
        const isContains = appliedSearch.matchType === 'contains';
        
        const formatCol = (c) => {
            if (enrichedColumns?.includes(c) || true ) return `"${c}"`;
            return `main."${c}"`;
        };

        if (!isContains && appliedSearch.column && appliedSearch.column !== 'Search' && headers.includes(appliedSearch.column)) {
            const clause = `(${formatCol(appliedSearch.column)} = '${term}')`;
            clauses.push(clause);
            displayClauses.push(clause);
        } else {
            // FIX: Use the columns that were actually searched, fallback to current tab columns if none exist
            const cols = (appliedSearch.searchCols && appliedSearch.searchCols.length > 0) 
                ? appliedSearch.searchCols 
                : ((searchColumns && searchColumns.length > 0) ? searchColumns : headers.filter(h => !/geometry/i.test(h)));
                
            if (cols.length > 0) {
                if (isContains) {
                    clauses.push(`(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) ILIKE '%${term}%'`).join(' OR ')})`);
                    const colsDisplay = cols.length > 3 ? `${cols.slice(0, 3).join(', ')}, ...` : cols.join(', ');
                    displayClauses.push(`(SEARCH IN (${colsDisplay}) ILIKE '%${term}%')`);
                } else {
                    clauses.push(`(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) = '${term}'`).join(' OR ')})`);
                    const colsDisplay = cols.length > 3 ? `${cols.slice(0, 3).join(', ')}, ...` : cols.join(', ');
                    displayClauses.push(`(SEARCH IN (${colsDisplay}) = '${term}')`);
                }
            }
        }
    }

    // Local Panel Search
    if (searchFilter && (!appliedSearch || searchFilter.toLowerCase() !== appliedSearch.term.toLowerCase())) {
      const term = searchFilter.replace(/'/g, "''");
      const cols = (searchColumns && searchColumns.length > 0) ? searchColumns : headers.filter(h => !/geometry/i.test(h));
      if (cols.length > 0) {
          clauses.push(`(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) ILIKE '%${term}%'`).join(' OR ')})`);
          const colsDisplay = cols.length > 3 ? `${cols.slice(0, 3).join(', ')}, ...` : cols.join(', ');
          displayClauses.push(`(SEARCH IN (${colsDisplay}) ILIKE '%${term}%')`);
      }
    }

    // Spatial
    if (spatialFilter) {
      const latCol = headers.find(h => /latitude?/i.test(h)) || 'lat';
      const lonCol = headers.find(h => /longitude?/i.test(h)) || 'lon';
      const clause = `(${formatCol(lonCol)} >= ${spatialFilter[0].toFixed(6)} AND ${formatCol(latCol)} >= ${spatialFilter[1].toFixed(6)} AND ${formatCol(lonCol)} <= ${spatialFilter[2].toFixed(6)} AND ${formatCol(latCol)} <= ${spatialFilter[3].toFixed(6)})`;
      clauses.push(clause);
      displayClauses.push(clause);
    }

    // Temporal
    const dateCol = headers.find(h => /parsed_collection_date_start/i.test(h));
    if (dateCol) {
      const dateClauses = [];
      if (dateFilter) {
        const minDate = new Date(dateFilter[0]).toISOString().split('T')[0];
        const maxDate = new Date(dateFilter[1]).toISOString().split('T')[0];
        dateClauses.push(`(${formatCol(dateCol)} >= '${minDate}' AND ${formatCol(dateCol)} <= '${maxDate}')`);
      }
      if (includeMissingDates === false) {
        const clause = dateFilter ? `(${dateClauses[0]} AND ${formatCol(dateCol)} IS NOT NULL)` : `(${formatCol(dateCol)} IS NOT NULL)`;
        clauses.push(clause);
        displayClauses.push(clause);
      } else if (dateFilter) {
         const clause = `(${dateClauses[0]} OR ${formatCol(dateCol)} IS NULL)`;
         clauses.push(clause);
         displayClauses.push(clause);
      }
    }

    // Numeric
    const numCols = new Set([...Object.keys(numericFilters || {}), ...Object.keys(numericIncludeNulls || {})]);
    numCols.forEach(col => {
      const range = numericFilters?.[col];
      const includeNulls = numericIncludeNulls?.[col] ?? !range;
      let colClause = [];
      
      if (range) {
        const isDate = /date/i.test(col);
        if (isDate && !isNaN(new Date(range[0]).getTime())) {
          const minDate = new Date(range[0]).toISOString().split('T')[0];
          const maxDate = new Date(range[1]).toISOString().split('T')[0];
          colClause.push(`(${formatCol(col)} >= '${minDate}' AND ${formatCol(col)} <= '${maxDate}')`);
        } else {
          colClause.push(`(${formatCol(col)} >= ${range[0]} AND ${formatCol(col)} <= ${range[1]})`);
        }
      }

      if (includeNulls && range) colClause.push(`(${formatCol(col)} IS NULL OR CAST(${formatCol(col)} AS VARCHAR) = '')`);
      else if (!includeNulls && !range) clauses.push(`(${formatCol(col)} IS NOT NULL AND CAST(${formatCol(col)} AS VARCHAR) != '')`);
      if (colClause.length > 0) {
          const clause = `(${colClause.join(' OR ')})`;
          clauses.push(clause);
          displayClauses.push(clause);
      }
    });

    // Categorical
    if (categoricalFilters) {
      Object.entries(categoricalFilters).forEach(([col, val]) => {
        if (val === 'null') {
            const clause = `(${formatCol(col)} IS NULL OR CAST(${formatCol(col)} AS VARCHAR) = '')`;
            clauses.push(clause);
            displayClauses.push(clause);
        } else {
            const clause = `(${formatCol(col)} = '${String(val).replace(/'/g, "''")}')`;
            clauses.push(clause);
            displayClauses.push(clause);
        }
      });
    }
    if (constraints && constraints.length > 0) {
      const constraintClauses = constraints.map(c => `(${c})`);
      clauses.push(...constraintClauses);
      displayClauses.push(...constraintClauses);
    }

    return {
        sqlQuery: clauses.join('\n  AND '),
        displaySqlQuery: displayClauses.join('\n  AND ')
    };
  }, [data, appliedSearch, constraints]);

  const hasAnyFilter = sqlQuery.length > 0;


    useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function fetchCount() {
        setIsComputingCount(true);
        setCompleteCount(null);
        try {
            const url = new URL('/count', window.location.origin);
            url.searchParams.append("path", searchPath);
            if (sqlQuery) url.searchParams.append("constraints", sqlQuery);
            const res = await fetch(url, { signal: controller.signal });
            if (!res.ok) throw new Error("Query failed");
            const result = await res.json();
            if (result.error) throw new Error(result.error);
            if (isMounted) {
                setCompleteCount(result.count ?? "Error");
                setIsComputingCount(false);
            }
        } catch (e) {
            if (e.name !== 'AbortError' && isMounted) {
                setIsComputingCount(false);
                setCompleteCount("Error");
            }
        }
    }
    const timer = setTimeout(fetchCount, 800);
    return () => { isMounted = false; clearTimeout(timer); controller.abort(); };
  }, [sqlQuery, searchPath, hasAnyFilter, data?.table?.numRows]);
  
  const [showStatModal, setShowStatModal] = useState(false);
  const [isStatFetching, setIsStatFetching] = useState(false);
  
  const SHOW_ALL_ON_EMPTY = true;


  // Helper to get mapped columns for a tab
  const getMappedColumns = (tabLabel, availableHeaders) => {
    // Only return columns that actually exist in the data headers
    const mapping = TAB_CONFIG[tabLabel.toLowerCase()] ||[tabLabel];
    return mapping.filter(col => availableHeaders.includes(col));
  };

  const activeSearchColumns = useMemo(() => data?.searchColumns || [], [data?.searchColumns]);
  const nsamples = data?.searchIndices ? data.searchIndices.length : (data?.table?.numRows || 0);
  const hasGeomId = data?.headers?.some(h => h.toLowerCase() === 'geom_id');

   // --- MEMOIZED FILTER PANELS ---
  const filterPanels = useMemo(() => {
    if (!panels || !data) return [];
    return panels.filter(p => !['map', 'table', 'lifemap', 'sample'].includes(p.type) && panelData[p.i]?.filename === data.filename);
  }, [panels, panelData, data]);

  // Remote Backend Suggestions Logic
    useEffect(() => {
    const query = localSearch.trim();
    if (!SHOW_ALL_ON_EMPTY && query.length === 0) {
      setSuggestions([]);
      return;
    }

    let isCancelled = false;
    const handler = setTimeout(async () => {
      try {
        const url = new URL("/suggestions", window.location.origin);

        if (searchPath) url.searchParams.append("path", searchPath);
        if (query) url.searchParams.append("q", query);
        url.searchParams.append("columns", activeSearchColumns.join(','));
        
        const res = await fetch(url);
        if (!res.ok) throw new Error("Fetch failed");
        
        const resData = await res.json();
        if (!isCancelled) {
          // Deduplicate suggestions by name and count
          const seenEntries = new Map();

          resData.forEach(d => {
            const lowerName = d.name.toLowerCase();
            // Composite key: name + count
            const key = `${lowerName}_${d.count}`;
            const existing = seenEntries.get(key);
            
            if (!existing) {
              seenEntries.set(key, d);
            } else {
              // If the exact same count exists, prioritize specific columns over generic "tax_name"
              if (existing.column.toLowerCase().includes('tax_name') && !d.column.toLowerCase().includes('tax_name')) {
                seenEntries.set(key, d);
              }
            }
          });

          // Convert back to the expected array format while preserving order
          setSuggestions(Array.from(seenEntries.values()).map(d => [d.name, d.column, d.count]));
        }
      } catch(e) {
        console.error("Suggestions error", e);
      }
    }, 600); // 600ms debounce
    
    return () => {
      clearTimeout(handler);
      isCancelled = true;
    };
  },[localSearch, activeSearchColumns, searchPath]);

  // Remote Backend Contains Count Logic
  useEffect(() => {
    const query = localSearch.trim();
    if (query.length === 0) {
      setContainsCount(null);
      return;
    }

    let isCancelled = false;
    const handler = setTimeout(async () => {
      setIsFetchingContainsCount(true);
      try {
        const term = query.replace(/'/g, "''");
        const formatCol = (c) => (data?.enrichedColumns?.includes(c) || true) ? `"${c}"` : `main."${c}"`;
        const cols = (activeSearchColumns && activeSearchColumns.length > 0) 
          ? activeSearchColumns 
          : (data?.headers || []).filter(h => !/geometry/i.test(h));
          
        if (cols.length === 0) return;

        // Build the broad ILIKE constraint
        const containsConstraint = `(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) ILIKE '%${term}%'`).join(' OR ')})`;

        const url = new URL("/count", window.location.origin);
        if (searchPath) url.searchParams.append("path", searchPath);
        url.searchParams.append("constraints", containsConstraint);

        const res = await fetch(url);
        if (!res.ok) throw new Error("Fetch failed");
        
        const resData = await res.json();
        if (!isCancelled) {
          setContainsCount(resData.count);
        }
      } catch(e) {
        console.error("Contains count error", e);
        if (!isCancelled) setContainsCount("Error");
      } finally {
        if (!isCancelled) setIsFetchingContainsCount(false);
      }
    }, 600); // Shares the same 600ms debounce as suggestions
    
    return () => {
      clearTimeout(handler);
      isCancelled = true;
    };
  }, [localSearch, activeSearchColumns, searchPath, data?.headers, data?.enrichedColumns]);

  const handleTriggerSearch = async (searchValue = localSearch, searchCols = activeSearchColumns, limitOverride = null, matchType = 'contains') => {
    let effectiveLimit = limitOverride;
    let matchedCol = searchCols && searchCols.length > 0 ? searchCols[0] : 'Search';
    
    // Fallbacks if logic is triggered ambigously
    if (matchType === 'exact' && !effectiveLimit) {
       const match = suggestions.find(s => s[0].toLowerCase() === searchValue.toLowerCase().trim());
       if (match) {
         effectiveLimit = match[2];
         matchedCol = match[1];
       } else {
         matchType = 'contains';
         matchedCol = 'Search';
       }
    } else if (matchType === 'contains') {
       matchedCol = 'Search';
    }

    if (searchValue.trim()) {
      // FIX: Store searchCols so the SQL query remains stable when tabs are switched
      setAppliedSearch({ term: searchValue.trim(), column: matchedCol, matchType, searchCols });
      setLocalSearch('');

      if (activeSearchColumns && activeSearchColumns.length > 0) {
        activeSearchColumns.forEach(col => {
          updateCategoricalFilter(data.filename, col, null);
          updateNumericFilter(data.filename, col, null);
          updateNumericNulls(data.filename, col, true);
        });
      }
    } else {
      setAppliedSearch(null);
    }
    
    setShowSuggestions(false);

    // Build the SQL locally so it is synchronously ready to send to the backend
    let extraConstraint = null;
    if (searchValue.trim()) {
        const term = searchValue.trim().replace(/'/g, "''");
        const isContains = matchType === 'contains';
        const formatCol = (c) => (data.enrichedColumns?.includes(c) || true) ? `"${c}"` : `main."${c}"`;
        
        if (!isContains && matchedCol !== 'Search' && data.headers.includes(matchedCol)) {
            extraConstraint = `(${formatCol(matchedCol)} = '${term}')`;
        } else {
            const cols = (searchCols && searchCols.length > 0) ? searchCols : data.headers.filter(h => !/geometry/i.test(h));
            if (cols.length > 0) {
                if (isContains) {
                    extraConstraint = `(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) ILIKE '%${term}%'`).join(' OR ')})`;
                } else {
                    extraConstraint = `(${cols.map(c => `CAST(${formatCol(c)} AS VARCHAR) = '${term}'`).join(' OR ')})`;
                }
            }
        }
    }

    startTransition(() => {
      onRemoteSearch(searchValue.trim(), searchCols, effectiveLimit, extraConstraint);
    });
  };

  const selectSuggestion = (name, column, count) => {
    handleTriggerSearch(name, [column], count, 'exact');
  };

  const handleStatSearch = async (pills) => {
        setIsStatFetching(true);
        const termStr = pills.map(p => `${p.name} (>=${p.minCount})`).join(' AND ');
        setAppliedSearch({ term: termStr, column: 'stat co-occurrence' });

        try {
          const conditions = pills.map(p => `(name = '${p.name.replace(/'/g, "''")}' AND total_count >= ${p.minCount})`);
          const havingCount = pills.length;
          
          // Delegate the heavy lifting to DuckDB so limits don't truncate matching pairs
          const runSubq = `SELECT run FROM read_parquet('${STAT_TABLE_PATH}') WHERE ${conditions.join(' OR ')} GROUP BY run HAVING COUNT(DISTINCT name) = ${havingCount}`;
          const subq = `SELECT biosample FROM read_parquet('${STAT_TABLE_PATH}') WHERE run IN (${runSubq})`;
          const extraConstraint = `primary_id IN (${subq}) and source = 'NCBI BioSample'`;

          // Fetch the stat table strictly for the qualifying runs to build an aggregate countMap
          const limit = 500000;
          const statUrl = new URL('/query', window.location.origin);
          statUrl.searchParams.append("path", STAT_TABLE_PATH);
          statUrl.searchParams.append("constraints", `run IN (${runSubq}) AND (${conditions.join(' OR ')})`);
          statUrl.searchParams.append("limit", limit);

          const res = await fetch(statUrl);
          if (!res.ok) throw new Error("Stat query failed");

          const statTable = await tableFromIPC(res.body);
          console.log(`[STAT Debug] Fetched ${statTable.numRows} rows from stat table for count matching.`);
          if (statTable.numRows >= limit) {
             console.warn(`[STAT Debug] WARNING: Hit the fetch limit of ${limit}. Results might be truncated!`);
          }

          const runMap = new Map();
          const runCountMap = new Map();

          const runCol = statTable.getChild("run");
          const nameCol = statTable.getChild("name");
          const countCol = statTable.getChild("total_count");

          if (runCol && nameCol && countCol) {
            for(let i = 0; i < statTable.numRows; i++) {
              const run = runCol.get(i);
              const name = nameCol.get(i);
              const rawCount = countCol.get(i);

              if (run !== null && run !== undefined) {
                const runStr = String(run).trim();
                const nameStr = String(name).trim();
                const count = typeof rawCount === 'bigint' ? Number(rawCount) : rawCount;

                if (!runMap.has(runStr)) {
                  runMap.set(runStr, new Set());
                  runCountMap.set(runStr, {});
                }
                runMap.get(runStr).add(nameStr);
                runCountMap.get(runStr)[nameStr] = (runCountMap.get(runStr)[nameStr] || 0) + count;
              }
            }
          }

          const finalCountMap = new Map();
          for (const [runStr, namesSet] of runMap.entries()) {
            if (namesSet.size === havingCount) {
              finalCountMap.set(runStr, runCountMap.get(runStr));
            } else {
              console.warn(`[STAT Debug] Run ${runStr} missing viruses. Expected ${havingCount}, got ${namesSet.size}.`);
            }
          }
          console.log(`[STAT Debug] Generated finalCountMap with ${finalCountMap.size} valid runs.`);

          startTransition(() => {
            onRemoteSearch('', activeSearchColumns, null, extraConstraint, finalCountMap);
          });

        } catch (e) {
          console.error("Stat search error", e);
        } finally {
          setIsStatFetching(false);
        }
      };


    const clearSearch = () => {
    setLocalSearch('');
    setAppliedSearch(null);
    startTransition(() => {
      onRemoteSearch('', activeSearchColumns, null);
    });
  };

  const smallScreen = typeof window !== 'undefined' && window.innerWidth < 640 
  const logosize = smallScreen ? 20 : 28;

  if (!data) return <div className="flex items-center justify-center h-full"><Title size={logosize+10} isLoading={true} /></div>;

  const { filename, headers =[] } = data;
  // Filter tabs to only show those where at least one mapped column exists in data
  const availableTabs = Object.keys(TAB_CONFIG).filter(tabLabel => 
    TAB_CONFIG[tabLabel].some(colName => headers.includes(colName))
  );

  const mapPanelId = panels.find(p => p.type === 'map' && panelData[p.i]?.filename === data.filename)?.i || 'user-map';
  const mapData = mapPanelId !== 'user-map' ? { ...panelData[mapPanelId], searchIndices: searchIndicesMap[data.filename] } : data;

  const lifemapPanelId = panels.find(p => p.type === 'lifemap' && panelData[p.i]?.filename === data.filename)?.i || 'user-lifemap';
  const lifemapData = lifemapPanelId !== 'user-lifemap' ? { ...panelData[lifemapPanelId], searchIndices: searchIndicesMap[data.filename] } : data;

  const tablePanelId = panels.find(p => p.type === 'table' && panelData[p.i]?.filename === data.filename)?.i || 'user-table';
  const tableData = tablePanelId !== 'user-table' ? { ...panelData[tablePanelId], searchIndices: searchIndicesMap[data.filename] } : data;

  return (
    <div className="flex flex-col h-full w-full pl-3 pt-2 pb-4 pr-3 gap-3 overflow-y-auto min-h-0">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 px-2 shrink-0">
         <div className={`flex items-center gap-4 flex-1 min-w-0 ${!smallScreen ? '' : 'mt-10'}`}>
          <div className="flex-1 min-w-0">  
            <div className="flex flex-col gap-0 min-w-0">
              <div className={`flex items-center gap-2 min-w-0 ${smallScreen ? '-mb-1' : '-mb-6'}`}>      
                <Title size={logosize} isLoading={isPending || isFetchingData || isStatFetching} />
                <span className='-ml-5 -mr-1'><Logoglyph /></span>

                {/* Geom button */}
                {hasGeomId && (
                   <GeomEnrichButton compact onClick={onOpenGeomModal} />
                )}
                <StatMenuButton onClick={() => setShowStatModal(true)} />
                <FilterToggleButton onClick={() => setShowFilters(!showFilters)}>
                
                </FilterToggleButton>
                <div className='ml-2 flex-1 min-w-0'>
                <HeaderFiltersPanel 
                  data={data} 
                  nsamples={nsamples}
                  isFetchingData={isFetchingData}
                  isStatFetching={isStatFetching}
                  sqlQuery={sqlQuery}                
                  completeCount={completeCount}       
                  isComputing={isComputingCount}
                  searchPath={searchPath}
                  appliedSearch={appliedSearch}
                  constraints={constraints}
                  onLoadAll={(sql, count) => {
                    if (count > 200000) {
                      setPendingLoadArgs({ sql, count });
                      setShowLoadWarning(true);
                    } else {
                      onRemoteSearch(data.searchFilter, data.searchColumns, count, sql, null, true);
                    }
                  }}
                  onClearAppliedSearch={clearSearch}
                  onClearLocalSearch={(fn) => updateSearchFilter(fn, '')}
                  onClearSpatial={(fn) => onSpatialFilterChange(fn, null)}
                  onClearDate={(fn) => updatePanelFilter(fn, null, data.includeMissingDates)}
                  onClearMissingDates={(fn) => updatePanelFilter(fn, data.dateFilter, true)}
                  onClearNumeric={(fn, col) => updateNumericFilter(fn, col, null)}
                  onClearNumericNulls={(fn, col) => updateNumericNulls(fn, col, true)}
                  onClearCategorical={(fn, col) => updateCategoricalFilter(fn, col, null)}
                  onClearConstraint={(idx) => {
                    const newConstraints = constraints.filter((_, i) => i !== idx);
                    setConstraints(newConstraints);
                    onRemoteSearch(data.searchFilter, data.searchColumns, null, null, null, false, newConstraints);
                  }}
                />
                </div>
                
              </div>
              <span className='text-2xl text-white font-bold ml-6 leading-none'>UNDER DEVELOPMENT</span>
            </div>      
          </div>
        </div>
        <div className="w-full md:w-[400px] lg:w-[450px] xl:w-[500px] shrink-0 relative mt-2 md:mt-4">
          
          <div className="flex justify-between items-end mx-2">
            
    <div className="flex gap-1">
      
      {availableTabs.map(tab => {
        const mappedCols = getMappedColumns(tab, headers);
        const isActive = activeSearchColumns.length > 0 && activeSearchColumns.every(c => mappedCols.includes(c));

        return (
          <button 
            key={tab}
            onClick={() => { 
              setLocalSearch(''); 
              setSuggestions([]); 
              onSearchColChange(filename, mappedCols); 
            }}
            className={`px-3 py-1.5 rounded-t-lg text-xs font-bold border-t border-x transition-colors ${
               isActive ? 'bg-gradient-to-r from-blue-100 to-blue-200 border-gray-800' : 'bg-gray-200 border-transparent text-gray-600 hover:bg-gray-300'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        );
      })}
    </div>
    
  </div>

  <div className="relative">
            <input 
              ref={searchInputRef}
              className={`w-full p-3 mx-2 text-md border-2 border-gray-800 rounded-b-xl rounded-tr-xl shadow-sm focus:outline-none z-20 relative bg-white ${(isPending || isFetchingData || isStatFetching) ? 'opacity-70' : ''}`}
              placeholder={`Search in ${activeSearchColumns.join(', ') || 'all columns'}...`}
              value={localSearch}
              onFocus={() => setShowSuggestions(true)}
              onChange={(e) => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTriggerSearch(localSearch, activeSearchColumns, null, 'contains');
                if (e.key === 'Escape') setShowSuggestions(false);
                if (e.key === 'ArrowDown' && showSuggestions && (suggestions.length > 0 || localSearch.trim().length > 0)) {
                  e.preventDefault();
                  const targetRef = suggestionRefs.current[-1] || suggestionRefs.current[0];
                  targetRef?.focus();
                }
              }}
            />
            {/* Suggestions logic */}
            {showSuggestions && (suggestions.length > 0 || localSearch.trim().length > 0) && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSuggestions(false)} />
                <div className="absolute top-full left-2 right-[-6px] mt-1 bg-white border-2 border-gray-800 rounded-xl shadow-xl z-30 overflow-hidden flex flex-col">
                    
                    {/* Sticky 'Contains' Action Row */}
                    {localSearch.trim().length > 0 && (
                      <button 
                        ref={el => suggestionRefs.current[-1] = el}
                        className="w-full flex justify-between items-center px-4 py-2.5 bg-blue-80/80 hover:bg-blue-100 focus:bg-blue-100 text-sm border-b border-gray-200 font-bold text-blue-900 shrink-0 z-10 transition-colors focus:outline-none"
                        onClick={() => handleTriggerSearch(localSearch, activeSearchColumns, containsCount !== "Error" ? containsCount : null, 'contains')}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            suggestionRefs.current[0]?.focus();
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            searchInputRef.current?.focus();
                          }
                        }}
                      >
                        <div className="flex items-center gap-3 truncate">
                          <span>Search all containing "{localSearch.trim()}"</span>
                        </div>
                        {isFetchingContainsCount ? (
                            <span className="animate-spin h-3.5 w-3.5 border-2 border-blue-900 border-t-transparent rounded-full shrink-0"/>
                          ) : containsCount !== null && containsCount !== "Error" ? (
                            <span className="text-gray-500 text-[11px] px-3 py-0.5 rounded-md font-mono shrink-0">
                              {containsCount.toLocaleString()}
                            </span>
                          ) : null}
                      </button>
                    )}

                    {suggestions.length > 0 && (
                      <div className="overflow-y-auto max-h-96 custom-scrollbar overscroll-contain bg-white">
                      {suggestions.map(([name, column, count], i) => {
                        let cleanCol = column.replace(/^(HOST_|VIRUS_|ENVIRONMENT_)/i, '').replace(/_/g, ' ').toLowerCase();
                        if (cleanCol) cleanCol = cleanCol.charAt(0).toUpperCase() + cleanCol.slice(1);
                        return (
                          <button 
                            key={i} 
                            ref={el => suggestionRefs.current[i] = el}
                            className="w-full flex justify-between items-center px-4 py-2 hover:bg-blue-100 focus:bg-blue-100 focus:outline-none text-sm border-b last:border-none border-gray-100 gap-4 bg-white" 
                            onClick={() => selectSuggestion(name, column, count)}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                suggestionRefs.current[i + 1]?.focus();
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                if (i === 0) {
                                  if (localSearch.trim().length > 0) suggestionRefs.current[-1]?.focus();
                                  else searchInputRef.current?.focus();
                                }
                                else suggestionRefs.current[i - 1]?.focus();
                              }
                            }}
                          >
                            <div className="text-left py-0.5 truncate flex-grow text-gray-800 font-medium"><span>{name}</span></div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="border border-blue-800 text-blue-800 rounded-lg px-3 py-1 text-xs font-bold font-mono tracking-tight bg-white min-w-[80px] text-center shadow-sm">
                                {cleanCol}
                              </span>
                              <span className="shrink-0 text-gray-400 font-mono text-xs pt-1 w-16 text-right">{count.toLocaleString()}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Visualisation (+eventually instructions) area */}
      <div className={`flex ${!smallScreen ? 'flex-row' : 'flex-col'} w-full h-full flex-1 min-h-[88vh] gap-2`}>  

        {showFilters && (
          <div className={`flex flex-col ${!smallScreen ? 'w-1/4 min-w-[250px] max-w-[350px]' : 'w-full'} h-full min-h-[89.22vh] gap-2 shrink-0 overflow-y-auto no-scrollbar`}>
            {filterPanels.map(p => {
              const pData = { ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] };
              const pColorConfig = colorConfigsMap[`${pData.filename}|${pData.colorBy || pData.headers?.[0]}`];
              
              return (
                <div key={p.i} className="flex-none min-h-[260px] h-[20vh]">
                  <PanelLoader id={p.i} type={p.type} data={pData} onRemove={removePanel}>
                    {p.type === 'plot' && (
                      <TimePlotPanel id={p.i} data={pData} basic={true} onRemove={removePanel} onFilterChange={updatePanelFilter} onConfigChange={onConfigChange} />
                    )}
                    {p.type === 'stats' && (
                      <StatsPanel id={p.i} data={pData} basic={true} onRemove={removePanel} colorConfig={pColorConfig} onColorByChange={onColorByChange} onCategoryClick={updateCategoricalFilter} onDuplicate={duplicatePanel} onNullToggle={updateNumericNulls} onConfigChange={onConfigChange} />
                    )}
                    {p.type === 'timestats' && (
                      <TimeStatsPanel id={p.i} data={pData} basic={true} onRemove={removePanel} colorConfig={pColorConfig} onColorByChange={onColorByChange} onNullToggle={updateNumericNulls} onConfigChange={onConfigChange} />
                    )}
                    {p.type === 'numericPlot' && (
                      <NumericPlotPanel id={p.i} data={pData} basic={true} onRemove={removePanel} onDuplicate={duplicatePanel} onFilterChange={updateNumericFilter} onColorByChange={onColorByChange}  onNullToggle={updateNumericNulls} onConfigChange={onConfigChange} />
                    )}
                    {p.type === 'scatterplot' && (
                      <ScatterPlotPanel id={p.i} data={pData} colorConfig={pColorConfig} basic={true} onRemove={removePanel} onColorByChange={onColorByChange} onConfigChange={onConfigChange} />
                    )}
                    {p.type === 'boxplot' && (
                      <BoxPlotPanel id={p.i} data={pData} colorConfig={pColorConfig} basic={true} onRemove={removePanel} onColorByChange={onColorByChange} onConfigChange={onConfigChange} />
                    )}
                  </PanelLoader>
                </div>
              );
            })}
            
           {/* ACTIVE FILTERS & SQL PANEL */}
            { data && (
              <>
              <div className="flex-none h-auto min-h-[100px]">
                <ActiveFiltersPanel 
                  data={data} 
                  sqlQuery={sqlQuery}                 
                  displaySqlQuery={displaySqlQuery}
                  completeCount={completeCount}      
                  isComputing={isComputingCount}      
                  searchPath={searchPath}
                  appliedSearch={appliedSearch}
                  constraints={constraints}
                  onLoadAll={(sql, count) => {
                    // Send exact count and a flag to bypass the max nRows safety cap
                    onRemoteSearch(data.searchFilter, data.searchColumns, count, sql, null, true);
                  }}
                  onClearAppliedSearch={clearSearch}
                  onClearLocalSearch={(fn) => updateSearchFilter(fn, '')}
                  onClearSpatial={(fn) => onSpatialFilterChange(fn, null)}
                  onClearDate={(fn) => updatePanelFilter(fn, null, data.includeMissingDates)}
                  onClearMissingDates={(fn) => updatePanelFilter(fn, data.dateFilter, true)}
                  onClearNumeric={(fn, col) => updateNumericFilter(fn, col, null)}
                  onClearNumericNulls={(fn, col) => updateNumericNulls(fn, col, true)}
                  onClearCategorical={(fn, col) => updateCategoricalFilter(fn, col, null)}
                  onClearConstraint={(idx) => {
                    const newConstraints = constraints.filter((_, i) => i !== idx);
                    setConstraints(newConstraints);
                    onRemoteSearch(data.searchFilter, data.searchColumns, null, null, null, false, newConstraints);
                  }}
                  onPasteQuery={onPasteQuery}
                />
              </div>
              {/* ADD PANELS SHORTCUT */}
                <div className="flex-none h-auto mt-2 mb-2">
                  <PanelContainer id="add-panels-basic">
                    <div className="panel-header bg-white border-b border-gray-200">
                      <span className="truncate ml-2 text-gray-800 font-bold text-xs">Add Panel:</span>
                    </div>
                    <div className="flex-grow flex flex-wrap p-5 gap-6 bg-gray-50 items-center justify-center">
                      <div className="transform scale-150 "><TimePlotButton onClick={() => onOpenPlot(data)} title="Add Timeline Filter" /></div>
                      <div className="transform scale-150 "><StatsButton onClick={() => onOpenStats(data)} title="Add Categorical Stats Filter" /></div>
                      <div className="transform scale-150 "><PlotButton onClick={() => onOpenNumericPlot(data)} title="Add Numeric Distribution Filter" /></div>
                      <div className="transform scale-150 "><TimeStatsButton onClick={() => onOpenTimeStats(data)} title="Add Time-Series Categorical Distribution" /></div>
                      <div className="transform scale-150 "><ScatterButton onClick={() => onOpenScatterPlot(data)} title="Add Scatter Plot" /></div>
                      <div className="transform scale-150 "><BoxPlotButton onClick={() => onOpenBoxPlot(data)} title="Add Box/Violin Plot" /></div>
                    </div>
                  </PanelContainer>
                </div>
                </>
              
            )}
          </div>
        )}
       
        {showInstructions && <ProjectCard mode="long"/>}

        {(selectedSample) && (
          <div className={`flex flex-col ${!smallScreen ? 'w-1/3 min-w-[300px]' : 'w-full'} h-full min-h-[89.22vh] gap-2 shrink-0`}>
            <div className="flex flex-col flex-1 min-h-0 bg-white border-2 border-gray-800 rounded-2xl shadow-lg relative animate-in slide-in-from-right-4 duration-1200 overflow-hidden">
              <PanelLoader id="basic-sample-view" type="sample" data={{isLoading: false}} basic={true}>
                <SamplePanel 
                  id="basic-sample-view" 
                  data={{ rowData: selectedSample, filename: data.filename }} 
                  onRemove={() => setSelectedSample(null)}
                  basic={true} 
                />
              </PanelLoader>
            </div>
          </div>
        )}
        <div className="flex flex-col flex-1 h-full min-h-0 gap-2 min-w-0">
          <div className={`flex ${!smallScreen ? 'flex-row' : 'flex-col'} gap-2 h-[70%] shrink-0 min-h-[360px]`}>
            <div style={{ flex: !smallScreen ? 1.675 : 1 }} className="flex flex-col bg-gray-800 border-2 border-gray-800 rounded-2xl overflow-hidden shadow-sm">
              <PanelLoader id={mapPanelId} type="map" data={mapData} basic={true}>
              <MapPanel 
                id={mapPanelId} 
                data={mapData} 
                basic={true} 
                colorConfig={mapData ? colorConfigsMap[`${mapData.filename}|${mapData.colorBy || mapData.headers?.[0]}`] : null}
                onColorByChange={onColorByChange} 
                onSpatialFilterChange={onSpatialFilterChange} 
                onBaseLayerChange={onBaseLayerChange}
                smallScreen={smallScreen} 
                onRowClick={onRowClick}
                selectedSampleIndex={selectedSampleIndex}  
                onConfigChange={onConfigChange}
              />
            </PanelLoader>
            </div>

            <div className="flex-1 flex flex-col bg-gray-800 border-2 border-gray-800 rounded-2xl overflow-hidden shadow-sm">
              <PanelLoader id={lifemapPanelId} type="lifemap" data={lifemapData} basic={true}>
                <LifeMapPanel id={lifemapPanelId} data={lifemapData} basic={true} selectedSample={selectedSample} />
              </PanelLoader>
            </div>
          </div>

          <div className="flex flex-col flex-1 min-h-[27vh] max-h-[40vh] bg-white border-2 border-gray-800 rounded-2xl overflow-hidden shadow-sm">
            <PanelLoader id={tablePanelId} type="table" data={tableData} basic={true}>
              <TablePanel 
                id={tablePanelId} 
                data={tableData} 
                basic={true} 
                onSearchColumnsChange={onSearchColChange} 
                onRowClick={onRowClick} 
              />
            </PanelLoader>
          </div>
        </div>
      </div>
      {/* Footer Section: Only visible at the bottom of the scroll */}
      <div className="mt-8 shrink-0">
        {activeAttributions && activeAttributions.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-600 flex flex-wrap gap-x-4 justify-end mb-2">
            Map attributions:
            {activeAttributions.map((attr, idx) => (
              <span key={idx} dangerouslySetInnerHTML={{ __html: attr }} />
            ))}
          </div>
        )}
        <Footer />
      </div>
      <StatSearchModal 
        isOpen={showStatModal} 
        onClose={() => setShowStatModal(false)} 
        onSearch={handleStatSearch} 
      />

       {showLoadWarning && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 w-[450px] max-w-full flex flex-col relative animate-in fade-in zoom-in duration-200">
            <button 
              onClick={() => {
                setShowLoadWarning(false);
                setPendingLoadArgs(null);
              }} 
              className="absolute top-3 right-3 text-gray-500 hover:bg-red-200 p-2 rounded-full transition-colors"
              title="Cancel"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold mb-2 text-gray-900 pr-6">Warning: Large Dataset</h2>
            <p className="text-sm text-gray-600 mb-6 font-medium">
              Loading more than 200,000 samples may crash your browser, please refine your search with additional filters.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLoadWarning(false);
                  // Load subset of exactly 100,000
                  onRemoteSearch(data.searchFilter, data.searchColumns, 100000, pendingLoadArgs.sql, null, true);
                }}
                className="px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
              >
                Load a subset
              </button>
              <button
                onClick={() => {
                  setShowLoadWarning(false);
                  // Load the original full count
                  onRemoteSearch(data.searchFilter, data.searchColumns, pendingLoadArgs.count, pendingLoadArgs.sql, null, true);
                }}
                className="px-4 py-2 rounded-xl bg-red-100 text-red-700 border-2 border-red-700 font-bold hover:bg-red-200 transition-colors shadow-sm"
              >
                Load Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

function StatSearchModal({ isOpen, onClose, onSearch }) {
  const [localSearch, setLocalSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPills, setSelectedPills] = useState([]);

  useEffect(() => {
    const query = localSearch.trim();

    let isCancelled = false;
    const handler = setTimeout(async () => {
      try {
        const url = new URL("/suggestions", window.location.origin);
        url.searchParams.append("path", STAT_TABLE_PATH);
        url.searchParams.append("q", query);
        url.searchParams.append("columns", "name");

        const res = await fetch(url);
        if (!res.ok) throw new Error("Fetch failed");

        const resData = await res.json();
        if (!isCancelled) {
          const seenEntries = new Map();
          resData.forEach(d => {
            const lowerName = d.name.toLowerCase();
            const key = `${lowerName}_${d.count}`;
            if (!seenEntries.has(key)) seenEntries.set(key, d);
          });
          setSuggestions(Array.from(seenEntries.values()).map(d => [d.name, d.column, d.count]));
        }
      } catch(e) {
        console.error("Suggestions error", e);
      }
    }, 600);

    return () => { clearTimeout(handler); isCancelled = true; };
  }, [localSearch]);

  const handleAddPill = (name) => {
    if (!selectedPills.some(p => p.name === name)) {
      setSelectedPills([...selectedPills, { name, minCount: 1 }]);
    }
    setLocalSearch('');
    setShowSuggestions(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-[600px] min-h-[400px] max-w-full flex flex-col relative animate-in fade-in zoom-in duration-200 overflow-visible">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:bg-red-200 p-2 rounded-full transition-colors">
          <XMarkIcon className="w-4 h-4" />
        </button>
        
        <div className="relative z-50 mt-4">
          <input
            className="w-full p-4 border-2 border-gray-400 rounded-2xl shadow-sm focus:outline-none focus:border-gray-800 text-lg transition-colors"
            placeholder="Search for viruses or viral families..."
            value={localSearch}
            onFocus={() => setShowSuggestions(true)}
            onChange={(e) => { setLocalSearch(e.target.value); setShowSuggestions(true); }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSuggestions(false)} />
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-800 rounded-2xl shadow-xl z-50 overflow-hidden">
                <div className="overflow-y-auto max-h-64 custom-scrollbar overscroll-contain bg-white">
                  {suggestions.map(([name, column, count], i) => (
                    <button
                      key={i}
                      className="w-full flex justify-between items-center px-5 py-3 hover:bg-blue-50 text-base border-b border-gray-100 bg-white last:border-0"
                      onClick={() => handleAddPill(name)}
                    >
                      <span className="text-gray-800 font-medium">{name}</span>
                      <span className="text-gray-400 font-mono text-sm">{count.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-4 mt-8 mb-8 min-h-[100px]">
          {selectedPills.map((pill, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-4 bg-white border-2 border-gray-400 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between border-2 border-gray-400 bg-gray-100 rounded-xl px-4 py-2 shrink-0 max-w-[280px]">
                <span className="text-base font-medium text-gray-700 truncate">{pill.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-base text-gray-600 font-bold">Minimum count:</span>
                <input
                  type="number"
                  min="1"
                  value={pill.minCount}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedPills(prev => prev.map((p, i) => i === idx ? { ...p, minCount: val } : p));
                  }}
                  className="w-24 border-2 border-gray-400 rounded-xl px-3 py-2 text-base text-center focus:outline-none focus:border-gray-800 font-medium"
                />
                <button onClick={() => setSelectedPills(prev => prev.filter((_, i) => i !== idx))} className="absolute right-12 text-gray-500 hover:bg-red-200 p-2 rounded-full transition-colors"><XMarkIcon className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {selectedPills.length === 0 && (
             <div className="text-center text-gray-400 mt-4 text-lg font-medium">Search and select viruses or viral families to add to the query</div>
          )}
        </div>

        <div className="flex justify-end mt-auto">
          <button
            onClick={() => { onSearch(selectedPills); onClose(); }}
            disabled={selectedPills.length === 0}
            className={`px-8 py-3 border-2 rounded-2xl font-bold text-lg transition-all ${selectedPills.length > 0 ? 'bg-white border-gray-800 text-gray-800 hover:bg-blue-200 shadow-md' : 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'}`}
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
}


function GeomEnrichModal({ isOpen, onClose, panelData, setHistory }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [schema, setSchema] = useState([]);
  const [selectedCols, setSelectedCols] = useState(new Set());
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  
  // Hover State
  const [hoveredCol, setHoveredCol] = useState(null);

  // Helper to find description (handles HYBAS levels)
  const getDescription = (col) => {
    if (!col) return "";
    return GEOM_VAR_DESCRIPTIONS[col] || "";
  };

  const validFiles = useMemo(() => {
    const files = new Set();
    Object.values(panelData).forEach(d => {
      if (d?.headers?.some(h => h.toLowerCase() === 'geom_id') && !d.isLoading) {
        files.add(d.filename);
      }
    });
    return Array.from(files);
  }, [panelData, isOpen]);

useEffect(() => {
    if (isOpen) {
      setSelectedFiles(new Set(validFiles));
      setSelectedCols(new Set()); // reset selected columns
      setIsLoading(true);
      const schemaUrl = new URL('/query', window.location.origin);
      schemaUrl.searchParams.append('path', GEOM_TABLE_PATH);
      schemaUrl.searchParams.append('limit', '1');
      fetch(schemaUrl)
        .then(res => res.ok ? res.body : Promise.reject('Failed to fetch geom schema'))
        .then(tableFromIPC)
        .then(table => {
          const colsToExclude = new Set(['_geojson_str', 'geometry', 'lat', 'lon', 'latitude', 'longitude']);
          const colorOrder = ['bg-cyan-100', 'bg-orange-100', 'bg-pink-100', 'bg-green-100', 'bg-purple-100', 'bg-yellow-100'];
          const cols = table.schema.fields.map(f => f.name).filter(n => n.toLowerCase() !== 'geom_id' && !colsToExclude.has(n.toLowerCase()));
          
          // Sort by their category's group/color order, then naturally alphabetically
          cols.sort((a, b) => {
            const colorA = (GEOM_METADATA[a] || {}).color || 'bg-transparent';
            const colorB = (GEOM_METADATA[b] || {}).color || 'bg-transparent';
            const indexA = colorOrder.indexOf(colorA);
            const indexB = colorOrder.indexOf(colorB);
            
            if (indexA === indexB) return a.localeCompare(b, undefined, { numeric: true });
            return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
          });
          
          setSchema(cols);
        })
        .catch(err => {
          console.error(err);
          alert("Failed to load geographic table schema.");
          onClose();
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, validFiles]);

  if (!isOpen) return null;

  const toggleCol = (col) => {
    const next = new Set(selectedCols);
    if (next.has(col)) next.delete(col);
    else next.add(col);
    setSelectedCols(next);
  };

  const toggleFile = (f) => {
    const next = new Set(selectedFiles);
    if (next.has(f)) next.delete(f);
    else next.add(f);
    setSelectedFiles(next);
  };

  const toggleCategory = (colorClass) => {
    const colsInCat = schema.filter(col => {
      const metaColor = (GEOM_METADATA[col] || {}).color || 'bg-transparent';
      return metaColor === colorClass;
    });
    if (colsInCat.length === 0) return;
    
    const allSelected = colsInCat.every(col => selectedCols.has(col));
    const next = new Set(selectedCols);
    colsInCat.forEach(col => {
      if (allSelected) next.delete(col);
      else next.add(col);
    });
    setSelectedCols(next);
  };

  const handleMerge = async () => {
    if (selectedCols.size === 0 || selectedFiles.size === 0) return;
    setIsMerging(true);
    try {
      const mergeUrl = new URL('/query', window.location.origin);
      mergeUrl.searchParams.append('path', GEOM_TABLE_PATH);
      mergeUrl.searchParams.append('limit', '800000');

      const res = await fetch(mergeUrl);
      if (!res.ok) throw new Error("Failed to fetch geom mapping data");
      const tableB = await tableFromIPC(res.body);

      const bJoinKey = tableB.schema.fields.map(f => f.name).find(n => n.toLowerCase() === 'geom_id');
      const bKeyCol = bJoinKey ? tableB.getChild(bJoinKey) : null;
      if (!bKeyCol) throw new Error("Geom lookup table is missing a geom_id column.");
      
      const bLookup = new Map();
      for (let i = 0; i < tableB.numRows; i++) {
        const val = bKeyCol.get(i);
        if (val !== null && val !== undefined) bLookup.set(String(val), i);
      }

      setHistory(h => {
        const nextData = { ...h.present.panelData };
        Object.keys(nextData).forEach(panelId => {
          const pData = nextData[panelId];
          const aJoinKey = pData.headers.find(h => h.toLowerCase() === 'geom_id');
          if (selectedFiles.has(pData.filename) && aJoinKey) {
            const tableA = pData.table;
            const aRows = tableA.numRows;
            const aKeyCol = tableA.getChild(aJoinKey);
            const bMatchIndices = new Int32Array(aRows).fill(-1);
            for (let i = 0; i < aRows; i++) {
              const val = aKeyCol.get(i);
              if (val !== null && val !== undefined) {
                const matchIdx = bLookup.get(String(val));
                if (matchIdx !== undefined) bMatchIndices[i] = matchIdx;
              }
            }
            const mergedColumns = {};
            pData.headers.forEach(header => { mergedColumns[header] = tableA.getChild(header); });
            Array.from(selectedCols).forEach(colName => {
              if (pData.headers.includes(colName)) return;
              const originalCol = tableB.getChild(colName);
              if (!originalCol) return;
              const newArray = new Array(aRows);
              for (let i = 0; i < aRows; i++) {
                const bIdx = bMatchIndices[i];
                newArray[i] = (bIdx !== -1 && originalCol.isValid(bIdx)) ? originalCol.get(bIdx) : null;
              }
              mergedColumns[colName] = newArray;
            });
            const newTable = tableFromArrays(mergedColumns);
            nextData[panelId] = { 
              ...pData, 
              table: newTable, 
              headers: Object.keys(mergedColumns),
              enrichedColumns: Array.from(new Set([...(pData.enrichedColumns || []), ...Array.from(selectedCols)]))
            };
          }
        });
        SearchCache.clear();
        GeoCache.clear();
        ColorCache.clear();
        return { ...h, present: { ...h.present, panelData: nextData } };
      });
      onClose();
    } catch (err) {
      console.error(err);
      alert("Error merging geom data: " + err.message);
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[850px] max-w-full max-h-[99vh] flex flex-col relative animate-in fade-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:bg-red-200 p-2 rounded-full transition-colors">
          <XMarkIcon className="w-6 h-6" />
        </button>
        
        <h2 className="text-xl font-bold mb-1 text-gray-800">Enrich With Geographic Data</h2>
        <p className="text-sm text-gray-500 mb-4">Select additional geographic values to enrich the table(s) based on the `geom_id` lookup key.</p>
        
        {/* Color Legend */}
        <div className="flex flex-wrap gap-4 mb-6 text-[10px] font-bold uppercase tracking-wider p-3 bg-gray-50 border border-gray-200 rounded-xl">
           <div onClick={() => toggleCategory('bg-cyan-100')} className="flex items-center gap-2 cursor-pointer hover:bg-cyan-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-cyan-100 border border-cyan-300 rounded" /> Hydrosphere</div>
           <div onClick={() => toggleCategory('bg-orange-100')} className="flex items-center gap-2 cursor-pointer hover:bg-orange-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-orange-100 border border-orange-300 rounded" /> Anthroposphere</div>
           <div onClick={() => toggleCategory('bg-pink-100')} className="flex items-center gap-2 cursor-pointer hover:bg-pink-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-pink-100 border border-pink-300 rounded" /> Biosphere</div>
           <div onClick={() => toggleCategory('bg-green-100')} className="flex items-center gap-2 cursor-pointer hover:bg-green-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-green-100 border border-green-300 rounded" /> Atmosphere</div>
           <div onClick={() => toggleCategory('bg-purple-100')} className="flex items-center gap-2 cursor-pointer hover:bg-purple-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-purple-100 border border-purple-300 rounded" /> Lithosphere</div>
           <div onClick={() => toggleCategory('bg-yellow-100')} className="flex items-center gap-2 cursor-pointer hover:bg-yellow-100 rounded-lg px-2 py-1 transition-colors"><div className="w-6 h-3 bg-yellow-100 border border-yellow-300 rounded" /> General</div>
        </div>

        {isLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center min-h-[200px]">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-gray-500 text-sm animate-pulse font-medium">Fetching available columns...</p>
          </div>
        ) : isMerging ? (
           <div className="flex-1 flex flex-col items-center justify-center min-h-[200px]">
             <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
             <p className="text-gray-500 text-sm animate-pulse font-medium">Downloading & Merging tables in memory...</p>
           </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {validFiles.length > 1 && (
              <div className="mb-4 shrink-0">
                <h3 className="text-sm font-bold text-gray-800 mb-2">Target Tables:</h3>
                <div className="flex flex-col gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50">
                  {validFiles.map(f => (
                    <label key={f} className="flex items-center gap-3 text-sm cursor-pointer select-none">
                      <input type="checkbox" checked={selectedFiles.has(f)} onChange={() => toggleFile(f)} className="w-4 h-4 text-blue-600 rounded" />
                      <span className="font-mono text-gray-700 truncate">{f}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex-1 flex flex-col min-h-0">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Available Columns:</h3>
              <div className="flex-1 overflow-y-auto border border-gray-200 rounded-xl bg-gray-50 p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-1 gap-x-4">
                  {schema.map(col => {
                    const meta = GEOM_METADATA[col] || { color: 'bg-transparent', link: '#' };
                    return (
                      <div 
                        key={col} 
                        // Hover Event Handlers
                        onMouseEnter={() => setHoveredCol(col)}
                        onMouseLeave={() => setHoveredCol(null)}
                        className={`flex items-center gap-2 p-0.5 rounded-lg border-2 border-transparent hover:border-blue-400 transition-all cursor-default ${meta.color}`}
                      >
                        <label className="flex items-start gap-2 text-[11px] cursor-pointer select-none group flex-grow min-w-0">
                          <input 
                            type="checkbox" 
                            checked={selectedCols.has(col)} 
                            onChange={() => toggleCol(col)} 
                            className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 shrink-0"
                          />
                          <span className="break-words font-mono font-bold text-gray-700 group-hover:text-black transition-colors leading-tight truncate">
                            {col}
                          </span>
                        </label>
                        {meta.link !== '#' && (
                          <a 
                            href={meta.link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                            title="View Data Source Information"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InformationCircleIcon className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer Section with Description Box */}
            <div className="mt-6 flex items-end justify-between gap-4 shrink-0">
              {/* Description Box */}
              <div className={`flex-1 min-h-[64px] max-h-[64px] max-w-[550px] ${hoveredCol ? 'border-2 border-gray-400 rounded-2xl bg-gray-50 shadow-inner' : 'bg-white'} p-3 flex items-center overflow-hidden`}>
                <p className="font-mono text-[11px] leading-snug text-gray-700">
                  {hoveredCol ? (
                    <>
                      <span className="font-bold text-black">{hoveredCol}: </span>
                      {getDescription(hoveredCol)}
                    </>
                  ) : (
                    <span className="text-gray-400 italic"></span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pb-1">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors">
                  Cancel
                </button>
                <button 
                  onClick={handleMerge}
                  disabled={selectedCols.size === 0 || selectedFiles.size === 0}
                  className={`px-6 py-2.5 rounded-xl font-bold transition-all shadow-md ${
                    selectedCols.size > 0 && selectedFiles.size > 0
                      ? 'bg-blue-500 text-white hover:bg-blue-600 border border-blue-500'
                      : 'bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed shadow-none'
                  }`}
                >
                  Load Selected ({selectedCols.size})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  const [history, setHistory] = useState({ past: [], present: { panels: [], layout: [], panelData: {} }, future: [] });
  const { panels, layout, panelData } = history.present;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchPath, setSearchPath] = useState(shuffledFullPath);
  const [nRows, setNRows] = useState(100000);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [constraints, setConstraints] = useState([]); //useState(["coordinates_precision<=1"]);
  const [newConstraint, setNewConstraint] = useState('');
  const [isAddingConstraint, setIsAddingConstraint] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [viewMode, setViewMode] = useState('basic'); // 'expert' or 'basic'
  const [activeUserTab, setActiveUserTab] = useState('map');
  const [showInstructions, setShowInstructions] = useState(false);
  const [showGeomModal, setShowGeomModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const anyPanelHasGeomId = useMemo(() => {
    return Object.values(history.present.panelData).some(d => d?.headers?.some(h => h.toLowerCase() === 'geom_id'));
  }, [history.present.panelData]);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState(false);

  const [selectedSample, setSelectedSample] = useState(null);
  const [selectedSampleIndex, setSelectedSampleIndex] = useState(null); 

  // State and refs to track active drag/resize operations
  const isInteractingRef = useRef(false);
  const [isInteracting, setIsInteracting] = useState(false);

  const handleInteractStart = useCallback(() => {
    isInteractingRef.current = true;
    setIsInteracting(true);
  }, []);

  const handleInteractStop = useCallback((newLayout) => {
    isInteractingRef.current = false;
    setIsInteracting(false);
    setHistory(h => ({ ...h, present: { ...h.present, layout: newLayout } }));
  }, []);


  // Reset selected sample when changing datasets or view modes
  useEffect(() => {
    setSelectedSample(null);
    setSelectedSampleIndex(null);
  }, [viewMode, searchPath]);

  const [gridContainerRef, { width: gridWidth }] = useElementSize();
  

  // Check localStorage on mount
  useEffect(() => {
    const savedPass = localStorage.getItem('app_password');
    if (savedPass === SITE_PASSWORD) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === SITE_PASSWORD) {
      localStorage.setItem('app_password', SITE_PASSWORD);
      setIsAuthenticated(true);
      setError(false);
    } else {
      setError(true);
    }
  };

  // Auto-load path when entering user-friendly mode
  useEffect(() => {
    if (viewMode === 'basic' && panels.length === 0) {
      handleRemoteSearch({ key: 'Enter' }, true);
    }
  }, [viewMode]);

  const searchIndicesMap = useMemo(() => {
    const cache = {};
    Object.keys(panelData).forEach(panelId => {
      const d = panelData[panelId];
      if (!d || !d.filename || !d.table || d.isLoading) return; // skipping panels that are loading or missing table
      const includeMissing = d.includeMissingDates ?? true;
      const cacheKey = `${d.filename}|${d.searchFilter || ''}|${(d.searchColumns || []).join(',')}|${JSON.stringify(d.spatialFilter)}|${JSON.stringify(d.numericFilters || {})}|${JSON.stringify(d.dateFilter || null)}|${includeMissing}|${JSON.stringify(d.numericIncludeNulls || {})}|${JSON.stringify(d.categoricalFilters || {})}`;
      if (SearchCache.has(cacheKey)) {
        cache[d.filename] = SearchCache.get(cacheKey);
      } else {

        SearchCache.clear(); 
        
        const indices = calculateSearchIndices(
          d.table, 
          d.headers, 
          d.searchFilter, 
          d.searchColumns, 
          d.spatialFilter,
          d.numericFilters,
          d.dateFilter,
          includeMissing,
          d.numericIncludeNulls,
          d.categoricalFilters
        );
        SearchCache.set(cacheKey, indices);
        cache[d.filename] = indices;
      }
    });
    return cache;
  }, [panelData]);


  const colorConfigsMap = useMemo(() => {
    const configs = {};
    Object.keys(panelData).forEach(id => {
      const d = panelData[id];
      const col = d.colorBy || d.headers?.[0];
      if (d.table && col && !d.isLoading) {
        const key = `${d.filename}|${col}`;
        configs[key] = computeStableColorMap(d.table, d.filename, col);
      }
    });
    return configs;
  }, [panelData]);

  const activeDataId = panels[0]?.i;
  const activeData = useMemo(() => {
    if (!activeDataId || !panelData[activeDataId]) return null;
    return { 
      ...panelData[activeDataId], 
      searchIndices: searchIndicesMap[panelData[activeDataId].filename] 
    };
  }, [activeDataId, panelData, searchIndicesMap]);

  
  const updatePanelConfig = useCallback((panelId, configUpdates) => {
    setHistory(h => {
        const nextData = { ...h.present.panelData };
        // Resolve target ID if it comes from basic mode fallback ids
        let targetId = panelId;
        if (panelId === 'user-map') targetId = h.present.panels.find(p => p.type === 'map')?.i || panelId;
        else if (panelId === 'user-table') targetId = h.present.panels.find(p => p.type === 'table')?.i || panelId;
        else if (panelId === 'user-lifemap') targetId = h.present.panels.find(p => p.type === 'lifemap')?.i || panelId;

        if (nextData[targetId]) {
            nextData[targetId] = { ...nextData[targetId], ...configUpdates };
        }
        return { ...h, present: { ...h.present, panelData: nextData } };
    });
  }, []);

  const updateDatasetState = useCallback((filename, updaterOrValue) => {
  setHistory(h => {
    const nextPanelData = { ...h.present.panelData };
    
    Object.keys(nextPanelData).forEach(id => {
      if (nextPanelData[id].filename === filename) {
        const currentData = nextPanelData[id];
        
        // If it's a function, call it with current data. If not, use as is.
        const updates = typeof updaterOrValue === 'function' 
          ? updaterOrValue(currentData) 
          : updaterOrValue;

        nextPanelData[id] = { 
          ...currentData, 
          ...updates 
        };
      }
    });

    return { 
      ...h, 
      present: { ...h.present, panelData: nextPanelData } 
    };
  });
}, []);

  const updatePanelColorBy = useCallback((panelId, columnName) => {
    setHistory(h => {
      const nextPanelData = { ...h.present.panelData };
      
      // --- RESOLVE ID FOR BASIC MODE ---
      // If the ID is one of the hardcoded basic mode IDs, 
      // map it to the first panel's ID (the primary dataset).
      let targetId = panelId;
      if (panelId === 'user-map' || panelId === 'user-table' || panelId === 'user-lifemap') {
        targetId = h.present.panels[0]?.i;
      }
      // ---------------------------------

      const p = h.present.panels.find(p => p.i === targetId);
      
      if (nextPanelData[targetId]) {
        const filename = nextPanelData[targetId].filename;
        
        // Logic for Numeric Plots
        //if (p?.type === 'numericPlot') {
        //  const currentVisibleCol = nextPanelData[targetId].colorBy || nextPanelData[targetId].headers.find(h => {
        //    const col = nextPanelData[targetId].table.getChild(h);
        //    return col && typeof col.get(0) === 'number';
        //  });

        //  Object.keys(nextPanelData).forEach(id => {
        //    if (nextPanelData[id].filename === filename) {
        //       const filters = { ...(nextPanelData[id].numericFilters || {}) };
        //       delete filters[currentVisibleCol];
        //       const nulls = { ...(nextPanelData[id].numericIncludeNulls || {}) };
        //       delete nulls[currentVisibleCol]; 
        //       nextPanelData[id] = { ...nextPanelData[id], numericFilters: filters, numericIncludeNulls: nulls };
        //    }
        //  });
        //}

        // Logic for Stat Plots
        //if (p?.type === 'stats') {
        //  const currentVisibleCol = nextPanelData[targetId].colorBy || nextPanelData[targetId].headers[0];
        //  Object.keys(nextPanelData).forEach(id => {
        //    if (nextPanelData[id].filename === filename) {
        //       const catFilters = { ...(nextPanelData[id].categoricalFilters || {}) };
        //       delete catFilters[currentVisibleCol]; 
        //       const nulls = { ...(nextPanelData[id].numericIncludeNulls || {}) };
        //       delete nulls[currentVisibleCol]; 
        //       nextPanelData[id] = { ...nextPanelData[id], categoricalFilters: catFilters, numericIncludeNulls: nulls };
        //    }
        //  });
        //}

        nextPanelData[targetId] = { ...nextPanelData[targetId], colorBy: columnName, isGradient: undefined };
      }
      return { ...h, present: { ...h.present, panelData: nextPanelData } };
    });
  }, []);



  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // quick filetype detector (ext + sniff)
  function detectFileType(filename, text){
    const lower = filename.toLowerCase();

    // Include geojson and json
    if (/\.(tsv|csv|txt|geojson|json)$/.test(lower)) return 'table';
    const head = text.slice(0, 2000);

    // numeric columns -> table-ish
    if (/[\d\.\-eE]+[,\t][\d\.\-eE]/.test(head)) return 'table';
    // json structure -> table-ish
    if (/^\s*[\{\[]/.test(head)) return 'table';

    return 'unknown';
  };

  const handleFileUpload = async (e) => {
  const f = e.target.files[0];
  try {
      const built = await buildPanelPayloadFromFile(f);
      if (built.type === 'unknown') return console.warn(`Unsupported file: ${f.name}`);
      
      const nRows = built.payload.table.numRows;
      addPanel({
        type: built.type,
        data: built.payload,
        layoutHint: { w: GRID_COLS, h: Math.min(1.25 * nRows, 12) }
      });
    } catch (err) {
      console.error('Failed to open uploaded file', f?.name, err);
    }
};

const handleDrop = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  const files = Array.from(e.dataTransfer?.files || []);
  
  for (const f of files) {
    try {
      const built = await buildPanelPayloadFromFile(f);
      if (built.type === 'unknown') {
        console.warn(`Unsupported file: ${f.name}`);
        continue;
      }
      const nRows = built.payload.table.numRows;
      addPanel({
        type: built.type,
        data: built.payload,
        layoutHint: { w: GRID_COLS, h: Math.min(1.25 * nRows, 12) }
      });
    } catch (err) {
      console.error('Failed to open dropped file', f.name, err);
    }
  }
};

  const buildPanelPayloadFromFile = async (file) => {
  const filename = file.name;
  const lower = filename.toLowerCase();
  let table, headers;

  if (lower.endsWith('.parquet')) {
    // Dynamically import the package
    const parquet = await import('parquet-wasm');

    // Initialize the WebAssembly module memory
    if (typeof parquet.default === 'function') {
      await parquet.default();
    } else if (typeof parquet.initWasm === 'function') {
      await parquet.initWasm();
    }
    
    const buffer = await file.arrayBuffer();
    // Read the parquet file into a WASM table wrapper
    const wasmTable = parquet.readParquet(new Uint8Array(buffer)); 
    // Extract the IPC bytes from WASM and parse into Apache Arrow JS
    table = tableFromIPC(wasmTable.intoIPCStream());
    headers = table.schema.fields.map(f => f.name).filter(n => n !== '_geojson_str');

      // --- Decode WKB GeoParquet Geometries natively ---
      const geoColName = headers.find(h => /^(geometry|geom)$/i.test(h));
      if (geoColName && !headers.includes('_geojson_str')) {
        const geomCol = table.getChild(geoColName);
        const geojsonStrs = new Array(table.numRows);
        let hasValidGeom = false;
        let firstError = null;
        
        for (let i = 0; i < table.numRows; i++) {
          const val = geomCol.get(i); // Arrow returns a Uint8Array
          if (val && val.length > 0) {
            try {
              // Pass the raw byte array directly to our pure JS parser!
              const geojsonObj = parseWKB(val);
              geojsonStrs[i] = JSON.stringify(geojsonObj);
              hasValidGeom = true;
            } catch (e) {
              geojsonStrs[i] = null;
              if (!firstError) firstError = e;
            }
          } else {
            geojsonStrs[i] = null;
          }
        }

        if (firstError) {
           console.warn("Some WKB geometries failed to parse. First error:", firstError);
        }
        
        if (hasValidGeom) {
          const columns = {};
          
          // Keep all columns except the raw binary geometry array
          headers.forEach(h => { 
              if (h !== geoColName) columns[h] = table.getChild(h); 
          });
          
          columns['_geojson_str'] = geojsonStrs;
          
          // Update headers list to match
          headers = headers.filter(h => h !== geoColName);
          headers.push('_geojson_str');
          
          table = tableFromArrays(columns);
        }
      }
    }
    else if (lower.endsWith('.geojson') || lower.endsWith('.json')) {
      const text = await file.text();
      let geoData;
      
      try {
        geoData = JSON.parse(text);
      } catch (e) {
        throw new Error("Invalid JSON/GeoJSON file format.");
      }

      // Identify features array
      let features = [];
      if (geoData.type === 'FeatureCollection' && Array.isArray(geoData.features)) {
        features = geoData.features;
      } else if (Array.isArray(geoData)) {
        features = geoData;
      } else if (geoData.type === 'Feature') {
        features = [geoData];
      } else {
        throw new Error("Unsupported GeoJSON format. Expected a FeatureCollection.");
      }

      // Collect all unique property keys
      const propKeys = new Set();
      features.forEach(f => {
        if (f.properties) {
          Object.keys(f.properties).forEach(k => propKeys.add(k));
        }
      });

      headers = Array.from(propKeys);
      const columns = {};
      headers.forEach(h => columns[h] = []);
      const geojsonStrs = [];

      // Map properties and geometry to columns
      for (const f of features) {
        headers.forEach(h => {
          let val = f.properties && f.properties[h] !== undefined ? f.properties[h] : null;
          // Flatten nested objects/arrays to strings for the Arrow table
          if (typeof val === 'object' && val !== null) {
            val = JSON.stringify(val);
          }
          columns[h].push(val);
        });

        if (f.geometry) {
          geojsonStrs.push(JSON.stringify(f.geometry));
        } else {
          geojsonStrs.push(null);
        }
      }

      // Only add geometry column if at least one feature has geometry
      if (geojsonStrs.some(g => g !== null)) {
        columns['_geojson_str'] = geojsonStrs;
        headers.push('_geojson_str');
      }

      // Fix for Arrow Inference: Force all-null or mixed-type columns to standard Strings
      headers.forEach(h => {
        const arr = columns[h];
        let allNull = true;
        const types = new Set();
        
        for (const val of arr) {
          if (val !== null && val !== undefined && val !== "") {
            allNull = false;
            types.add(typeof val);
          }
        }
        
        if (allNull) {
          // Arrow fails on entirely null arrays. Force to empty strings.
          columns[h] = arr.map(() => ""); 
        } else if (types.size > 1) {
          // Arrow fails if numeric and string types are mixed. Force all to strings.
          columns[h] = arr.map(v => (v === null || v === undefined) ? "" : String(v));
        }
      });

      table = tableFromArrays(columns);
    }
    else if (lower.endsWith('.csv') || lower.endsWith('.tsv') || lower.endsWith('.txt')) {
      const text = await file.text();
      
      // Auto-detect delimiter by sniffing the first line
      const firstLineIdx = text.indexOf('\n');
      const firstLine = text.slice(0, firstLineIdx !== -1 ? firstLineIdx : Math.min(text.length, 1000));
      
      const commaCount = (firstLine.match(/,/g) || []).length;
      const semiCount = (firstLine.match(/;/g) || []).length;
      const tabCount = (firstLine.match(/\t/g) || []).length;
      
      let delimiter = ',';
      if (lower.endsWith('.tsv')) {
        delimiter = '\t';
      } else if (tabCount > commaCount && tabCount > semiCount) {
        delimiter = '\t';
      } else if (semiCount > commaCount && semiCount > tabCount) {
        delimiter = ';';
      }
      
      // Robust CSV Parser (handles delimiter and newlines inside quotes)
      const rows = [];
      let row = [], cur = '', inQuotes = false;
      
      for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === '"') {
          if (inQuotes && text[i+1] === '"') { cur += '"'; i++; } // Escaped quote
          else inQuotes = !inQuotes;
        } else if (c === delimiter && !inQuotes) {
          row.push(cur); cur = '';
        } else if ((c === '\n' || c === '\r') && !inQuotes) {
          if (c === '\r' && text[i+1] === '\n') i++; // Handle Windows CRLF
          row.push(cur); rows.push(row); row = []; cur = '';
        } else {
          cur += c;
        }
      }
      // Push the final row if the file didn't end with a newline
      if (cur || row.length > 0) { row.push(cur); rows.push(row); }
      // Remove trailing empty row if it exists
      if (rows.length && rows[rows.length-1].length === 1 && rows[rows.length-1][0] === '') rows.pop();

      headers = rows[0].map(h => h.trim());
      const columns = {};
      headers.forEach(h => columns[h] = []);
      
      // Map row data to columnar arrays
      for (let i = 1; i < rows.length; i++) {
        const cols = rows[i];
        headers.forEach((h, idx) => {
          const v = cols[idx]?.trim();
          // Check for numbers, but specifically handle French decimal commas if the delimiter is a semicolon
          let n;
          if (delimiter === ';' && v && v.includes(',') && !v.includes('.')) {
            // Convert French decimal comma to standard dot for parsing
            n = Number(v.replace(',', '.'));
          } else {
            n = Number(v);
          }
          
          columns[h].push((v === '' || v === undefined) ? null : (Number.isFinite(n) ? n : v));
        });
      }

       const geoColName = headers.find(h => /^(geometry|geom|geojson|wkt)$/i.test(h));
      
      if (geoColName && !headers.includes('_geojson_str')) {
        const geojsonStrs = [];
        const wktArray = columns[geoColName];
        let hasValidGeom = false;
        
        for (const val of wktArray) {
          if (!val || typeof val !== 'string') {
            geojsonStrs.push(null);
            continue;
          }
          try {
            // Clean quotes and remove extra spaces before parentheses
            let cleanWkt = val.replace(/^"|"$/g, '').trim();
            cleanWkt = cleanWkt.replace(/MULTIPOLYGON\s+\(\(\(/gi, 'MULTIPOLYGON(((');
            cleanWkt = cleanWkt.replace(/POLYGON\s+\(\(/gi, 'POLYGON((');
            
            const geojson = wellknown.parse(cleanWkt);
            if (geojson) {
              geojsonStrs.push(JSON.stringify(geojson));
              hasValidGeom = true;
            } else {
              geojsonStrs.push(null);
            }
          } catch (e) {
            geojsonStrs.push(null); // Fallback for invalid WKT
          }
        }
        
        if (hasValidGeom) {
          columns['_geojson_str'] = geojsonStrs;
          headers.push('_geojson_str'); 
        }
      }
    
      // Fix for Arrow Inference: Force all-null or mixed-type columns to standard Strings
      headers.forEach(h => {
        const arr = columns[h];
        let allNull = true, hasString = false;
        for (const val of arr) {
          if (val !== null) {
            allNull = false;
            if (typeof val === 'string') hasString = true;
          }
        }
        if (allNull) {
          // Arrow fails on entirely null arrays. Force to empty strings.
          columns[h] = arr.map(() => ""); 
        } else if (hasString) {
          // Arrow fails if numbers and strings are mixed in the same array. Force all to strings.
          columns[h] = arr.map(v => v === null ? "" : String(v));
        }
      });
      
      table = tableFromArrays(columns);
    } else {
      return { type: 'unknown', payload: { filename } };
    }

  // Return standard payload structure
  return { 
    type: 'table', 
    payload: { 
      table, 
      headers, 
      filename,
      searchColumns: [],
      colorBy: headers[0],
      searchFilter: '',
      isLoading: false
    } 
  };
};

  const setPanelData = useCallback((updater) => {
    setHistory(currentHistory => {
      const oldPresent = currentHistory.present;
      const newPanelData = typeof updater === 'function' 
        ? updater(oldPresent.panelData) 
        : updater;
      
      return {
        ...currentHistory,
        present: { ...oldPresent, panelData: newPanelData }
      };
    });
  }, []);

  const addPanel = useCallback((config = {}) => {
      const { type, data, layoutHint, id } = config;
      const newId = id || `${type}-${Date.now()}`;
      
      const defaultH = layoutHint?.h || (type === 'table' ? 12 : 10);
      const defaultW = layoutHint?.w || GRID_COLS;
      let present = { ...history.present };
      const presentLayout = present.layout || [];
      
      // Create occupancy map
      const occupancy = {};
      presentLayout.forEach(l => {
        for (let x = l.x; x < l.x + l.w; x++) {
          for (let y = l.y; y < l.y + l.h; y++) {
            occupancy[`${x},${y}`] = true;
          }
        }
      });

      // Find first fit
      let found = false, newX = 0, newY = 0;
      outer: for (let y = 0; y < 200; y++) {
        for (let x = 0; x <= GRID_COLS - defaultW; x++) {
          let fits = true;
          for (let dx = 0; dx < defaultW; dx++) {
            for (let dy = 0; dy < defaultH; dy++) {
              if (occupancy[`${x+dx},${y+dy}`]) {
                fits = false;
                break;
              }
            }
            if (!fits) break;
          }
          if (fits) {
            newX = x;
            newY = y;
            found = true;
            break outer;
          }
        }
      }
      
      // Fallback to bottom if no hole is found
      if (!found) {
        newY = presentLayout.reduce((max, l) => Math.max(max, l.y + l.h), 0);
        newX = 0;
      }

      // If already explicitly marked as loading, no need to setTimeout trick.
      if (data.isLoading) {
          setHistory(h => {
            const nextPresent = {
              panels: [...h.present.panels, { i: newId, type }],
              layout: [...h.present.layout, { i: newId, x: newX, y: newY, w: defaultW, h: defaultH, minW: 2, minH: 2 }],
              panelData: { ...h.present.panelData, [newId]: data }
            };
            return { past: [...h.past, h.present].slice(-MAX_HISTORY), present: nextPresent, future: [] };
          });
          return newId;
      }

      // First tick: add loading skeleton layout to yield control to browser layout/paint
      setHistory(h => {
        const nextPresent = {
          panels: [...h.present.panels, { i: newId, type }],
          layout: [...h.present.layout, { 
            i: newId, x: newX, y: newY, w: defaultW, h: defaultH, minW: 2, minH: 2 
          }],
          panelData: { ...h.present.panelData, [newId]: { ...data, isLoading: true } }
        };
        return { past: [...h.past, h.present].slice(-MAX_HISTORY), present: nextPresent, future: [] };
      });

      // Second tick: inject real data to unblock heavy render calculations
      requestAnimationFrame(() => {
        setTimeout(() => {
          setHistory(h => {
            if (!h.present.panels.find(p => p.i === newId)) return h; // panel closed manually
            const nextPanelData = { ...h.present.panelData };
            nextPanelData[newId] = { ...data, isLoading: false };
            return { ...h, present: { ...h.present, panelData: nextPanelData } };
          });
        }, 150);
      });

      return newId;
  }, [history]);

const duplicatePanel = useCallback((id) => {
    setHistory(h => {
      const sourceIndex = h.present.panels.findIndex(p => p.i === id);
      if (sourceIndex === -1) return h;

      const sourcePanel = h.present.panels[sourceIndex];
      const sourceData = h.present.panelData[id];
      
      const newId = `${sourcePanel.type}-${Date.now()}`;
      
      // Splice allows inserting the new panel *immediately* after the source panel 
      // This guarantees it shows up directly underneath it in Basic mode
      const newPanels = [...h.present.panels];
      newPanels.splice(sourceIndex + 1, 0, { i: newId, type: sourcePanel.type });
      
      const sourceLayout = h.present.layout.find(l => l.i === id);
      const newLayoutEntry = sourceLayout 
        ? { ...sourceLayout, i: newId, y: sourceLayout.y + sourceLayout.h }
        : { i: newId, x: 0, y: 0, w: 4, h: 10, minW: 2, minH: 2 };
      
      // Shallow copy panelData (this lets filters sync, but isolated columns can be set per-panel)
      const newPanelData = { ...h.present.panelData, [newId]: { ...sourceData } };
      
      return {
        past: [...h.past, h.present],
        present: { 
          panels: newPanels, 
          layout: [...h.present.layout, newLayoutEntry], 
          panelData: newPanelData 
        },
        future: []
      };
    });
  }, []);

  const removePanel = useCallback((id) => {
  setHistory(h => {
    const panelToRemove = h.present.panels.find(p => p.i === id);
    if (panelToRemove?.type === 'sample') {
      setSelectedSampleIndex(null);
    }
    const dataOfRemovedPanel = h.present.panelData[id];
    
    const targetFilename = dataOfRemovedPanel?.filename;
    const targetHeaders = dataOfRemovedPanel?.headers;
    let targetColorBy = dataOfRemovedPanel?.colorBy;
    if (!targetColorBy && targetHeaders) {
      if (panelToRemove?.type === 'numericPlot' || panelToRemove?.type === 'scatterplot') {
        targetColorBy = targetHeaders.filter(h => {
          const col = dataOfRemovedPanel.table?.getChild(h);
          return col && typeof col.get(0) === 'number';
        })[0];
      } else {
        targetColorBy = targetHeaders[0];
      }
    }
    const nextPanelData = { ...h.present.panelData };
    delete nextPanelData[id];

    if (panelToRemove?.type === 'numericPlot' && targetColorBy) {
      Object.keys(nextPanelData).forEach(pId => {
        if (nextPanelData[pId].filename === targetFilename) {
          const newFilters = { ...(nextPanelData[pId].numericFilters || {}) };
          delete newFilters[targetColorBy]; 
                    
          const newNulls = { ...(nextPanelData[pId].numericIncludeNulls || {}) };
          delete newNulls[targetColorBy];
          nextPanelData[pId] = { ...nextPanelData[pId], numericFilters: newFilters, numericIncludeNulls: newNulls };
        }
      });
    }

    if (panelToRemove?.type === 'stats' && targetColorBy) {
      Object.keys(nextPanelData).forEach(pId => {
        if (nextPanelData[pId].filename === targetFilename) {
          const newCatFilters = { ...(nextPanelData[pId].categoricalFilters || {}) };
          delete newCatFilters[targetColorBy]; 
          const newNulls = { ...(nextPanelData[pId].numericIncludeNulls || {}) };
          delete newNulls[targetColorBy]; 
          nextPanelData[pId] = { ...nextPanelData[pId], categoricalFilters: newCatFilters, numericIncludeNulls: newNulls };
        }
      });
    }

    const remainingPanelsWithFile = Object.values(nextPanelData).filter(
      d => d.filename === targetFilename
    );

    if (remainingPanelsWithFile.length === 0 && targetFilename) {
      clearFileFromCaches(targetFilename, targetHeaders);
    }

    if (targetFilename && remainingPanelsWithFile.length > 0) {
      Object.keys(nextPanelData).forEach(panelId => {
        if (nextPanelData[panelId].filename === targetFilename) {
          const updates = {};
          if (panelToRemove?.type === 'plot') updates.dateFilter = undefined;
          if (panelToRemove?.type === 'map') updates.spatialFilter = undefined;
          if (Object.keys(updates).length > 0) {
            nextPanelData[panelId] = { ...nextPanelData[panelId], ...updates };
          }
        }
      });
    }

    return { 
      past: [...h.past, h.present], 
      present: { ...h.present, panels: h.present.panels.filter(p => p.i !== id), layout: h.present.layout.filter(l => l.i !== id), panelData: nextPanelData }, 
      future: [] 
    };
  });
  }, []);

const [mergeSourceId, setMergeSourceId] = useState(null);

const handleTableMerge = useCallback((targetId) => {
  if (!mergeSourceId) {
    setMergeSourceId(targetId);
    return;
  }
  if (mergeSourceId === targetId) {
    setMergeSourceId(null);
    return;
  }

  const sourceData = panelData[mergeSourceId];
  const targetData = panelData[targetId];

  if (!sourceData?.table || !targetData?.table) {
    setMergeSourceId(null);
    return;
  }

  // Identify the Join Key
  const commonHeaders = sourceData.headers.filter(h => targetData.headers.includes(h));
  const joinKey = commonHeaders.find(h => h.toLowerCase() === 'geom_id') || 
                  commonHeaders.find(h => h.toLowerCase().includes('id')) || 
                  commonHeaders[0];
  
  if (!joinKey) {
    alert("No common columns found.");
    setMergeSourceId(null);
    return;
  }

  // Smart Layout Calculation (Find hole or append to bottom)
  const newId = `merged-${Date.now()}`;
  const defaultW = GRID_COLS;
  const defaultH = 12;
  const presentLayout = history.present.layout || [];
  
  const occupancy = {};
  presentLayout.forEach(l => {
    for (let x = l.x; x < l.x + l.w; x++) {
      for (let y = l.y; y < l.y + l.h; y++) {
        occupancy[`${x},${y}`] = true;
      }
    }
  });

  let foundSpace = false, newX = 0, newY = 0;
  outer: for (let y = 0; y < 1000; y++) {
    for (let x = 0; x <= GRID_COLS - defaultW; x++) {
      let fits = true;
      for (let dx = 0; dx < defaultW; dx++) {
        for (let dy = 0; dy < defaultH; dy++) {
          if (occupancy[`${x+dx},${y+dy}`]) { fits = false; break; }
        }
        if (!fits) break;
      }
      if (fits) { newX = x; newY = y; foundSpace = true; break outer; }
    }
  }

  if (!foundSpace) {
    newY = presentLayout.reduce((max, l) => Math.max(max, l.y + l.h), 0);
    newX = 0;
  }

  // Inject a loading skeleton so the UI yields instantly
  setHistory(h => ({
    ...h,
    present: {
      ...h.present,
      panels: [...h.present.panels, { i: newId, type: 'table' }],
      layout: [...h.present.layout, { 
        i: newId, x: newX, y: newY, w: defaultW, h: defaultH, 
        minW: 2, minH: 2 
      }],
      panelData: {
        ...h.present.panelData,
        [newId]: {
          filename: `Merging: ${sourceData.filename.split(':')[0]} + ${targetData.filename.split(':')[0]}`,
          isLoading: true
        }
      }
    }
  }));

  setMergeSourceId(null);

  // Compute heavy table join on the next tick
  requestAnimationFrame(() => {
  setTimeout(() => {
    try {
      const tableA = sourceData.table;
      const tableB = targetData.table;

      // Build Index Map for Table B
      const bKeyCol = tableB.getChild(joinKey);
      const bLookup = new Map();
      for (let i = 0; i < tableB.numRows; i++) {
        const val = bKeyCol.get(i);
        if (val !== null && val !== undefined) bLookup.set(String(val), i);
      }

      // Create Row Match Map
      const aRows = tableA.numRows;
      const bMatchIndices = new Int32Array(aRows).fill(-1);
      for (let i = 0; i < aRows; i++) {
        const val = tableA.getChild(joinKey).get(i);
        if (val !== null && val !== undefined) {
          const matchIdx = bLookup.get(String(val));
          if (matchIdx !== undefined) bMatchIndices[i] = matchIdx;
        }
      }

      // Construct Columns (Arrow Logic)
      const mergedColumns = {};
      
      // Preserve original columns from Table A as native Arrow Vectors
      sourceData.headers.forEach(h => { mergedColumns[h] = tableA.getChild(h); });

      targetData.headers.forEach(h => {
        // Skip if column already exists in sourceData (includes the joinKey)
        if (sourceData.headers.includes(h)) return;

        const originalCol = tableB.getChild(h);
        const colName = h; // Keep original name
        
        // Use a standard JS Array to bypass Arrow's Builder coercions
        const newArray = new Array(aRows);

        for (let i = 0; i < aRows; i++) {
          const bIdx = bMatchIndices[i];
          
          // Check if a match was found AND if the value in Table B is truly valid
          if (bIdx !== -1 && originalCol.isValid(bIdx)) {
            newArray[i] = originalCol.get(bIdx);
          } else {
            // Strict primitive null for missing matches or empty target cells
            newArray[i] = null;
          }
        }
        
        mergedColumns[colName] = newArray;
      });

      const newArrowTable = tableFromArrays(mergedColumns);
      const filename = `Merged: ${sourceData.filename.split(':')[0]} + ${targetData.filename.split(':')[0]}`;

      setHistory(h => {
        const nextPanelData = { ...h.present.panelData };
        if (nextPanelData[newId]) {
            nextPanelData[newId] = {
              table: newArrowTable,
              headers: Object.keys(mergedColumns),
              filename,
              searchFilter: '',
              searchColumns: [],
              isLoading: false
            };
        }
        return { ...h, present: { ...h.present, panelData: nextPanelData } };
      });
    } catch (err) {
      console.error("Join Error:", err);
      alert("Merge Failed: " + err.message);
      // Clean up the skeleton panel if merge fails
        setHistory(h => ({
           ...h,
           present: {
              panels: h.present.panels.filter(p => p.i !== newId),
              layout: h.present.layout.filter(l => l.i !== newId),
              panelData: { ...h.present.panelData, [newId]: undefined }
           }
        }));
      }
    }, 150);
  });

}, [mergeSourceId, panelData, setHistory, history.present.layout]);

const updateNumericFilter = useCallback((filename, columnName, range) => {
  updateDatasetState(filename, (prevData) => {
    const nextFilters = { ...(prevData.numericFilters || {}) };
    
    if (range === null) {
      delete nextFilters[columnName];
    } else {
      nextFilters[columnName] = range;
    }  
    return { numericFilters: nextFilters };
  });
}, [updateDatasetState]);

  const updateNumericNulls = useCallback((filename, columnName, includeNulls) => {
    updateDatasetState(filename, (prevData) => {
      const nextNulls = { ...(prevData.numericIncludeNulls || {}) };
      nextNulls[columnName] = includeNulls;
      return { numericIncludeNulls: nextNulls };
    });
  }, [updateDatasetState]);

  const updateCategoricalFilter = useCallback((filename, columnName, category) => {
    updateDatasetState(filename, (prevData) => {
      const nextFilters = { ...(prevData.categoricalFilters || {}) };
      
      if (category === null) {
        delete nextFilters[columnName];
      } else {
        nextFilters[columnName] = category;
      }  
      return { categoricalFilters: nextFilters };
    });
  }, [updateDatasetState]);

  const parseAndExtractFilters = (constraintsArray) => {
    let parsedCategorical = {};
    let parsedNumeric = {};
    let parsedNumericNulls = {};
    let parsedDateFilter = null;
    let parsedIncludeMissingDates = true;
    let unparsedConstraints = [];

    constraintsArray.forEach(constraintString => {
      let str = constraintString.trim();
      
      // Safely split wrapped clauses like (A) AND (B)
      const regex = /\)\s+AND\s+\(/gi;
      let match;
      let lastIdx = 0;
      let parts = [];
      
      while ((match = regex.exec(str)) !== null) {
          parts.push(str.substring(lastIdx, match.index + 1));
          lastIdx = match.index + match[0].length - 1; 
      }
      parts.push(str.substring(lastIdx));

      // Further split by plain newlines if any are preserved
      let finalParts = [];
      parts.forEach(p => {
          finalParts.push(...p.split(/\n\s*AND\s+/i));
      });

      finalParts.forEach(part => {
        let cl = part.trim();
        let originalCl = cl;
        
        // Greedily strip unbalanced or balanced parentheses resulting from naive AND splitting
        while (cl.startsWith('(') || cl.endsWith(')')) {
            if (cl.startsWith('(')) cl = cl.substring(1).trim();
            if (cl.endsWith(')')) cl = cl.substring(0, cl.length - 1).trim();
        }

        // Match categorical 
        const catMatch = cl.match(/^(?:main\.)?"?([a-zA-Z0-9_]+)"?\s*=\s*'([^']+)'$/);
        if (catMatch) {
          parsedCategorical[catMatch[1]] = catMatch[2].replace(/''/g, "'");
          return;
        }

        // Match categorical nulls
        const catNullMatch = cl.match(/^(?:main\.)?"?([a-zA-Z0-9_]+)"?\s+IS\s+NULL\s+OR\s+CAST/i);
        if (catNullMatch) {
          parsedCategorical[catNullMatch[1]] = 'null';
          return;
        }

        // Match standalone Numeric NOT NULL
        const numStandaloneNotNull = cl.match(/^(?:main\.)?"?([a-zA-Z0-9_]+)"?\s+IS\s+NOT\s+NULL\s+AND\s+CAST/i);
        if (numStandaloneNotNull) {
            parsedNumericNulls[numStandaloneNotNull[1]] = false;
            return;
        }

        // Match standalone Date NOT NULL
        const dateStandaloneNotNull = cl.match(/^(?:main\.)?"?([a-zA-Z0-9_]+)"?\s+IS\s+NOT\s+NULL$/i);
        if (dateStandaloneNotNull) {
            parsedIncludeMissingDates = false;
            return;
        }

        // Match Range (Numeric or Date) with optional trailing null logic
        const rangeMatch = cl.match(/(?:main\.)?"?([a-zA-Z0-9_]+)"?\s*>=?\s*'?([\d.-]+)'?\s+AND\s+(?:main\.)?"?\1"?\s*<=?\s*'?([\d.-]+)'?/);
        if (rangeMatch) {
            const col = rangeMatch[1];
            const isDate = /^\d{4}-\d{2}-\d{2}$/.test(rangeMatch[2]);
            const val1 = isDate ? new Date(rangeMatch[2]).getTime() : parseFloat(rangeMatch[2]);
            const val2 = isDate ? new Date(rangeMatch[3]).getTime() : parseFloat(rangeMatch[3]);
            
            if (/parsed_collection_date_start/i.test(col)) {
                parsedDateFilter = [val1, val2];
                // Check if SQL query was excluding or including null dates
                if (cl.match(/AND\s+(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NOT\s+NULL/i)) {
                    parsedIncludeMissingDates = false;
                } else if (cl.match(/OR\s+(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NULL/i)) {
                    parsedIncludeMissingDates = true;
                } else {
                    parsedIncludeMissingDates = false;
                }
            } else {
                parsedNumeric[col] = [val1, val2];
                // Check if SQL query was excluding or including null numbers
                if (cl.match(/OR\s+\(\s*(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NULL/i)) {
                    parsedNumericNulls[col] = true;
                } else {
                    parsedNumericNulls[col] = false;
                }
            }
            return;
        }

        // Match Single-Sided Inequality (new block)
        const singleMatch = cl.match(/(?:main\.)?"?([a-zA-Z0-9_]+)"?\s*(>=?|<=?|<|>)\s*'?([\d.-]+)'?/);
        if (singleMatch) {
            const col = singleMatch[1];
            const op = singleMatch[2];
            const isDate = /^\d{4}-\d{2}-\d{2}$/.test(singleMatch[3]);
            const val = isDate ? new Date(singleMatch[3]).getTime() : parseFloat(singleMatch[3]);

            if (/parsed_collection_date_start/i.test(col)) {
                // Use massive safe timestamps for the missing side
                parsedDateFilter = parsedDateFilter || [-8640000000000000, 8640000000000000];
                if (op.includes('<')) parsedDateFilter[1] = val;
                if (op.includes('>')) parsedDateFilter[0] = val;

                if (cl.match(/AND\s+(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NOT\s+NULL/i)) parsedIncludeMissingDates = false;
                else if (cl.match(/OR\s+(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NULL/i)) parsedIncludeMissingDates = true;
                else parsedIncludeMissingDates = false;
            } else {
                // Use massive safe numbers for the missing side
                parsedNumeric[col] = parsedNumeric[col] || [-999999999, 999999999];
                if (op.includes('<')) parsedNumeric[col][1] = val;
                if (op.includes('>')) parsedNumeric[col][0] = val;

                if (cl.match(/OR\s+\(\s*(?:main\.)?"?[a-zA-Z0-9_]+"?\s+IS\s+NULL/i)) parsedNumericNulls[col] = true;
                else parsedNumericNulls[col] = false;
            }
            return;
        }

        // If it doesn't match standard UI filters, keep it as raw SQL
        if (originalCl) unparsedConstraints.push(originalCl);
      });
    });

    return { parsedCategorical, parsedNumeric, parsedNumericNulls, parsedDateFilter, parsedIncludeMissingDates, unparsedConstraints };
  };


  const handleRemoteSearch = async (e, isInitial = false) => {
    if (e.key === 'Enter' && searchPath.trim() !== '' && !isLoading) {
      const path = searchPath.trim();
      setIsLoading(true);
      try {
        // Parse SQL constraints
        const { parsedCategorical, parsedNumeric, parsedNumericNulls, parsedDateFilter, parsedIncludeMissingDates, unparsedConstraints } = parseAndExtractFilters(constraints);

        const url = new URL('/query', window.location.origin);
        url.searchParams.append("path", path);
        url.searchParams.append("limit", nRows);
        
        // We still send the full original constraints to the backend to get the correctly filtered initial data
        if (constraints.length > 0) url.searchParams.append("constraints", constraints.join('|'));

        const response = await fetch(url);
        if (!response.ok) throw new Error(await response.text());
        const table = await tableFromIPC(response.body);
        
        const headers = table.schema.fields.map(f => f.name).filter(n => n !== '_geojson_str');
        const filename = path.split('/').pop().split('.')[0] + (constraints.length > 0 ? ':' + constraints.join('|') : '');
        const sourceHeader = headers.find(h => h.toLowerCase() === 'source') || headers[0];

        const defaultBasicCols = TAB_CONFIG.host.filter(col => headers.includes(col));

       // Apply parsed filters to the base data
        const baseData = { 
          table, headers, filename,
          searchColumns: viewMode === 'basic' ? defaultBasicCols : EMPTY_ARRAY,
          colorBy: viewMode === 'basic' ? sourceHeader : headers[0],
          categoricalFilters: Object.keys(parsedCategorical).length > 0 ? parsedCategorical : undefined,
          numericFilters: Object.keys(parsedNumeric).length > 0 ? parsedNumeric : undefined,
          numericIncludeNulls: Object.keys(parsedNumericNulls).length > 0 ? parsedNumericNulls : undefined,
          dateFilter: parsedDateFilter,
          includeMissingDates: parsedIncludeMissingDates,
        };
        
        // Update global constraints to only keep the ones we couldn't parse into UI buttons
        setConstraints(unparsedConstraints);

        if (isInitial) {
          const now = Date.now();
          const newPanels = [];
          const newLayout = [];
          const newPanelData = {};

           // Helper to detect specific data features
          const dateCol = headers.find(h => /parsed_collection_date_start/i.test(h));
          const { latCol, lonCol, geoCol } = getGeoColumns(headers, table);
          const hasGeo = (latCol && lonCol) || geoCol;
          
          // Table
          const tableId = `table-${now}`;
          newPanels.push({ i: tableId, type: 'table' });
          newLayout.push({ i: tableId, x: 0, y: 0, w: 4, h: 12 });
          newPanelData[tableId] = baseData;

          // Timeline
          if (dateCol) {
            const plotId = `plot-${now}-init`;
            newPanels.push({ i: plotId, type: 'plot' });
            newLayout.push({ i: plotId, x: 0, y: 12, w: 4, h: 8 });
            newPanelData[plotId] = { ...baseData };
          }

          // Map
          if (hasGeo) {
            const mapId = `map-${now}-init`;
            newPanels.push({ i: mapId, type: 'map' });
            newLayout.push({ i: mapId, x: 4, y: 0, w: 5, h: 20 });
            newPanelData[mapId] = { ...baseData };
          }
          
          // Stats
          const statsId = `stats-${now}-init`;
          newPanels.push({ i: statsId, type: 'stats' });
          newLayout.push({ i: statsId, x: 9, y: 0, w: 3, h: 8 });
          newPanelData[statsId] = { ...baseData };

          // Lat/Lon Distributions
          if (latCol) {
            const latDistId = `latdist-${now}-init`;
            newPanels.push({ i: latDistId, type: 'numericPlot' });
            newLayout.push({ i: latDistId, x: 9, y: 8, w: 3, h: 6 });
            newPanelData[latDistId] = { ...baseData, colorBy: latCol };
          }


          // Update State with skeletons immediately
          const skeletonPanelData = {};
          Object.keys(newPanelData).forEach(k => {
             skeletonPanelData[k] = { filename: newPanelData[k].filename, isLoading: true };
          });

          setHistory(h => ({
            past: [...h.past, h.present],
            present: {
              panels: [...h.present.panels, ...newPanels],
              layout: [...h.present.layout, ...newLayout],
              panelData: { ...h.present.panelData, ...skeletonPanelData }
            },
            future: []
          }));

          // Replace skeleton with real data on next tick to avoid frozen UI
          requestAnimationFrame(() => {
            setTimeout(() => {
               setHistory(h => {
                   const actualData = { ...h.present.panelData };
                   Object.keys(newPanelData).forEach(k => {
                       // Check if it wasn't removed while loading
                       if (actualData[k]) {
                           actualData[k] = { ...newPanelData[k], isLoading: false };
                       }
                   });
                   return { ...h, present: { ...h.present, panelData: actualData } };
               });
            }, 150);
          });

        } else {
            // Standard behavior for user-triggered searches (just adds one table)
            addPanel({
              type: 'table',
              data: baseData,
              layoutHint: { w: GRID_COLS, h: 12 }
            });
        }

      } catch (err) {
        console.error("Backend Error:", err);
        alert("Error: " + err.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

    const handleRemoteSearchRefetch = async (searchTerm, searchColumns, limitOverride, extraConstraint = null, countMap = null, isLoadAll = false, overrideConstraints = null) => {
    if (!searchPath) return;
    setIsLoading(true);

    // If we have a suggestion count, we use it as the limit (capped by our max nRows)
    // This triggers the "Early Out" optimization in DuckDB for rare terms.
    // If isLoadAll is true, we bypass the max nRows safety cap and use the exact count.
    const finalLimit = isLoadAll ? limitOverride : (limitOverride ? Math.min(limitOverride, nRows) : nRows);

    setHistory(h => {
        const nextData = { ...h.present.panelData };
        Object.keys(nextData).forEach(k => {
               nextData[k] = { 
              ...nextData[k], 
              searchFilter: searchTerm?.toLowerCase() || '',
              //searchColumns: searchColumns || nextData[k].searchColumns
            };
        });
        return { ...h, present: { ...h.present, panelData: nextData } };
    });

    try {
        const url = new URL('/query', window.location.origin);
        url.searchParams.append("path", searchPath);
        url.searchParams.append("limit", finalLimit);
        
        let combinedConstraints = overrideConstraints ? [...overrideConstraints] : [...constraints];
        if (extraConstraint) combinedConstraints.push(extraConstraint);

        if (combinedConstraints.length > 0) {
            url.searchParams.append("constraints", combinedConstraints.join('|'));
        }
        
        if (searchTerm && !extraConstraint) {
            url.searchParams.append("search", searchTerm);
            if (searchColumns && searchColumns.length > 0) {
                url.searchParams.append("search_cols", searchColumns.join(','));
            }
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error(await response.text());
        let table = await tableFromIPC(response.body);

        let headers = table.schema.fields.map(f => f.name).filter(n => n !== '_geojson_str');
        const filename = searchPath.split('/').pop().split('.')[0] + (constraints.length > 0 ? ':' + constraints.join('|') : '');

// Explode rows with multiple runs, sync corresponding columns, and inject total_count safely
        if (countMap && countMap.size > 0) {
          console.log(`[STAT Debug] Exploding rows and injecting counts for ${countMap.size} runs.`);
          const runCol = table.getChild('run');
          if (runCol) {
            // keptData will now store the exact split index so we can sync other columns
            let keptData =[]; 
            
            // Identify indices to keep and map out the exploded runs/counts/split indices
            for (let i = 0; i < table.numRows; i++) {
              if (runCol.isValid(i)) {
                const runVal = runCol.get(i);
                
                // Split strictly by pipe to ensure perfect index alignment with other columns.
                const runs = String(runVal).split('|'); 
                
                for (let k = 0; k < runs.length; k++) {
                  const cleanRun = runs[k].trim();
                  if (countMap.has(cleanRun)) {
                    keptData.push({
                      origIdx: i,
                      run: cleanRun,
                      counts: countMap.get(cleanRun), // Object with counts for each virus
                      splitIdx: k // Save the position of this run
                    });
                  }
                }
              }
            }

            // Enforce the user's row limit on the exploded result
            if (keptData.length > nRows) {
              keptData = keptData.slice(0, nRows);
            }

            // Rebuild columns using standard JS Arrays
            const colData = {};
            const numExplodedRows = keptData.length;
            
            // Columns that need to be split and positionally matched alongside 'run'
            const syncedCols = new Set(['librarysource', 'library_source', 'assay_type', 'platform', 'instrument']);

            headers.forEach(h => {
              if (h === 'run') {
                colData['run'] = keptData.map(d => d.run);
                return;
              }

              const originalCol = table.getChild(h);
              const newArray = new Array(numExplodedRows);
              const isSyncedCol = syncedCols.has(h.toLowerCase());
              
              for (let j = 0; j < numExplodedRows; j++) {
                const { origIdx, splitIdx } = keptData[j];
                
                if (originalCol.isValid(origIdx)) {
                  const rawVal = originalCol.get(origIdx);
                  
                  // If this is a synchronized column, split it and grab the matching index
                  if (isSyncedCol && typeof rawVal === 'string') {
                    const parts = rawVal.split('|');
                    
                    if (parts.length > splitIdx) {
                      // Grab the exact corresponding value
                      newArray[j] = parts[splitIdx].trim();
                    } else if (parts.length === 1) {
                      // Fallback: If it's a single value (no pipes), assume it applies to all runs
                      newArray[j] = parts[0].trim();
                    } else {
                      // Fallback: Missing or malformed data at this index
                      newArray[j] = null;
                    }
                  } else {
                    // Normal column (like location, coordinates, etc) - just duplicate the value
                    newArray[j] = rawVal;
                  }
                } else {
                  newArray[j] = null; 
                }
              }
              
              colData[h] = newArray;
            });

            // Append the dynamic count columns to the end
            const sampleCounts = countMap.values().next().value;
            if (sampleCounts) {
              Object.keys(sampleCounts).forEach(virusName => {
                const colName = `${virusName}_count`.replace(/\s+/g, '_');
                colData[colName] = keptData.map(d => d.counts[virusName]);
                if (!headers.includes(colName)) {
                  headers.push(colName);
                }
              });
            }
            
           table = tableFromArrays(colData);
          }
        } else if (countMap) {
          console.warn("[STAT Debug] countMap provided but empty. No counts to inject.");
        }

        // ---- AUTO ENRICHMENT FOR +G VARIABLES ----
        const existingEnrichedCols = new Set();
        Object.values(history.present.panelData).forEach(d => {
            if (d.enrichedColumns) d.enrichedColumns.forEach(c => existingEnrichedCols.add(c));
        });

        if (existingEnrichedCols.size > 0 && headers.some(h => h.toLowerCase() === 'geom_id')) {
            const joinKeyName = headers.find(h => h.toLowerCase() === 'geom_id');
            try {
                const mergeUrl = new URL('/query', window.location.origin);
                mergeUrl.searchParams.append('path', GEOM_TABLE_PATH);
                mergeUrl.searchParams.append('limit', '800000');
                const res = await fetch(mergeUrl);
                if (res.ok) {
                    const tableB = await tableFromIPC(res.body);
                    const bJoinKey = tableB.schema.fields.map(f => f.name).find(n => n.toLowerCase() === 'geom_id');
                    const bKeyCol = bJoinKey ? tableB.getChild(bJoinKey) : null;
                    
                    if (bKeyCol) {
                        const bLookup = new Map();
                        for (let i = 0; i < tableB.numRows; i++) {
                            const val = bKeyCol.get(i);
                            if (val !== null && val !== undefined) bLookup.set(String(val), i);
                        }

                        const aRows = table.numRows;
                        const aKeyCol = table.getChild(joinKeyName);
                        const bMatchIndices = new Int32Array(aRows).fill(-1);
                        for (let i = 0; i < aRows; i++) {
                            const val = aKeyCol.get(i);
                            if (val !== null && val !== undefined) {
                                const matchIdx = bLookup.get(String(val));
                                if (matchIdx !== undefined) bMatchIndices[i] = matchIdx;
                            }
                        }

                        const mergedColumns = {};
                        headers.forEach(header => { mergedColumns[header] = table.getChild(header); });
                        
                        existingEnrichedCols.forEach(colName => {
                            if (headers.includes(colName)) return; // Already exists
                            const originalCol = tableB.getChild(colName);
                            if (!originalCol) return;
                            const newArray = new Array(aRows);
                            for (let i = 0; i < aRows; i++) {
                                const bIdx = bMatchIndices[i];
                                newArray[i] = (bIdx !== -1 && originalCol.isValid(bIdx)) ? originalCol.get(bIdx) : null;
                            }
                            mergedColumns[colName] = newArray;
                        });
                        
                        table = tableFromArrays(mergedColumns);
                        headers = Object.keys(mergedColumns);
                    }
                }
            } catch (err) {
                console.error("Auto-enrichment failed:", err);
            }
        }

        // Update panelData with newly filtered source dataset 
        setHistory(h => {
            const nextData = { ...h.present.panelData };
            Object.keys(nextData).forEach(k => {
                nextData[k] = {
                    ...nextData[k],
                    table,
                    headers,
                    filename,
                    searchFilter: '', 
                    isLoading: false
                };
            });
            return { ...h, present: { ...h.present, panelData: nextData } };
        });
        
        // Clear caches as dataset dimensions just changed
        SearchCache.clear();
        GeoCache.clear();
        ColorCache.clear();

    } catch(e) {
        console.error(e);
        alert("Search failed: " + e.message);
    } finally {
        setIsLoading(false);
    }
  };

  const updateSpatialFilter = useCallback((filename, bounds) => {
    updateDatasetState(filename, { spatialFilter: bounds });
  }, [updateDatasetState]);


  const updatePanelFilter = useCallback((filename, filterRange, includeMissing) => {
  updateDatasetState(filename, { 
    dateFilter: filterRange,
    includeMissingDates: includeMissing 
  });
}, [updateDatasetState]);

  // Update function for maps base layer
  const updatePanelBaseLayer = useCallback((panelId, baseLayer) => {
    setHistory(h => {
      const nextPanelData = { ...h.present.panelData };
      let targetId = panelId;
      if (panelId === 'user-map' || panelId === 'user-table' || panelId === 'user-lifemap') {
        targetId = h.present.panels[0]?.i;
      }
      if (nextPanelData[targetId]) {
        nextPanelData[targetId] = { ...nextPanelData[targetId], baseLayer };
      }
      return { ...h, present: { ...h.present, panelData: nextPanelData } };
    });
  }, []);

  // Memoized active attributions from all map panels
  const activeAttributions = useMemo(() => {
    const atts = new Set();

    if (viewMode === 'expert') {
      // Expert Mode: check all grid panels
      panels.forEach(p => {
        if (p.type === 'map') {
          const data = panelData[p.i];
          const styleKey = data?.baseLayer || 'satellite';
          const styleConfig = MAP_STYLES[styleKey];
          if (styleConfig?.attribution) atts.add(styleConfig.attribution);
        }
      });
    } else {
      if (activeData) {
        const styleKey = activeData.baseLayer || 'satellite';
        const styleConfig = MAP_STYLES[styleKey];
        if (styleConfig?.attribution) atts.add(styleConfig.attribution);
      }
    }

    return Array.from(atts);
  }, [panels, panelData, viewMode, activeUserTab, activeData]);

  const updateSearchFilter = useCallback((filename, searchTerm) => {
    updateDatasetState(filename, { searchFilter: searchTerm?.toLowerCase() || '' });
  }, [updateDatasetState]);

  const updateSearchColumns = useCallback((filename, columns) => {
  updateDatasetState(filename, { searchColumns: columns });
}, [updateDatasetState]);


  const openSamplePanel = useCallback((rowData, filename) => {
    addPanel({
      type: 'sample',
      layoutHint: { w: 3, h: 12 },
      data: { rowData, filename }
    });
  }, [addPanel]);

  const openNumericPlotPanel = useCallback((config) => {
      addPanel({
        type: 'numericPlot',
        layoutHint: { w: 4, h: 10 },
        data: config
      });
    }, [addPanel]);

  const openScatterPlotPanel = useCallback((config) => {
      addPanel({
        type: 'scatterplot',
        layoutHint: { w: 4, h: 12 },
        data: config
      });
    }, [addPanel]);

    const openBoxPlotPanel = useCallback((config) => {
      addPanel({
        type: 'boxplot',
        layoutHint: { w: 4, h: 12 },
        data: config
      });
    }, [addPanel]);

  const openStatsPanel = useCallback((statsConfig) => {
    addPanel({
      type: 'stats',
      layoutHint: { w: 4, h: 10 },
      data: statsConfig
    });
  }, [addPanel]);

  const openTimeStatsPanel = useCallback((statsConfig) => {
    addPanel({
      type: 'timestats',
      layoutHint: { w: 4, h: 10 },
      data: statsConfig
    });
  }, [addPanel]);

  const openTimePlotPanel = useCallback((plotConfig) => {
    addPanel({
      type: 'plot',
      layoutHint: { w: 4, h: 10 },
      data: plotConfig
    });
  }, [addPanel]);

  const openMapPanel = useCallback((mapData) => {
    addPanel({ 
      type: 'map',
      layoutHint: { w: 6, h: 15 },
      data: mapData
     });
  }, [addPanel]);

  const openLifeMapPanel = useCallback((lifeMapData) => {
  addPanel({ 
    type: 'lifemap',
    layoutHint: { w: 6, h: 15 },
    data: lifeMapData
  });
  }, [addPanel]);

  if (!isAuthenticated) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200 w-full max-w-md">
          <div className="flex flex-col items-center gap-4 mb-6">
            <Logoglyph />
            <h1 className="text-2xl font-bold text-gray-800">Restricted Access</h1>
            <p className="text-gray-500 text-sm text-center">Access is restricted while the tool is still in development.
              Please <a href="https://viromeatlas.univ-lyon1.fr/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">register your interest</a> to be notified when the final Virome Atlas Explorer tool will be released.</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              autoFocus
              type="password"
              className={`w-full p-3 border-2 rounded-xl focus:outline-none transition-all ${
                error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
            />
            {error && <p className="text-red-500 text-xs font-bold mt-1">Incorrect password. Please try again.</p>}
            
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg transition-colors"
            >
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }
  
  return (
  <>
    <div className="h-screen w-full flex flex-col overflow-hidden bg-white" onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
   
      {/* Sticky buttons */}
      <div className="fixed top-3 right-4 z-[100] justify-elements gap-1.5 flex">
        
      { viewMode === 'expert' && panels.length>0 && (
        <ClearAllButton onClick={() => {
                // Clear the React State holding the tables
                setHistory({ past: [], present: { panels: [], layout: [], panelData: {} }, future: [] });
                
                // Clear lingering local states
                setSelectedSample(null);
                setMergeSourceId(null);

                // Purge all global Maps/Caches to free memory
                SearchCache.clear();
                ColorCache.clear();
                GeoCache.clear();
              }}
              />
      )}   
        <DevelopmentButton onClick={() => window.open("https://viromeatlas.univ-lyon1.fr/", "_blank", "noopener,noreferrer")} />
        <InstructionsButton onClick={() => setShowInstructions(s => !s)} showInstructions={showInstructions} />
        <ViewModeButton onClick={() => setViewMode(v => v === 'expert' ? 'basic' : 'expert')} viewMode={viewMode} />

      </div>

      {viewMode === 'expert' ? (
        <>
          <div className="fixed top-0 left-0 w-full z-50 p-4 flex justify-between items-center pointer-events-none ">
            <div className="flex items-center gap-2 pointer-events-auto">
              <Title isLoading={isLoading} />
              {anyPanelHasGeomId && (
                <GeomEnrichButton onClick={() => setShowGeomModal(true)} />
              )}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={isLoading ? "Loading data..." : "Paste S3 path and press Enter..."}
                    className={`w-96 px-4 py-4 border rounded-xl focus:outline-none transition ${
                      isLoading ? "bg-gray-200 cursor-not-allowed" : "bg-gray-100 border-gray-300 focus:ring-2 focus:ring-gray-400 focus:bg-white"
                    }`}
                    value={searchPath}
                    onChange={(e) => setSearchPath(e.target.value)}
                    onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                         handleRemoteSearch(e, panels.length === 0);
                     }
                    }}
                    disabled={isLoading}
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={isLoading ? "Loading data..." : "N_rows=?"}
                    className={`text-center w-24 px-4 py-4 border rounded-xl focus:outline-none transition ${
                      isLoading ? "bg-gray-200 cursor-not-allowed" : "bg-gray-100 border-gray-300 focus:ring-2 focus:ring-gray-400 focus:bg-white"
                    }`}
                    value={nRows}
                    onChange={(e) => setNRows(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  />
                </div>
                <PlusButton onClick={() => setIsAddingConstraint(true)} />
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                  {constraints.map((c, idx) => (
                    <div key={idx} className="flex-shrink-0">
                      {editingIdx === idx ? (
                        <input
                          autoFocus
                          className="border-2 border-blue-400 p-4 rounded-xl outline-none shadow-sm max-w-[300px] text-sm font-mono bg-gray-100"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => {
                            if (editingValue.trim()) {
                              const next = [...constraints];
                              next[idx] = editingValue.trim();
                              setConstraints(next);
                            }
                            setEditingIdx(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editingValue.trim()) {
                                const next = [...constraints];
                                next[idx] = editingValue.trim();
                                setConstraints(next);
                              }
                              setEditingIdx(null);
                            } else if (e.key === 'Escape') {
                              setEditingIdx(null);
                            }
                          }}
                        />
                      ) : (
                        <div 
                          onDoubleClick={() => {
                            setEditingIdx(idx);
                            setEditingValue(c);
                          }}
                          className="flex items-center gap-2 bg-white border-2 border-gray-800 p-3 rounded-xl shadow-sm whitespace-nowrap cursor-pointer max-w-[300px] overflow-hidden"
                          title="Double-click to edit"
                        >
                          <span className="flex-1 min-w-0 truncate text-sm font-mono font-bold text-gray-800 select-none">{c}</span>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setConstraints(prev => prev.filter((_, i) => i !== idx));
                            }}
                            className="text-gray-400 hover:text-red-500 transition"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {isAddingConstraint && (
                    <input
                      autoFocus
                      className="p-4 items-center gap-2 bg-gray-100 border-2 border-gray-800 rounded-xl shadow-sm whitespace-nowrap "
                      placeholder="e.g. rg_country='Peru'"
                      value={newConstraint}
                      onChange={(e) => setNewConstraint(e.target.value)}
                      onBlur={() => {
                        if (!newConstraint) setIsAddingConstraint(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newConstraint.trim()) {
                          setConstraints([...constraints, newConstraint.trim()]);
                          setNewConstraint('');
                          setIsAddingConstraint(false);
                        } else if (e.key === 'Escape') {
                          setIsAddingConstraint(false);
                          setNewConstraint('');
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-grow overflow-auto min-h-0">
            <div className="flex flex-col items-center justify-center p-6 mt-2 mb-4"></div>
            <div className="justify-between min-h-[60%]">
              {showInstructions && <ProjectCard mode="wide"/>}
              <div ref={gridContainerRef} className="w-full"> 
                <GridLayout 
                  className="layout" 
                  layout={layout} 
                  cols={GRID_COLS} 
                  rowHeight={30} 
                  width={gridWidth > 0 ? gridWidth : windowWidth - 10} 
                  autoSize={true}
                  margin={[8,8]}
                  containerPadding={[8,8]}
                  isResizable
                  isDraggable
                  draggableHandle=".panel-header"
                  draggableCancel="button, select, input"
                  onDragStart={handleInteractStart}
                  onResizeStart={handleInteractStart}
                  onDragStop={handleInteractStop}
                  onResizeStop={handleInteractStop}
                  onLayoutChange={l => {
                    // Stop massive re-renders by skipping state syncs mid-drag
                    if (!isInteractingRef.current) {
                      setHistory(h => ({ ...h, present: { ...h.present, layout: l } }));
                    }
                  }}
                >
                  {panels.map(p => (
                    <div key={p.i}>
                      <InteractionWrapper isInteracting={isInteracting}>
                        <PanelLoader id={p.i} type={p.type} data={panelData[p.i]} onRemove={removePanel}>
                          {p.type === 'sample' ? (
                            <SamplePanel id={p.i} data={panelData[p.i]} onRemove={removePanel} viewMode={viewMode} />
                          ) : p.type === 'lifemap' ? (
                            <LifeMapPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} selectedSample={selectedSample} />
                          ) : p.type === 'numericPlot' ? (
                            <NumericPlotPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} onFilterChange={updateNumericFilter} onColorByChange={updatePanelColorBy} onNullToggle={updateNumericNulls} onConfigChange={updatePanelConfig} />
                          ) : p.type === 'scatterplot' ? (
                            <ScatterPlotPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} colorConfig={colorConfigsMap[`${panelData[p.i].filename}|${panelData[p.i].colorBy || panelData[p.i].headers?.[0]}`]} onRemove={removePanel} onColorByChange={updatePanelColorBy} onConfigChange={updatePanelConfig} />
                          ) : p.type === 'boxplot' ? (
                            <BoxPlotPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} colorConfig={colorConfigsMap[`${panelData[p.i].filename}|${panelData[p.i].colorBy || panelData[p.i].headers?.[0]}`]} onRemove={removePanel} onColorByChange={updatePanelColorBy} onConfigChange={updatePanelConfig} />
                          ) : p.type === 'stats' ? (
                            <StatsPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} colorConfig={colorConfigsMap[`${panelData[p.i].filename}|${panelData[p.i].colorBy || panelData[p.i].headers?.[0]}`]} onColorByChange={updatePanelColorBy}  onCategoryClick={updateCategoricalFilter} onNullToggle={updateNumericNulls} onConfigChange={updatePanelConfig} />
                          ) : p.type === 'timestats' ? (
                            <TimeStatsPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} colorConfig={colorConfigsMap[`${panelData[p.i].filename}|${panelData[p.i].colorBy || panelData[p.i].headers?.[0]}`]} onColorByChange={updatePanelColorBy} onNullToggle={updateNumericNulls} onConfigChange={updatePanelConfig} />  
                          ) : p.type === 'table' ? (
                            <TablePanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} setPanelData={setPanelData} onOpenMap={openMapPanel} onOpenPlot={openTimePlotPanel} onOpenNumericPlot={openNumericPlotPanel} onOpenScatterPlot={openScatterPlotPanel} onOpenBoxPlot={openBoxPlotPanel} onSearchChange={updateSearchFilter} onOpenStats={openStatsPanel} onOpenTimeStats={openTimeStatsPanel} onSearchColumnsChange={updateSearchColumns} onOpenLifeMap={openLifeMapPanel} onMergeClick={handleTableMerge} isMergeSource={mergeSourceId === p.i} onRowClick={(rowData, index) => {
        setSelectedSampleIndex(index);
        openSamplePanel(rowData, panelData[p.i].filename);
      }}  />) : p.type === 'map' ? (
                            <MapPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} onOpenPlot={openTimePlotPanel} onSpatialFilterChange={updateSpatialFilter} colorConfig={colorConfigsMap[`${panelData[p.i].filename}|${panelData[p.i].colorBy || panelData[p.i].headers?.[0]}`]} onColorByChange={updatePanelColorBy} onBaseLayerChange={updatePanelBaseLayer} selectedSampleIndex={selectedSampleIndex}  onRowClick={(rowData, index) => {
                              setSelectedSampleIndex(index);
                              openSamplePanel(rowData, panelData[p.i].filename);
                             }} onConfigChange={updatePanelConfig} /> 
                          ) : p.type === 'plot' ? (
                            <TimePlotPanel id={p.i} data={{ ...panelData[p.i], searchIndices: searchIndicesMap[panelData[p.i].filename] }} onRemove={removePanel} onFilterChange={updatePanelFilter} onConfigChange={updatePanelConfig} />
                          ) : null}
                        </PanelLoader>
                      </InteractionWrapper>
                    </div>
                  ))}
                </GridLayout>
              </div>
            </div>
            {/* Scrollable Footer Area */}
            <div className="mt-20"> 
              {activeAttributions.length > 0 && (
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-600 flex flex-wrap gap-x-4 justify-end">
                  Map attributions:
                  {activeAttributions.map((attr, idx) => (
                    <span key={idx} dangerouslySetInnerHTML={{ __html: attr }} />
                  ))}
                </div>
              )}
              <Footer />
            </div>
          </div>
        </>
      ) : (
        <SimplifiedView 
          data={activeData}
          onSearchColChange={updateSearchColumns}
          onColorByChange={updatePanelColorBy}
          onSpatialFilterChange={updateSpatialFilter}
          colorConfig={activeData ? colorConfigsMap[`${activeData.filename}|${activeData.colorBy || activeData.headers?.[0]}`] : null}
          showInstructions={showInstructions}
          activeAttributions={activeAttributions}
          showFilters={showFilters}          
          setShowFilters={setShowFilters}  
          onRowClick={(rowData, index) => {
            setSelectedSample(rowData);
            setSelectedSampleIndex(index);
          }}
          selectedSample={selectedSample}
          setSelectedSample={(val) => {
            setSelectedSample(val);
            if (!val) setSelectedSampleIndex(null);
          }}
          selectedSampleIndex={selectedSampleIndex}
          searchPath={searchPath}
          onRemoteSearch={handleRemoteSearchRefetch}
          isFetchingData={isLoading}
          onOpenGeomModal={() => setShowGeomModal(true)}
          onOpenTimeStats={openTimeStatsPanel}
          nRows={nRows}
          constraints={constraints}
          setConstraints={setConstraints}
          panels={panels}
          panelData={panelData}
          searchIndicesMap={searchIndicesMap}
          colorConfigsMap={colorConfigsMap}
          removePanel={removePanel}
          duplicatePanel={duplicatePanel}
          updateNumericFilter={updateNumericFilter}
          updatePanelFilter={updatePanelFilter}
          updateNumericNulls={updateNumericNulls}
          updateCategoricalFilter={updateCategoricalFilter}
          updateSearchFilter={updateSearchFilter}
          onBaseLayerChange={updatePanelBaseLayer}
          onPasteQuery={(query) => {
                if (!activeData) return;
                const { parsedCategorical, parsedNumeric, parsedNumericNulls, parsedDateFilter, parsedIncludeMissingDates, unparsedConstraints } = parseAndExtractFilters([query]);
                
                updateDatasetState(activeData.filename, {
                  categoricalFilters: Object.keys(parsedCategorical).length > 0 ? parsedCategorical : undefined,
                  numericFilters: Object.keys(parsedNumeric).length > 0 ? parsedNumeric : undefined,
                  numericIncludeNulls: Object.keys(parsedNumericNulls).length > 0 ? parsedNumericNulls : undefined,
                  dateFilter: parsedDateFilter,
                  includeMissingDates: parsedIncludeMissingDates,
                });
                
                setConstraints(unparsedConstraints);
                handleRemoteSearchRefetch(activeData.searchFilter, activeData.searchColumns, null, null, null, false, [query]);
              }}
          onOpenPlot={openTimePlotPanel}
          onOpenStats={openStatsPanel}
          onOpenNumericPlot={openNumericPlotPanel}
          onOpenScatterPlot={openScatterPlotPanel}
          onOpenBoxPlot={openBoxPlotPanel}
          onConfigChange={updatePanelConfig}
            />
          )}
      <GeomEnrichModal 
        isOpen={showGeomModal} 
        onClose={() => setShowGeomModal(false)} 
        panelData={panelData} 
        setHistory={setHistory} 
      />
    </div>
  </>
);
}

export default App;